/**
 * Mobile Apps API Contracts
 *
 * Request/response schemas for app releases and the artifacts (builds, OTA
 * updates, store submissions) each release target produces.
 */

import { z } from 'zod'
import {
  AscWebhookCannotCheckReasonSchema,
  AscWebhookReadinessSchema
} from '../../schemas/entities/mobile-app-readiness'
import {
  AppReleaseResponseSchema,
  AppReleaseStatusSchema,
  AppReleaseTypeSchema,
  AppArtifactKindSchema,
  AppReleaseEventResponseSchema,
  BuildProfilesSchema,
  GitBranchResponseSchema,
  GitReleaseRefSchema,
  MobileAppResponseSchema,
  MobileAppStoreListingSchema,
  PlatformSchema,
  RegionMatrixSchema,
  RegistryTierSchema,
  StoreListingStateSchema,
  AscAppVersionStateSchema,
  AscReleaseTypeSchema,
  StoreVersionStandingValueSchema,
  StoreVersionStateSchema,
  StoreVersionUnknownReasonSchema,
  VersionDeploymentStatusResponseSchema,
  type DeliveryBuildFact,
  type DeliveryLiveFact,
  type DeliveryOtaFact,
  type Platform
} from '../../schemas/entities/mobile-deployment'
import {
  STORE_REJECTION_CAUSE_CLASSES,
  STORE_REJECTION_OUTSTANDING_BUCKET,
  StoreRejectionCauseClassSchema,
  StoreRejectionStoreSchema
} from '../../schemas/entities/store-rejection'
import {
  ApiAccessProbeOutcomeSchema,
  PublishingAccountKindSchema,
  PublishingAccountOwnershipSchema,
  PublishingAccountProvisioningStateSchema,
  PublishingAccountReadinessSchema,
  PublishingAccountSchema,
  PublishingAccountVendorSchema
} from '../../schemas/entities/publishing-account'
import {
  AppLineDriftSchema,
  AppLineStateReasonSchema,
  AppLineStateSchema,
  AppPlatformLinesSchema,
  ComplianceFieldSchema,
  ComplianceLaneSchema,
  ComplianceSignoffSchema,
  isComplianceFieldForPlatform,
  LineComplianceFieldSchema,
  LineReleaseKindSchema,
  LineReleaseStatusSchema,
  LineReleaseStepResultSchema,
  LineReleaseStepSchema,
  ReleaseLineLifecycleSchema,
  ReleaseLineRuntimeVersionSchema,
  ReleaseLineSchema,
  compareLineRuntimes
} from '../../schemas/entities/release-line'
import { SchoolAppFieldDeliveryClassSchema } from '../../schemas/entities/school-app-field-delivery'
import { SchoolAppFieldKeySchema } from '../../schemas/entities/school-app-field-draft'
import {
  ReleaseSourceClassSchema,
  RuntimeGuardReportSchema,
  VersionIdentityFindingSchema,
  VersionIdentitySchema
} from '../../schemas/entities/release-line-runtime-guard'
import {
  StoreScreenshotDeviceClassSchema,
  StoreScreenshotListResponseSchema
} from '../../schemas/entities/mobile-app-store-screenshot'
import { SetupLastRunSchema } from './setup-last-run'
import { SetupGraphNodeIdSchema, SetupGraphNodeSchema, SetupRunAllSchema, type SetupGraphNodeId } from './setup-graph'
import { SetupPreflightStatusSchema } from './setup-preflight'

const PLACEHOLDER_PATTERN = /^(TODO|TBD|FIXME|XXX|null|undefined|\s*)$/i

export const placeholderRejected = (label: string) =>
  z
    .string()
    .min(1, { message: `${label} is required` })
    .refine(v => !PLACEHOLDER_PATTERN.test(v), {
      message: `${label} cannot be a placeholder (TODO/TBD/FIXME/XXX/empty)`
    })

// `maxLength`/`maxLengthMessage` are optional so existing callers (Bundle ID,
// Expo project ID, etc.) are unaffected — only fields with a real external
// cap (e.g. `displayName`'s 30-char App Store / Play listing-title limit)
// pass them.
const optionalPlaceholderRejected = (label: string, maxLength?: number, maxLengthMessage?: string) => {
  const base = z.string().refine(v => v === '' || !PLACEHOLDER_PATTERN.test(v), {
    message: `${label} cannot be a placeholder (TODO/TBD/FIXME/XXX)`
  })
  const bounded =
    maxLength === undefined
      ? base
      : base.max(maxLength, { message: maxLengthMessage ?? `${label} cannot exceed ${maxLength} characters` })
  return bounded.optional().or(z.literal(''))
}

// Re-export entity schemas for convenience
export {
  PlatformSchema,
  AppReleaseTypeSchema,
  AppReleaseStatusSchema,
  AppReleaseTargetStatusSchema,
  AppReleaseEventTypeSchema,
  AppReleaseResponseSchema,
  AppReleaseAppSummarySchema,
  AppReleaseTargetResponseSchema,
  AppReleaseEventResponseSchema,
  AppArtifactKindSchema,
  AppArtifactProviderSchema,
  AppArtifactResponseSchema,
  BuildProfilesSchema,
  MobileAppResponseSchema,
  MobileAppStoreListingSchema,
  StoreListingStateSchema,
  StoreStateSourceSchema,
  STORE_LISTING_STATES,
  OUT_OF_SCOPE_STORE_LISTING_STATES,
  isStoreLaneExcluded,
  DeliveryFactSchema,
  DeliveryBuildFactSchema,
  DeliveryOtaFactSchema,
  DeliveryLiveFactSchema,
  DeliveryLaneSchema,
  DeliveryLanesSchema,
  DeliveryLaneNameSchema,
  DeliveryLaneOutcomeSchema,
  DeliveryFlagSchema,
  DELIVERY_LANES,
  DELIVERY_LANE_OUTCOMES,
  DELIVERY_FLAGS,
  MobileAppPlatformDeliverySchema,
  MobileAppDeliverySchema,
  compareVersions,
  ASC_APP_VERSION_STATES,
  AscAppVersionStateSchema,
  AscReleaseTypeSchema,
  APPLE_VERSION_STATE_PHRASES,
  STORE_VERSION_STATES,
  STORE_VERSION_TERMINAL_STATES,
  STORE_VERSION_WRITERS,
  STORE_VERSION_LANE_CODES,
  STORE_VERSION_STANDINGS,
  STORE_VERSION_UNKNOWN_REASONS,
  STORE_VERSION_STALENESS_MS,
  StoreVersionStateSchema,
  StoreVersionWriterSchema,
  StoreVersionLaneCodeSchema,
  StoreVersionLaneSchema,
  StoreVersionStandingValueSchema,
  StoreVersionUnknownReasonSchema,
  StoreVersionReleaseRequestSchema,
  StoreVersionRolloutSchema,
  StoreVersionActionSchema,
  StoreVersionBoardSchema,
  MobileAppStoreVersionSchema,
  MobileAppPlatformStoreVersionsSchema,
  MobileAppStoreVersionsSchema,
  isStoreVersionInFlight,
  mapAppleVersionState,
  mapPlayReleaseStatus,
  describeStoreVersionState,
  storeVersionStanding,
  GitBranchResponseSchema,
  GitReleaseRefSchema,
  RegionMatrixSchema,
  RegistryTierSchema,
  VersionDeploymentStatusResponseSchema,
  VersionDeploymentAppStatusSchema,
  VersionDeploymentPlatformStatusSchema
} from '../../schemas/entities/mobile-deployment'

export {
  MobileAppAssetSlotSchema,
  MobileAppAssetManifestEntrySchema,
  MobileAppAssetManifestSchema,
  AssetUploadSignatureRequestSchema,
  AssetUploadSignatureResponseSchema,
  AssetCommitRequestSchema,
  AssetCommitResponseSchema,
  AssetInitializeRequestSchema,
  AssetInitializeResponseSchema,
  MOBILE_APP_ASSET_SLOTS,
  MOBILE_APP_ASSET_SLOT_LIST,
  ASSET_SLOT_DELIVERY_LABELS,
  REQUIRED_SLOT_NAMES,
  RUNTIME_SLOT_NAMES,
  assetSlotDeliveryFor,
  isMobileAppAssetSlotName,
  PRE_LOGIN_ASSET_SLOT_NAMES,
  SCHOOL_EDITABLE_ASSET_SLOTS,
  isSchoolEditableAssetSlotName,
  SCHOOL_DRAFT_ASSET_SLOTS,
  isSchoolDraftAssetSlotName,
  PreLoginAssetEntrySchema,
  PreLoginAssetsSchema
} from '../../schemas/entities/mobile-app-asset'

export type {
  MobileAppAssetSlot,
  MobileAppAssetManifestEntry,
  MobileAppAssetManifest,
  AssetUploadSignatureRequest,
  AssetUploadSignatureResponse,
  AssetCommitRequest,
  AssetCommitResponse,
  AssetInitializeRequest,
  AssetInitializeResponse,
  AssetSlotKind,
  AssetSlotDelivery,
  AssetSlotDeliveryVerdict,
  AssetSlotMetadata,
  MobileAppAssetSlotName,
  PreLoginAssetSlotName,
  SchoolEditableAssetSlotName,
  PreLoginAssetEntry,
  PreLoginAssets
} from '../../schemas/entities/mobile-app-asset'

// The store enum itself, re-exported beside the screenshot contracts that key on
// it (SCH-2092). Consumers were writing their own two-value union because this
// barrel carried every screenshot contract EXCEPT the store they are keyed by.
export { StoreListingStoreSchema } from '../../schemas/entities/mobile-app-store-listing'
export type { StoreListingStore } from '../../schemas/entities/mobile-app-store-listing'

// Store screenshots (SCH-1945). Defined beside the catalog that validates them
// and re-exported here, exactly as the slot contracts above are.
export {
  APPLE_SCREENSHOT_SPEC_URL,
  PLAY_SCREENSHOT_SPEC_URL,
  STORE_SCREENSHOT_ALTERNATIVE_GROUPS,
  STORE_SCREENSHOT_CLASS_LIST,
  STORE_SCREENSHOT_DEVICE_CLASSES,
  STORE_SCREENSHOT_DEVICE_CLASS_NAMES,
  STORE_SCREENSHOT_DEVICE_CLASS_PAIR_RULE,
  STORE_SCREENSHOT_VERSION_MAX_LENGTH,
  STORE_SCREENSHOT_VERSION_PATTERN,
  STORE_SCREENSHOT_VERSION_RULE,
  StoreScreenshotDeviceClassSchema,
  StoreScreenshotLocaleSchema,
  StoreScreenshotVersionSchema,
  MobileAppStoreScreenshotSchema,
  StoreScreenshotSetTargetSchema,
  StoreScreenshotSummarySchema,
  StoreScreenshotSetSchema,
  StoreScreenshotListResponseSchema,
  StoreScreenshotPushPlanSchema,
  StoreScreenshotLineSchema,
  StoreScreenshotUploadSignatureRequestSchema,
  StoreScreenshotUploadSignatureResponseSchema,
  StoreScreenshotUploadedImageSchema,
  StoreScreenshotCommitRequestSchema,
  StoreScreenshotCommitResponseSchema,
  StoreScreenshotOrderRequestSchema,
  StoreScreenshotOrderResponseSchema,
  StoreScreenshotRemoveResponseSchema,
  StoreScreenshotCopyRequestSchema,
  StoreScreenshotCopyResponseSchema,
  InternalStoreScreenshotImageSchema,
  InternalStoreScreenshotCommitRequestSchema,
  InternalStoreScreenshotBatchRequestSchema,
  InternalStoreScreenshotBatchResponseSchema,
  alternativeGroupForClass,
  describeScreenshotClassMinimum,
  describeScreenshotValidationError,
  getStoreScreenshotClass,
  normalizeStoreScreenshotVersion,
  orderStoreScreenshotVersions,
  screenshotAlternativeGroupMet,
  screenshotMinimumMet,
  storeScreenshotAlternativeGroups,
  storeScreenshotDeviceClasses,
  storeScreenshotMinimumsMet,
  validateScreenshotCount,
  validateScreenshotMetadata
} from '../../schemas/entities/mobile-app-store-screenshot'

export type {
  AppleScreenshotDeviceClass,
  PlayScreenshotDeviceClass,
  StoreScreenshotDeviceClass,
  StoreScreenshotAspectRatio,
  StoreScreenshotClassSpec,
  StoreScreenshotDimensionRule,
  StoreScreenshotSize,
  ScreenshotMetadata,
  ScreenshotValidationError,
  ScreenshotValidationResult,
  MobileAppStoreScreenshot,
  StoreScreenshotSetTarget,
  StoreScreenshotSummary,
  StoreScreenshotSet,
  StoreScreenshotAlternativeGroup,
  StoreScreenshotListResponse,
  StoreScreenshotPushPlan,
  StoreScreenshotLine,
  StoreScreenshotUploadSignatureRequest,
  StoreScreenshotUploadSignatureResponse,
  StoreScreenshotUploadedImage,
  StoreScreenshotCommitRequest,
  StoreScreenshotCommitResponse,
  StoreScreenshotOrderRequest,
  StoreScreenshotOrderResponse,
  StoreScreenshotRemoveResponse,
  StoreScreenshotCopyRequest,
  StoreScreenshotCopyResponse,
  InternalStoreScreenshotImage,
  InternalStoreScreenshotCommitRequest,
  InternalStoreScreenshotBatchRequest,
  InternalStoreScreenshotBatchResponse
} from '../../schemas/entities/mobile-app-store-screenshot'

export {
  APP_READINESS_CAPABILITIES,
  ASC_WEBHOOK_READINESS_STATES,
  AppReadinessCapabilitySchema,
  AppReadinessStatusSchema,
  AppReadinessMissingReasonSchema,
  AppReadinessMissingFieldSchema,
  AppReadinessCapabilityResultSchema,
  AppReadinessRefusalSchema,
  READINESS_REGISTRY_COLUMNS,
  isReadinessRegistryColumn,
  classifyFieldValue,
  READINESS_EDITABLE_COLUMNS,
  isReadinessEditableColumn,
  AppReadinessNextActionSchema,
  AscWebhookCannotCheckReasonSchema,
  AscWebhookReadinessSchema,
  AscWebhookReadinessStateSchema,
  MobileAppReadinessSchema,
  SIGNAL_FRESHNESS_STATES,
  SIGNAL_MODES,
  PollSignalDetailSchema,
  SignalCoverageCountsSchema,
  SignalFreshnessSchema,
  SignalFreshnessStateSchema,
  SignalModeSchema
} from '../../schemas/entities/mobile-app-readiness'

export type {
  AppReadinessCapability,
  AppReadinessStatus,
  AppReadinessMissingReason,
  AppReadinessMissingField,
  AppReadinessCapabilityResult,
  AppReadinessRefusal,
  ReadinessColumn,
  ReadinessRegistryColumn,
  ReadinessEditableColumn,
  AppReadinessNextAction,
  ReadinessVaultColumn,
  ReadinessChannelColumn,
  AscWebhookCannotCheckReason,
  AscWebhookReadiness,
  AscWebhookReadinessState,
  MobileAppReadiness,
  PollSignalDetail,
  SignalCoverageCounts,
  SignalFreshness,
  SignalFreshnessState,
  SignalMode
} from '../../schemas/entities/mobile-app-readiness'

export {
  MOBILE_APP_EVENT_CLASSES,
  MOBILE_APP_EVENT_TYPES,
  MOBILE_APP_EVENT_TYPE_LIST,
  MOBILE_APP_EVENT_ACTOR_TYPES,
  MOBILE_APP_EVENT_PAGE_SIZE_DEFAULT,
  MOBILE_APP_EVENT_PAGE_SIZE_MAX,
  MobileAppEventClassSchema,
  MobileAppEventTypeSchema,
  MobileAppEventActorTypeSchema,
  MobileAppEventSchema,
  MobileAppEventListSchema,
  isMobileAppEventType,
  mobileAppEventClassFor
} from '../../schemas/entities/mobile-app-event'

export type {
  MobileAppEvent,
  MobileAppEventClass,
  MobileAppEventType,
  MobileAppEventActorType,
  MobileAppEventList
} from '../../schemas/entities/mobile-app-event'

export {
  DEFAULT_PRE_AUTH_THEME,
  MobileAppBrandingConfigSchema,
  MobileAppBuildBrandingSchema,
  MobileAppLoginLayoutSchema,
  MobileAppPreAuthThemeSchema,
  MobileAppPreAuthThemeModeSchema
} from '../../schemas/entities/mobile-app-branding'

export type {
  MobileAppBrandingConfig,
  MobileAppBuildBranding,
  MobileAppLoginLayout,
  MobileAppPreAuthTheme,
  MobileAppPreAuthThemeMode
} from '../../schemas/entities/mobile-app-branding'

import { MobileAppAssetManifestSchema, MobileAppBuildBrandingSchema } from '../../schemas/entities'
import {
  MOBILE_APP_EVENT_PAGE_SIZE_MAX,
  MobileAppEventClassSchema,
  MobileAppEventSchema
} from '../../schemas/entities/mobile-app-event'
import { MobileAppBrandingConfigSchema, MobileAppLoginLayoutSchema } from '../../schemas/entities/mobile-app-branding'
import { PreLoginAssetsSchema } from '../../schemas/entities/mobile-app-asset'
import {
  MobileAppOnboardingConfigSchema,
  MobileAppOnboardingConfigSourceSchema
} from '../../schemas/entities/mobile-app-onboarding'
import {
  StoreListingFieldSchema,
  StoreListingLocaleSchema,
  StoreListingRecordSchema,
  StoreListingStoreSchema
} from '../../schemas/entities/mobile-app-store-listing'
// Reused verbatim from the provisioning-run inputs: the registry's credential upload and the
// provisioning wizard vault into the SAME `Integration` rows, so a second near-identical shape
// here would let the two intakes drift into writing differently-shaped blobs.
import { ApnsAuthKeyInputSchema, AscApiKeyInputSchema } from '../../schemas/entities/provisioning'

/**
 * GET /v1/mobile-apps/by-school-app/:schoolAppId/asset-manifest
 *
 * Public, EAS-build-pipeline-facing response. Carries the asset slot manifest
 * (unchanged) plus the registry-sourced build branding the staging script
 * materializes into the staged `build.config.json`. `branding` is always
 * present but every field inside it is optional, so a partially-configured
 * registry row still yields a build-usable (default-padded) payload.
 */
export const AssetManifestBySchoolAppResponseSchema = MobileAppAssetManifestSchema.extend({
  branding: MobileAppBuildBrandingSchema
})
export type AssetManifestBySchoolAppResponse = z.infer<typeof AssetManifestBySchoolAppResponseSchema>

// ==========================================
// School-app directory (pre-login school switcher — plan 2026-08-13-001 R8)
// ==========================================

/**
 * One entry of the public school-app directory.
 *
 * `displayName` is nullable because the registry column is: a row created by
 * phase 1 of provisioning has no name yet. A client that gets `null` derives a
 * label from the slug rather than rendering an empty row.
 */
export const SchoolAppDirectoryEntrySchema = z.object({
  schoolAppId: z.string(),
  displayName: z.string().nullable()
})
export type SchoolAppDirectoryEntry = z.infer<typeof SchoolAppDirectoryEntrySchema>

/**
 * GET /v1/mobile-apps/school-apps
 *
 * PUBLIC read (no auth) — same posture as the two by-school-app reads above,
 * and read from the same place: before login, by the browser build's onboarding
 * school switcher. It is the ONLY way that switcher can name a school, because
 * the pre-login screen has no session and the admin list endpoint is
 * super-admin-gated.
 *
 * Deliberately a two-field projection of active rows, not a subset of the admin
 * list shape: an unauthenticated caller gets the identifiers a preview link
 * already needs (`?id=<schoolAppId>`) and the name a store listing already
 * shows, and nothing about readiness, provisioning, credentials or delivery.
 */
export const SchoolAppDirectoryResponseSchema = z.object({
  schoolApps: z.array(SchoolAppDirectoryEntrySchema)
})
export type SchoolAppDirectoryResponse = z.infer<typeof SchoolAppDirectoryResponseSchema>

// ==========================================
// School key (build-config / dispatch key, always derived from schoolAppId — SCH-2084)
// ==========================================

export {
  deriveSchoolKey,
  EXPO_SLUG_SCHOOL_PREFIX,
  expoSlugBase,
  formatExpoProjectSlug,
  formatSchoolAppId,
  joinWithinLimit,
  paddedSchoolId,
  projectIdCandidate,
  schoolKeyFor,
  slugFromSchoolAppId
} from './school-key'

// Fleet Firebase attestation / approval (SCH-2134 U4a). Its own file because
// the contract belongs to the fleet lane rather than to the app registry, and
// this module is already 4,000 lines.
export {
  FleetFirebaseApprovableDecisionSchema,
  FleetFirebaseApprovalSchema,
  FleetFirebaseAttestationRequestSchema,
  FleetFirebaseAttestationResponseSchema,
  FleetFirebaseQuotaAttestationSchema,
  FleetInventoryFingerprintSchema,
  type FleetFirebaseApprovableDecision,
  type FleetFirebaseAttestationRequest,
  type FleetFirebaseAttestationResponse
} from './fleet-firebase'

// School-started setups as provisioning candidates (release lines U10).
export {
  ListSchoolAppSetupCandidatesResponseSchema,
  SchoolAppSetupCandidateSchema,
  type ListSchoolAppSetupCandidatesResponse,
  type SchoolAppSetupCandidate
} from './setup-candidates'

// The app setup graph and its run-full-setup drive (SCH-2236).
export * from './setup-graph'

// Which setup, store-submission and EAS runs are still moving, finished, or retryable.
export * from './setup-lifecycle'

// Brand art generation and store-listing autofill (SCH-2236).
export * from './brand-art'
export * from './setup-preflight'

// The registry gate every build of a school app waits on (SCH-2318).
export * from './build-readiness'
// The readiness view: stored readiness, the optional live gate, the one next action (SCH-2963).
export * from './app-readiness-view'
// What a build dispatch does with the gate's reading, per variant (SCH-2661).
export * from './build-gate'

// Which stage of the release-target machine each status is in (SCH-2661).
export * from './release-target-stage'

// The Rejections tab: rejection history, reviewer text and remedy runs (SCH-2889).
export * from './rejections'

// Apple team discovery and App Store Connect API key minting for publishing accounts.
export * from './apple-team-access'

// ==========================================
// Onboarding Config (server-driven pre-login copy/slides — plan 2026-06-20-002 U2)
// ==========================================

export { MobileAppOnboardingConfigSchema, MobileAppOnboardingConfigSourceSchema }
export { MobileAppOnboardingSlideSchema, ONBOARDING_CONFIG_SOURCES } from '../../schemas/entities/mobile-app-onboarding'
export type {
  MobileAppOnboardingConfig,
  MobileAppOnboardingConfigSource,
  MobileAppOnboardingSlide
} from '../../schemas/entities/mobile-app-onboarding'

/**
 * GET /v1/mobile-apps/by-school-app/:schoolAppId/onboarding-config
 *
 * PUBLIC read (no auth) — symmetric with the asset-manifest-by-school-app
 * endpoint. Read before login by the mobile app, so unauthenticated. Resolves
 * the build-time `schoolAppId` to a MobileApp and returns its stored onboarding
 * config. A row with no stored config (or a corrupt one) yields `config: null`
 * so the mobile fallback can take over; an unknown school is a 404.
 *
 * `onboardingEnabled` is a TOP-LEVEL field, present in every one of the three
 * states — stored config with a registry decision, stored config with no decision
 * (`null`), and no stored config at all (`config: null` plus the derived-or-null
 * switch). Deliberately not only inside `config`: the copy object is replaced
 * whole by whoever edits it, so a switch that lived only in there could be dropped
 * by a copy edit and silently re-enable a school's carousel (plan 2026-09-01-2310
 * R8 / KTD5). `null` means "no decision recorded anywhere", which the device
 * resolves by falling through to its baked snapshot and then to ON — an unseeded
 * row can never read as a decision.
 */
export const OnboardingConfigBySchoolAppResponseSchema = z.object({
  schoolAppId: z.string(),
  config: MobileAppOnboardingConfigSchema.nullable(),
  /**
   * Pre-login image URLs, composed at read time from the app's asset slot rows
   * and never stored in the config JSON (so a re-upload cannot leave a stale
   * URL behind). Optional so a new binary parses an older backend's body that
   * has no `assets` key; no passthrough, so a slot outside the pre-login set
   * cannot ride along.
   */
  assets: PreLoginAssetsSchema.optional(),
  // `.default(null)` rather than a bare required field: the OUTPUT type is
  // `boolean | null`, so the backend cannot build a response without deciding the
  // value, while a body that predates this field still parses. The mobile client
  // validates every response against this same schema, and a hard requirement
  // would make it reject — and fall back to bundled copy on — any backend
  // deployed before this change.
  onboardingEnabled: z.boolean().nullable().default(null)
})
export type OnboardingConfigBySchoolAppResponse = z.infer<typeof OnboardingConfigBySchoolAppResponseSchema>

/**
 * PUT /v1/mobile-apps/:id/onboarding-config
 *
 * Super-admin write. The body IS the onboarding config (validated against
 * `MobileAppOnboardingConfigSchema`), persisted to `MobileApp.onboardingConfig`
 * so pre-login copy is editable without a rebuild.
 *
 * ## HOW THIS LANE CLEARS AN OPTIONAL KEY: IT OMITS IT (SCH-1906)
 *
 * The write is whole-blob — the stored column becomes exactly what this body parsed
 * to — so an optional key left OUT of the body is a key removed from the stored JSON,
 * and the app goes back to the layout's own committed value. That is why this schema
 * gets no `null` arm the way `UpdateBrandingConfigRequestSchema` does: `null` exists
 * there to express a removal a MERGE cannot, and there is no merge here. Adding one
 * would put a second spelling of "cleared" on an object the mobile client swaps in
 * WHOLE, where a stray `null` is a value some layout would try to render.
 *
 * A REQUIRED key left out is still a 400, not a silent drop — the schema is strict
 * about both directions, so only the `?` slots can be cleared this way.
 */
export const UpdateOnboardingConfigRequestSchema = MobileAppOnboardingConfigSchema
export type UpdateOnboardingConfigRequest = z.infer<typeof UpdateOnboardingConfigRequestSchema>

/**
 * Header the `/internal/` PUT reads to record which automated lane wrote a
 * config (plan 2026-09-01-2310 U11 / R18).
 *
 * A HEADER rather than a body field, because the body IS the config: the
 * request schema is `.strict()` and the mobile client swaps the stored object
 * in for its bundled one WHOLE, so anything that rides in the body is copy the
 * device would try to render. A transport hint is not copy.
 *
 * Internal-lane only. `/v1/` never reads it — that lane's source is derived from
 * the Clerk principal, and a header that could override it would let a browser
 * session file its edit under an automation's name.
 */
export const INTERNAL_ONBOARDING_CONFIG_SOURCE_HEADER = 'x-onboarding-config-source'

/**
 * The source values an internal caller may CLAIM. Deliberately narrower than
 * `MobileAppOnboardingConfigSourceSchema`:
 *
 * - `super_admin` is excluded because no API-key caller is a person, and a
 *   version row attributed to a human that no human wrote is worse than one with
 *   no attribution at all — it is the row an operator trusts most.
 * - `pre_login_settings` is excluded because that value means "the switch mirror
 *   displaced this copy, nobody was editing it". It IS a real member of
 *   `ONBOARDING_CONFIG_SOURCES` and is written today — by
 *   `services/mobile-apps/crud/pre-login-settings.ts`, which passes it directly.
 *   Only that mirror can claim it truthfully, and it does not come through this
 *   route, so accepting it here would let a caller forge the one lane that
 *   means "the copy in the displaced row was not being edited".
 *
 * Absent header ⇒ `internal_api`, so every caller that predates this stays
 * exactly as it was.
 */
export const InternalOnboardingConfigSourceSchema = z.enum(['internal_api', 'seed_script'])
export type InternalOnboardingConfigSource = z.infer<typeof InternalOnboardingConfigSourceSchema>

export const UpdateOnboardingConfigResponseSchema = z.object({
  data: z.object({
    mobileAppId: z.string(),
    config: MobileAppOnboardingConfigSchema
  }),
  message: z.string()
})
export type UpdateOnboardingConfigResponse = z.infer<typeof UpdateOnboardingConfigResponseSchema>

/**
 * Why `config` is null on the admin read, which are NOT the same situation.
 *
 * - `stored` — a valid config came back, and `config` carries it.
 * - `absent` — the column is empty. The app serves the copy baked into its build, and
 *   the registry copy is seeded on app creation or by the seed script.
 * - `invalid` — the column holds something that no longer parses. The copy is still
 *   there and still restorable from the version history, so an editor must NOT offer to
 *   overwrite it as though the app had never been configured.
 *
 * The public by-schoolAppId read deliberately collapses the last two into `null`: a
 * device has to boot, and it falls through to its bundled copy either way. An operator
 * screen has the opposite obligation — the difference between the two is the difference
 * between a first save and silent data loss.
 */
export const MobileAppOnboardingConfigStateSchema = z.enum(['stored', 'absent', 'invalid'])
export type MobileAppOnboardingConfigState = z.infer<typeof MobileAppOnboardingConfigStateSchema>

/**
 * GET /v1/mobile-apps/:id/onboarding-config
 *
 * Super-admin read by MobileApp `publicId` — the authenticated counterpart to
 * the public by-school-app read, so an agent/admin can read-then-edit by the
 * same `publicId` the PUT writes with (no schoolAppId side-channel). `config`
 * is null unless `configState` is `stored`; unknown app is 404.
 */
export const GetOnboardingConfigResponseSchema = z.object({
  mobileAppId: z.string(),
  configState: MobileAppOnboardingConfigStateSchema,
  config: MobileAppOnboardingConfigSchema.nullable()
})
export type GetOnboardingConfigResponse = z.infer<typeof GetOnboardingConfigResponseSchema>

// ==========================================
// Onboarding config version history (plan 2026-09-01-2310 U8 / R18 / KTD9)
// ==========================================

/**
 * One entry in an app's onboarding-config history.
 *
 * READ IT AS "WHAT WAS REPLACED", NOT "WHAT WAS SAVED". A row is written by the
 * write that DISPLACED this config, so `config` is the value that stopped being
 * live at `replacedAt` and `actorId` / `source` name whoever displaced it. The
 * live config is never in this list. Labelling a row by its save would be off by
 * one and would offer an operator the version they already have.
 *
 * `config` is nullable and null is a real entry, not a gap: an app that had no
 * stored config until the write that produced this row has null as its
 * restorable prior state, so the first write to a fresh app is undoable like any
 * other. See the `MobileAppOnboardingConfigVersion` model comment.
 */
export const MobileAppOnboardingConfigVersionSchema = z.object({
  publicId: z.string(),
  config: MobileAppOnboardingConfigSchema.nullable(),
  source: MobileAppOnboardingConfigSourceSchema,
  actorId: z.string().nullable(),
  actorLabel: z.string().nullable(),
  replacedAt: z.string().datetime()
})
export type MobileAppOnboardingConfigVersion = z.infer<typeof MobileAppOnboardingConfigVersionSchema>

/**
 * GET /v1/mobile-apps/:id/onboarding-config/versions
 *
 * Super-admin read — the SAME gate as the write, because a version body is
 * registry content in full and reading one is reading the school's copy.
 * Newest first. An app that has never been written returns an empty array, which
 * is a fact about the app and not an error.
 */
export const ListOnboardingConfigVersionsResponseSchema = z.object({
  mobileAppId: z.string(),
  versions: z.array(MobileAppOnboardingConfigVersionSchema)
})
export type ListOnboardingConfigVersionsResponse = z.infer<typeof ListOnboardingConfigVersionsResponseSchema>

/**
 * POST /v1/mobile-apps/:id/onboarding-config/versions/:versionId/restore
 *
 * Super-admin write. The version is addressed by path, so there is no body —
 * a restore carries no operator input beyond which row to put back.
 *
 * `config` in the response is what is now LIVE, so it is nullable for the same
 * reason a version body is: restoring an app's pre-config state clears the
 * column, and that is a legitimate undo rather than a failed restore. The
 * restore is itself a write, so it records the config it displaced as a new
 * version — rolling forward again is a second restore, not a lost value.
 */
export const RestoreOnboardingConfigVersionResponseSchema = z.object({
  data: z.object({
    mobileAppId: z.string(),
    restoredVersionId: z.string(),
    config: MobileAppOnboardingConfigSchema.nullable()
  }),
  message: z.string()
})
export type RestoreOnboardingConfigVersionResponse = z.infer<typeof RestoreOnboardingConfigVersionResponseSchema>

// ==========================================
// Pre-login settings (onboarding switch + login layout)
// ==========================================

/**
 * The two pre-login SHAPE decisions for one app, as the admin registry holds them.
 *
 * Separate from the onboarding *copy* endpoints above on purpose: that config is
 * replaced whole by the client when the registry serves it, so a partial write to
 * flip a switch would blank a school's baked wording. These settings are stored on
 * `MobileApp.brandingConfig` and can be written on their own.
 *
 * - `onboardingEnabled` — `null` when nothing is stored anywhere, which resolves to
 *   ON in the app (the mobile getter reads `?? true`).
 * - `loginLayout` — `null` when the registry has never recorded one; the build
 *   defaults to `classic`.
 * - `onboardingConfigStored` — whether a full `onboardingConfig` exists on the row.
 *   When it does, a write mirrors the switch into it (that copy is what the runtime
 *   actually reads); when it does not, the switch is stored on `brandingConfig`
 *   alone and no copy is invented.
 */
export const MobileAppPreLoginSettingsSchema = z.object({
  onboardingEnabled: z.boolean().nullable(),
  loginLayout: MobileAppLoginLayoutSchema.nullable(),
  onboardingConfigStored: z.boolean()
})
export type MobileAppPreLoginSettings = z.infer<typeof MobileAppPreLoginSettingsSchema>

/** GET /v1/mobile-apps/:id/pre-login-settings — super-admin read by MobileApp `publicId`. */
export const GetPreLoginSettingsResponseSchema = z.object({
  mobileAppId: z.string(),
  settings: MobileAppPreLoginSettingsSchema
})
export type GetPreLoginSettingsResponse = z.infer<typeof GetPreLoginSettingsResponseSchema>

/**
 * PATCH /v1/mobile-apps/:id/pre-login-settings — super-admin partial write.
 *
 * Partial by design: the section's two controls save independently of each other and
 * of every other registry column, so an omitted key means "leave it alone" rather
 * than "clear it". An empty body is rejected — it is always a caller bug, and
 * silently succeeding would report a save that wrote nothing.
 */
export const UpdatePreLoginSettingsRequestSchema = z
  .object({
    onboardingEnabled: z.boolean().optional(),
    loginLayout: MobileAppLoginLayoutSchema.optional()
  })
  .strict()
  .refine(body => Object.keys(body).length > 0, { message: 'At least one setting must be provided' })
export type UpdatePreLoginSettingsRequest = z.infer<typeof UpdatePreLoginSettingsRequestSchema>

export const UpdatePreLoginSettingsResponseSchema = z.object({
  data: z.object({
    mobileAppId: z.string(),
    settings: MobileAppPreLoginSettingsSchema
  }),
  message: z.string()
})
export type UpdatePreLoginSettingsResponse = z.infer<typeof UpdatePreLoginSettingsResponseSchema>

/**
 * PATCH /v1/mobile-apps/:id/branding-config (and its `/internal/` twin) —
 * super-admin partial write of the branding remainder stored on
 * `MobileApp.brandingConfig`.
 *
 * Partial at the TOP level only: a present key replaces that key's whole value, an
 * absent one is left alone. `button` and `preAuthTheme` are therefore sent complete
 * or not at all, which is deliberate — a half-written theme is not a theme, and a
 * deep merge would let a caller leave one HSL channel behind from a previous edit.
 *
 * `loginLayout` and `onboardingEnabled` are OMITTED, and `.strict()` turns them into
 * a 400 rather than a silent no-op. They live on this column but they are not this
 * endpoint's to write: `updatePreLoginSettings` mirrors `onboardingEnabled` into a
 * stored `onboardingConfig`, because that copy — not this one — is what the mobile
 * runtime reads. A branding write accepting the key would flip `brandingConfig`
 * alone, skip the mirror, and return a 200 for a change the device never sees.
 * PATCH `/pre-login-settings` owns both keys.
 *
 * `.strict()` also because a typo'd key would otherwise be accepted, stored, and then
 * refused by the entity schema on the NEXT read — turning one bad save into a
 * column the admin can no longer edit at all.
 *
 * An empty body is rejected: it is always a caller bug, and reporting success for a
 * save that wrote nothing is how an operator concludes the registry took a change
 * it never took.
 *
 * ## THREE STATES PER KEY, NOT TWO (SCH-1906)
 *
 * Every key here is `.nullable().optional()`, and the three inputs mean three different
 * things:
 *
 * - **absent** — leave the stored value alone. Unchanged from before this existed, so
 *   every caller that predates it behaves exactly as it did.
 * - **a value** — replace that key's whole value.
 * - **`null`** — REMOVE the key from the stored JSON, so the app falls back to the
 *   platform default again.
 *
 * Until `null` existed the endpoint took values only, so an operator who had set a
 * per-school colour could replace it forever but never get back to "inherit the
 * default" — the column is read-modify-written, and a key the body omits is a key the
 * merge preserves. "Set to the default value" and "inherit the default" are not the
 * same stored state: the first pins a school to today's default, the second follows it.
 *
 * `null` is safe to spend on this meaning because no field on this column accepts it
 * for anything else — `MobileAppBrandingConfigSchema` has no nullable member, so a
 * stored `null` has never been a legal branding value and cannot be confused with one.
 *
 * Nullable at the TOP LEVEL ONLY. `button.color: null` and
 * `preAuthTheme.light.background: null` are still 400s, because the nested slots are
 * required: `preAuthTheme` goes whole or not at all (cleared whole, or replaced whole),
 * and a theme missing one HSL channel is not a theme.
 */
export const UpdateBrandingConfigRequestSchema = z
  .object({
    // Built key-by-key off the entity shape rather than `.omit().partial()`, because
    // `.partial()` cannot express the third state: it makes a key skippable, not
    // clearable. `.unwrap()` takes the already-optional entity member back to its base
    // schema so the value arm keeps validating exactly as it did.
    loginBackground: MobileAppBrandingConfigSchema.shape.loginBackground.unwrap().nullable().optional(),
    onboardingBackground: MobileAppBrandingConfigSchema.shape.onboardingBackground.unwrap().nullable().optional(),
    button: MobileAppBrandingConfigSchema.shape.button.unwrap().nullable().optional(),
    loginText: MobileAppBrandingConfigSchema.shape.loginText.unwrap().nullable().optional(),
    preAuthTheme: MobileAppBrandingConfigSchema.shape.preAuthTheme.unwrap().nullable().optional()
  })
  .strict()
  // `Object.keys` and not a "some key is defined" check: a body of `{ loginText: null }`
  // is a real change — it removes a stored value — and counting it as empty would refuse
  // the one write this endpoint could not previously express.
  .refine(body => Object.keys(body).length > 0, { message: 'At least one branding field must be provided' })
export type UpdateBrandingConfigRequest = z.infer<typeof UpdateBrandingConfigRequestSchema>

/**
 * GET /v1/mobile-apps/:id/branding-config — super-admin read by MobileApp `publicId`.
 *
 * The read the PATCH above needs to be usable: an editor that could only write would
 * have to invent the colors it is about to replace. `brandingConfig` is `null` for a row
 * that stores none — a real state (the app ships the platform defaults), not an error —
 * and an unknown app is a 404.
 *
 * It returns the WHOLE column, `loginLayout` and `onboardingEnabled` included, even
 * though the PATCH refuses to write them. Reading is not writing: the pre-login editor
 * already surfaces those two through its own endpoint, and a filtered read here would
 * make this response disagree with the column it claims to describe.
 */
export const GetBrandingConfigResponseSchema = z.object({
  mobileAppId: z.string(),
  brandingConfig: MobileAppBrandingConfigSchema.nullable()
})
export type GetBrandingConfigResponse = z.infer<typeof GetBrandingConfigResponseSchema>

export const UpdateBrandingConfigResponseSchema = z.object({
  data: z.object({
    mobileAppId: z.string(),
    brandingConfig: MobileAppBrandingConfigSchema
  }),
  message: z.string()
})
export type UpdateBrandingConfigResponse = z.infer<typeof UpdateBrandingConfigResponseSchema>

/**
 * Why a platform's demo login cannot be read or cleared. The two causes live in
 * different systems and are fixed by different people, so they are never
 * collapsed into a bare `available: false`:
 *
 * - `MISSING_NAMESPACE` — the MobileApp row has no bundle id / package name.
 *   Ours to fix, on the Identifiers section of this same dialog.
 * - `FLAG_SHAPE` — the app has a namespace, but the `app_store_demo_login` flag
 *   holds a group this platform cannot read or will not edit blind: a
 *   release-condition group that could match the app carries a condition the
 *   toggle cannot read (an unknown property, a non-`exact` operator, a partial
 *   rollout, no exact `$app_namespace`), two groups for the same version and
 *   platform, or an `$app_version` condition naming the app that is not one exact
 *   version. Whether the app is on cannot be told without guessing, so nothing is
 *   edited. Fixed in PostHog, not here; `unreadableGroupCount` says how many
 *   groups.
 */
export const MobileAppDemoLoginUnavailableReasonSchema = z.enum(['MISSING_NAMESPACE', 'FLAG_SHAPE'])
export type MobileAppDemoLoginUnavailableReason = z.infer<typeof MobileAppDemoLoginUnavailableReasonSchema>

/**
 * A marketing version the demo-login flag can be pinned to: `1.9.1`, `1.1`, or a
 * four-part store resubmission (`1.9.1.1`). This is the `$app_version` the app
 * reports, so a release line (`1.9`) is only valid where an app really ships as
 * one, and anything with a suffix (`1.9.1-rc`) never matches a store build.
 */
export const DemoLoginVersionSchema = z
  .string()
  .trim()
  .regex(/^\d+(\.\d+){1,3}$/, 'Use a marketing version such as 1.9.1')
export type DemoLoginVersion = z.infer<typeof DemoLoginVersionSchema>

/**
 * One release-condition group of `app_store_demo_login` that lists this app's
 * namespace and applies to the platform.
 *
 * The flag is per version since 2026-10 (SCH-3141): each group pairs a set of
 * namespaces with an `$app_version` list, an `$os` list, or both. `appVersions`
 * is `null` when the group has no version condition — demo login is on for every
 * version of the app, which is the shape to avoid, since the button then shows in
 * production builds too. `osValues` is `null` when the group has no `$os`
 * condition and so applies to both platforms at once.
 */
export const MobileAppDemoLoginGroupSchema = z.object({
  appVersions: z.array(z.string()).nullable(),
  osValues: z.array(z.string()).nullable(),
  /** How many apps share the group, this one included. */
  namespaceCount: z.number().int().nonnegative()
})
export type MobileAppDemoLoginGroup = z.infer<typeof MobileAppDemoLoginGroupSchema>

/**
 * One platform's demo-login state, as read from PostHog at `checkedAt`.
 *
 * Demo login is targeted at the (app version, namespace) pair sent to store
 * review, not at the app: the flag carries one release group per app version
 * and platform, matching `$app_version` (the binary's marketing version,
 * CFBundleShortVersionString on iOS and versionName on Android, e.g. "1.9.1"),
 * `$os`, and an `$app_namespace` list of the apps in review at that version. The
 * app's namespace goes into the group when a version is submitted and comes out
 * when that version is released, and a version already live for the app is
 * never added, so a live school build never matches (SCH-2929). A super-admin
 * can also turn it on for one named version from the dashboard (SCH-3141).
 *
 * - `enabled` — a LIVE read of whether this app's namespace appears in any
 *   `app_store_demo_login` group that applies to that platform (its `$os`, or no
 *   `$os`): `versions` is non-empty or `legacyNamespaceListed` is true.
 * - `versions` — the app versions the flag targets for this app now, from its
 *   platform's groups and from any version group with no `$os` (which matches
 *   both platforms). Empty when nothing is in review.
 * - `legacyNamespaceListed` — the app's namespace still sits in a pre-SCH-2929
 *   whole-app group (namespace and `$os`, no `$app_version`), which matches
 *   EVERY build of the app, the live one included. The next submission or clear
 *   removes it.
 * - `groups` — every group that lists the app and applies to the platform, as
 *   read; `unreadableGroupCount` counts the ones that could match but cannot be
 *   read.
 *
 * There is no stored mirror, so a client must not cache it or default it.
 * `namespace` is the value that was looked for (`bundleId` for iOS,
 * `androidPackage` for Android) and is `null` only when the app carries none. A
 * client renders an unavailable platform as disabled with `unavailableReason`
 * explained, never as an off switch.
 *
 * `osCoverageComplete` is a separate axis from `enabled`: it says whether every
 * group listing this app matches every `$os` value that platform's devices
 * actually report. iPads report `$os = "iPadOS"`, so an iOS group listing only
 * `"iOS"` is enabled-but-invisible to every reviewer on an iPad — the exact
 * shape that got an app rejected on 2026-08-30. It is `false` whenever the
 * platform is unavailable, because an unreadable group covers nothing, and
 * `true` while the app is off, because no group is narrowed for it. Turning
 * demo login on repairs the group it writes to, so a client has no repair
 * action of its own to offer beyond that.
 */
export const MobileAppDemoLoginPlatformStateSchema = z.object({
  platform: PlatformSchema,
  available: z.boolean(),
  unavailableReason: MobileAppDemoLoginUnavailableReasonSchema.nullable(),
  enabled: z.boolean(),
  versions: z.array(z.string()),
  legacyNamespaceListed: z.boolean(),
  namespace: z.string().nullable(),
  osCoverageComplete: z.boolean(),
  /** Every group that turns demo login on for this app on this platform; empty when off. */
  groups: z.array(MobileAppDemoLoginGroupSchema),
  /** Groups that could match this app but cannot be read. Non-zero only with `FLAG_SHAPE`. */
  unreadableGroupCount: z.number().int().nonnegative()
})
export type MobileAppDemoLoginPlatformState = z.infer<typeof MobileAppDemoLoginPlatformStateSchema>

export const MobileAppDemoLoginStateSchema = z.object({
  mobileAppId: z.string(),
  flagKey: z.string(),
  checkedAt: z.string(),
  platforms: z.array(MobileAppDemoLoginPlatformStateSchema)
})
export type MobileAppDemoLoginState = z.infer<typeof MobileAppDemoLoginStateSchema>

/** GET /v1/mobile-apps/:id/demo-login — super-admin live read by MobileApp `publicId`. */
export const GetDemoLoginStateResponseSchema = MobileAppDemoLoginStateSchema
export type GetDemoLoginStateResponse = z.infer<typeof GetDemoLoginStateResponseSchema>

/**
 * PATCH /v1/mobile-apps/:id/demo-login — super-admin write of ONE platform.
 *
 * Two body shapes, the same endpoint:
 *
 * - `{ platform, action: 'clear' }` (SCH-2929) — takes the app's namespace out
 *   of every version group on the platform and out of any legacy whole-app
 *   group, so no build of the app on that platform matches. A version group with
 *   no `$os` serves both platforms: the app leaves it and joins the other
 *   platform's own group for that version, so the other platform keeps its
 *   match. `{ platform, enabled: false }` is the same write.
 * - `{ platform, enabled: true, version }` (SCH-3141) — puts the app's namespace
 *   into the group for exactly this platform and version. Never a whole-app
 *   target: that is what once showed the demo button to live users. The
 *   submission steps still set demo login for the version they send to review
 *   on their own; this is the operator's way to do it by hand.
 *
 * One platform per request on purpose: each write is a read-modify-write against
 * a flag humans also edit, and a two-platform body would have to define what a
 * half-applied change means.
 */
export const UpdateDemoLoginClearRequestSchema = z
  .object({
    platform: PlatformSchema,
    action: z.literal('clear')
  })
  .strict()

export const UpdateDemoLoginToggleRequestSchema = z
  .object({
    platform: PlatformSchema,
    enabled: z.boolean(),
    /**
     * Turning on: the marketing version the reviewer's build reports. The app's
     * namespace goes into the group for exactly this platform and version, through
     * the same path the submission steps use, so a version already live is skipped.
     * Required when turning on (whole-app targeting is not allowed). Ignored when
     * turning off, which removes the app from every version.
     */
    version: DemoLoginVersionSchema.optional()
  })
  .strict()

export const UpdateDemoLoginRequestSchema = z.union([
  UpdateDemoLoginClearRequestSchema,
  UpdateDemoLoginToggleRequestSchema
])
export type UpdateDemoLoginRequest = z.infer<typeof UpdateDemoLoginRequestSchema>

export const UpdateDemoLoginResponseSchema = z.object({
  data: MobileAppDemoLoginStateSchema,
  message: z.string()
})
export type UpdateDemoLoginResponse = z.infer<typeof UpdateDemoLoginResponseSchema>

/**
 * One app in the fleet dry-run read: which platforms still sit in a legacy
 * whole-app group (matching every build of the app) and which app versions the
 * flag targets for it. A read only — there is no bulk-conversion write; each
 * app converts on its next submission or clear.
 */
export const DemoLoginFleetAppSchema = z.object({
  mobileAppId: z.string(),
  name: z.string().nullable(),
  platforms: z.array(
    z.object({
      platform: PlatformSchema,
      namespace: z.string(),
      legacyNamespaceListed: z.boolean(),
      versions: z.array(z.string())
    })
  )
})
export type DemoLoginFleetApp = z.infer<typeof DemoLoginFleetAppSchema>

/** GET /internal/mobile-apps/demo-login/legacy — fleet dry-run of the SCH-2929 conversion. */
export const GetDemoLoginFleetResponseSchema = z.object({
  flagKey: z.string(),
  checkedAt: z.string(),
  /** Namespaces in a legacy shared group that no registry row carries. */
  unknownLegacyNamespaces: z.array(z.object({ platform: PlatformSchema, namespace: z.string() })),
  /** Apps with a namespace in a legacy group. */
  legacyApps: z.array(DemoLoginFleetAppSchema),
  /** Apps with at least one version targeted now. */
  targetedApps: z.array(DemoLoginFleetAppSchema)
})
export type GetDemoLoginFleetResponse = z.infer<typeof GetDemoLoginFleetResponseSchema>

export type {
  Platform,
  AppReleaseType,
  AppReleaseStatus,
  AppReleaseTargetStatus,
  AppReleaseEventType,
  AppReleaseResponse,
  AppReleaseAppSummary,
  AppReleaseTargetResponse,
  AppReleaseEventResponse,
  AppArtifactKind,
  AppArtifactProvider,
  AppArtifactResponse,
  BuildProfiles,
  MobileAppResponse,
  MobileAppStoreListing,
  StoreListingState,
  StoreStateSource,
  OutOfScopeStoreListingState,
  DeliveryFact,
  DeliveryBuildFact,
  DeliveryOtaFact,
  DeliveryLiveFact,
  DeliveryLane,
  DeliveryLanes,
  DeliveryLaneName,
  DeliveryLaneOutcome,
  DeliveryFlag,
  MobileAppPlatformDelivery,
  MobileAppDelivery,
  ComparableVersion,
  AscAppVersionState,
  AscReleaseType,
  StoreVersionState,
  StoreVersionWriter,
  StoreVersionStateMapping,
  StoreVersionLaneCode,
  StoreVersionLane,
  StoreVersionStandingValue,
  StoreVersionUnknownReason,
  StoreVersionReleaseRequest,
  StoreVersionRollout,
  StoreVersionAction,
  StoreVersionBoard,
  StoreVersionStanding,
  StoreVersionThresholds,
  StoreVersionStandingInput,
  MobileAppStoreVersion,
  MobileAppPlatformStoreVersions,
  MobileAppStoreVersions,
  GitBranchResponse,
  GitReleaseRef,
  RegionMatrix,
  RegistryTier,
  VersionDeploymentStatusResponse,
  VersionDeploymentAppStatus,
  VersionDeploymentPlatformStatus
} from '../../schemas/entities/mobile-deployment'

export const DEFAULT_REGION_MATRIX = {
  ios: ['US', 'CA'],
  android: ['US', 'CA']
} as const
export const DEFAULT_REGISTRY_TIER = 'b' as const

// =============================================================================
// Per-store listing state (plan 2026-09-03-0330)
// =============================================================================

/**
 * Which store a state write is about. Deliberately the lowercase `'ios' | 'android'`
 * pair the rest of the mobile surface already speaks, not the `Platform` entity enum
 * — a caller naming the platform in a URL or a select should not have to know which
 * casing this particular endpoint chose.
 */
export const StoreStatePlatformSchema = z.enum(['ios', 'android'])
export type StoreStatePlatform = z.infer<typeof StoreStatePlatformSchema>

/**
 * The two casings, translated in ONE place each way.
 *
 * The wire speaks `'ios' | 'android'` and the `Platform` entity enum (and every
 * database row keyed on it) speaks `'IOS' | 'ANDROID'`, so a translation is
 * unavoidable — but it was being written out as an inline `=== 'ios' ? 'IOS' :
 * 'ANDROID'` ternary at each crossing, which is a conditional that cannot be
 * wrong today and silently picks `ANDROID` for anything added later.
 */
export const PLATFORM_BY_WIRE_NAME = {
  ios: 'IOS',
  android: 'ANDROID'
} as const satisfies Record<StoreStatePlatform, Platform>

export const WIRE_NAME_BY_PLATFORM = {
  IOS: 'ios',
  ANDROID: 'android'
} as const satisfies Record<Platform, StoreStatePlatform>

/**
 * POST /v1/mobile-apps/:id/store-state
 * POST /internal/mobile-apps/:id/store-state
 *
 * Sets ONE store's state. Two calls to move both stores, because they are two
 * independent facts and a combined payload would make "I only know the Apple
 * side" unexpressible without a null that means something different from "clear
 * it".
 *
 * `note` is nullish rather than optional-only so a caller can explicitly CLEAR a
 * stale note (`null`) as distinct from leaving it alone (absent). 500 characters
 * matches the column's documented cap.
 */
export const SetMobileAppStoreStateRequestSchema = z.object({
  platform: StoreStatePlatformSchema,
  state: StoreListingStateSchema,
  note: z.string().max(500, { message: 'Note cannot exceed 500 characters' }).nullish()
})
export type SetMobileAppStoreStateRequest = z.infer<typeof SetMobileAppStoreStateRequestSchema>

/**
 * The write's outcome.
 *
 * `changed` is false for a no-op — a resubmission of the state and note the row
 * already carries. The caller gets a success either way (the requested state IS
 * the state), but nothing is stamped and no event is recorded, so `changed`
 * is how a caller tells "I moved it" from "it was already there".
 */
export const SetMobileAppStoreStateResponseSchema = z.object({
  id: z.string(),
  platform: StoreStatePlatformSchema,
  changed: z.boolean(),
  /** The state BEFORE this call. Equal to `store.state` when `changed` is false. */
  previousState: StoreListingStateSchema,
  store: MobileAppStoreListingSchema
})
export type SetMobileAppStoreStateResponse = z.infer<typeof SetMobileAppStoreStateResponseSchema>

// =============================================================================
// Store-listing copy (school app design plan U8, KTD9)
// =============================================================================

/**
 * Why a store's `listing` is null, which are not the same situation.
 *
 * - `stored` — a row parsed, and `listing` carries it.
 * - `absent` — nothing has ever been saved for this store.
 * - `invalid` — a row exists but no longer parses (a hand edit past a cap, say).
 *   The school read collapses it to null so a school editor still loads; an
 *   operator needs to know the difference, because the row is still there.
 */
export const StoreListingRowStateSchema = z.enum(['stored', 'absent', 'invalid'])
export type StoreListingRowState = z.infer<typeof StoreListingRowStateSchema>

/**
 * How much of a store's listing is filled in: the school fields for that store
 * (`STORE_LISTING_READINESS_FIELDS`), seven for Apple and six for Play. A blank or
 * whitespace-only value counts as unfilled; `missing` names the rest in form order.
 */
export const StoreListingCompletenessSchema = z.object({
  filled: z.number().int().nonnegative(),
  total: z.number().int().positive(),
  missing: z.array(StoreListingFieldSchema)
})
export type StoreListingCompleteness = z.infer<typeof StoreListingCompletenessSchema>

export const StoreListingSummarySchema = z.object({
  state: StoreListingRowStateSchema,
  listing: StoreListingRecordSchema.nullable(),
  updatedAt: z.string().datetime().nullable(),
  completeness: StoreListingCompletenessSchema
})
export type StoreListingSummary = z.infer<typeof StoreListingSummarySchema>

/**
 * GET /v1/mobile-apps/:id/store-listing
 *
 * Super-admin read of both stores' `en-US` listing, every column, with per-store
 * completeness for the readiness panel. The super-admin write is
 * `PUT /v1/mobile-apps/:id/store-listing/:store` (below).
 */
export const GetMobileAppStoreListingsResponseSchema = z.object({
  mobileAppId: z.string(),
  locale: StoreListingLocaleSchema,
  apple: StoreListingSummarySchema,
  play: StoreListingSummarySchema
})
export type GetMobileAppStoreListingsResponse = z.infer<typeof GetMobileAppStoreListingsResponseSchema>

/**
 * `PUT /v1/mobile-apps/:id/store-listing/:store` — a super-admin's partial write of one store's
 * `en-US` listing. Only that store's `ADMIN_STORE_LISTING_FIELDS` are accepted; an omitted field
 * is left alone and `null` clears it.
 */
export const UpdateMobileAppStoreListingRequestSchema = StoreListingRecordSchema.partial().strict()
export type UpdateMobileAppStoreListingRequest = z.infer<typeof UpdateMobileAppStoreListingRequestSchema>

export const UpdateMobileAppStoreListingResponseSchema = GetMobileAppStoreListingsResponseSchema.extend({
  store: StoreListingStoreSchema,
  changedFields: z.array(StoreListingFieldSchema)
})
export type UpdateMobileAppStoreListingResponse = z.infer<typeof UpdateMobileAppStoreListingResponseSchema>

/**
 * `GET /v1/mobile-apps/:id/store-listing/defaults` — suggested copy for never-set fields, filled
 * from the fleet templates with this school's values. URLs appear only when they resolved.
 */
export const GetStoreListingDefaultsResponseSchema = z.object({
  mobileAppId: z.string(),
  apple: StoreListingRecordSchema.partial(),
  play: StoreListingRecordSchema.partial(),
  /** Why a default was left out (a URL that did not resolve, a school with no slug). */
  warnings: z.array(z.string())
})
export type GetStoreListingDefaultsResponse = z.infer<typeof GetStoreListingDefaultsResponseSchema>

/**
 * `POST /v1/mobile-apps/:id/store-listing/ai-suggestion` — a web-searching model (via OpenRouter)
 * researches the school and writes school-specific copy. Nothing is saved; every value is a
 * suggestion, and a value that failed the server's checks comes back null with a warning. When the
 * school's identity could not be confirmed, every copy field is null.
 */
export const StoreListingAiSuggestionResponseSchema = z.object({
  mobileAppId: z.string(),
  websiteUrl: z.string().nullable(),
  subtitle: z.string().nullable(),
  description: z.string().nullable(),
  shortDescription: z.string().nullable(),
  promotionalText: z.string().nullable(),
  keywords: z.string().nullable(),
  /** 0-100: how sure the model was that it researched this exact school. */
  identityConfidence: z.number().nullable(),
  sources: z.array(z.string()),
  notes: z.string().nullable(),
  warnings: z.array(z.string())
})
export type StoreListingAiSuggestionResponse = z.infer<typeof StoreListingAiSuggestionResponseSchema>

// =============================================================================
// Manual delivery-fact writes (Version Ledger, plan 2026-09-02-2140 U8)
// =============================================================================

/** Which of the three fact groups a manual write is about. */
export const DELIVERY_FACT_GROUPS = ['build', 'ota', 'live'] as const
export const DeliveryFactGroupSchema = z.enum(DELIVERY_FACT_GROUPS)
export type DeliveryFactGroup = z.infer<typeof DeliveryFactGroupSchema>

/**
 * Whichever of the three fact types a group's slot actually holds.
 *
 * The row types the three groups separately, so a reader that takes "a fact"
 * without saying which one had to widen to `Record<string, unknown>` and index
 * it by hand — which typechecks a misspelt column name as `undefined` and
 * renders it as "nothing published". This union keeps that reader honest: every
 * field it touches has to exist on some group.
 */
export type DeliveryGroupFact = DeliveryBuildFact | DeliveryOtaFact | DeliveryLiveFact

/**
 * Which value fields each group owns, and which one of them a manual write MUST
 * carry.
 *
 * Exported rather than kept private to the request schema because the same two
 * facts are needed in four places that must not drift: the Zod refinement, the
 * use-case's second validation (a service caller parses no body), the web
 * control that swaps its field set with the group, and the ledger's write rule
 * (`ledger/write-rule.ts`), which normalizes and compares exactly these fields.
 * The backend writer's COLUMN map is a different table — that one is about where
 * a value lands, this one is about which fields the group owns.
 */
export const DELIVERY_FACT_GROUP_FIELDS = {
  build: ['version', 'buildNumber', 'runtimeVersion', 'easBuildId'],
  ota: ['appVersion', 'runtimeVersion', 'updateGroupId', 'gitCommit'],
  live: ['version', 'buildNumber']
} as const satisfies Record<DeliveryFactGroup, readonly string[]>

/** The one field without which the group says nothing (KTD13: "primary version field required"). */
export const DELIVERY_FACT_GROUP_PRIMARY_FIELD = {
  build: 'version',
  ota: 'appVersion',
  live: 'version'
} as const satisfies Record<DeliveryFactGroup, string>

/**
 * Free text, capped at the column width. Deliberately NOT semver-validated:
 * two rows have no Expo project at all and a human correcting a version by hand
 * is the last writer standing — a regex that rejects `1.8.10-hotfix` would make
 * the true answer unrecordable.
 */
const DeliveryFactFieldSchema = z.string().trim().min(1).max(64)

/**
 * Every value field across the three groups, all optional. The group decides
 * which of them are meaningful; the refinement below rejects the rest, so a
 * caller cannot post an `easBuildId` against the `live` group and believe it
 * landed.
 */
export const MobileAppDeliveryFactValuesSchema = z.object({
  /** build, live */
  version: DeliveryFactFieldSchema.optional(),
  /** build, live */
  buildNumber: DeliveryFactFieldSchema.optional(),
  /** build, ota */
  runtimeVersion: DeliveryFactFieldSchema.optional(),
  /** build */
  easBuildId: DeliveryFactFieldSchema.optional(),
  /** ota */
  appVersion: DeliveryFactFieldSchema.optional(),
  /** ota */
  updateGroupId: DeliveryFactFieldSchema.optional(),
  /** ota */
  gitCommit: DeliveryFactFieldSchema.optional()
})
export type MobileAppDeliveryFactValues = z.infer<typeof MobileAppDeliveryFactValuesSchema>

/**
 * The namespace an internal-lane caller must set itself in.
 *
 * R10's whole point: an agent writing a version must be legible AS an agent. A
 * free-text `setBy` would let a script post `user_2abc...` and become
 * indistinguishable from the person whose id that is, in a table whose entire
 * job is saying who claimed what.
 */
export const DELIVERY_FACT_SET_BY_PATTERN = /^(agent|script):[a-z0-9][a-z0-9-]{0,47}$/

/**
 * POST /v1/mobile-apps/:id/delivery-fact
 * POST /internal/mobile-apps/:id/delivery-fact
 *
 * Set or clear ONE fact group on ONE platform by hand (R9, R10). Exactly one of
 * `fact` and `clear` is present: a write of nulls means "I did not observe
 * this", and only an explicit clear may erase.
 *
 * `setBy` is honoured on the internal lane ONLY, where it is the agent or script
 * label; the Clerk lane records the caller's own user id and ignores this field
 * rather than letting a browser session choose its own attribution.
 */
export const SetMobileAppDeliveryFactRequestSchema = z
  .object({
    platform: StoreStatePlatformSchema,
    group: DeliveryFactGroupSchema,
    fact: MobileAppDeliveryFactValuesSchema.optional(),
    clear: z.literal(true).optional(),
    /** `null` clears a stored note; absent leaves it alone. 500 chars matches the column. */
    note: z.string().max(500, { message: 'Note cannot exceed 500 characters' }).nullish(),
    /** Internal lane only, and only inside the `agent:` / `script:` namespace. */
    setBy: z.string().max(64).optional()
  })
  .superRefine((value, ctx) => {
    if ((value.fact === undefined) === (value.clear === undefined)) {
      ctx.addIssue({
        code: 'custom',
        path: ['fact'],
        message: 'Provide exactly one of `fact` or `clear`'
      })
      return
    }

    if (value.fact === undefined) return

    const allowed = DELIVERY_FACT_GROUP_FIELDS[value.group] as readonly string[]
    const primary = DELIVERY_FACT_GROUP_PRIMARY_FIELD[value.group]

    for (const key of Object.keys(value.fact)) {
      if (!allowed.includes(key)) {
        ctx.addIssue({
          code: 'custom',
          path: ['fact', key],
          message: `The \`${value.group}\` group has no field \`${key}\``
        })
      }
    }

    if ((value.fact as Record<string, unknown>)[primary] === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['fact', primary],
        message: `The \`${value.group}\` group requires \`${primary}\``
      })
    }
  })
export type SetMobileAppDeliveryFactRequest = z.infer<typeof SetMobileAppDeliveryFactRequestSchema>

/**
 * The write's outcome.
 *
 * `changed` is false for R11's no-op — the same fact and note resubmitted, which
 * stamps nothing and emits no event. `previous` is the group's values as they
 * stood BEFORE the call (`null` when nothing had ever written the group), and
 * `fact` is how they stand after it (`null` after a clear). Values only: the
 * provenance the row now carries comes back on the next read, and duplicating it
 * here would be a second place for it to be wrong.
 */
export const SetMobileAppDeliveryFactResponseSchema = z.object({
  id: z.string(),
  platform: StoreStatePlatformSchema,
  group: DeliveryFactGroupSchema,
  changed: z.boolean(),
  previous: MobileAppDeliveryFactValuesSchema.nullable(),
  fact: MobileAppDeliveryFactValuesSchema.nullable()
})
export type SetMobileAppDeliveryFactResponse = z.infer<typeof SetMobileAppDeliveryFactResponseSchema>

/**
 * POST /v1/mobile-apps/:id/delivery/reconcile — the "Verify now" action (R19).
 *
 * `already_queued` is a SUCCESS, not a refusal: a reconcile is already on its
 * way and `reconcileQueuedAt` is the original stamp, which is what lets the
 * button render its pending state across a reload instead of enqueueing a
 * second run each time someone clicks.
 */
/**
 * How long a queued single-app reconcile counts as outstanding, on BOTH sides
 * of the wire: the backend answers `already_queued` for a stamp younger than
 * this, and the web treats an older `reconcileQueuedAt` as stranded (a dropped
 * message) rather than pending, so a page never polls forever on it.
 */
export const RECONCILE_QUEUE_WINDOW_MS = 10 * 60 * 1000

/**
 * The lanes a reconcile can be asked for (KTD9).
 *
 * `asc` and `play` rather than the ledger's `appStoreConnect` / `play` because
 * this is what a caller types — into a button's prop, a scheduled job's body, or
 * a curl against the internal mirror. The two vocabularies are translated in the
 * use-case, once.
 *
 * WHO MAY ASK FOR WHAT IS NOT SYMMETRIC (R18): the public route accepts `expo`
 * and `asc`; `play` is internal-only, because a Play read opens an ephemeral
 * edit and spends the developer account's daily quota, and a row button that
 * anyone can hold down must not be able to exhaust it.
 */
export const RECONCILE_LANES = ['expo', 'asc', 'play'] as const
export const ReconcileLaneSchema = z.enum(RECONCILE_LANES)
export type ReconcileLane = z.infer<typeof ReconcileLaneSchema>

/**
 * The reconcile body. `lanes` is REQUIRED and non-empty on purpose: an absent
 * lane list used to mean "Expo only", which is the silent half-run AE6 is about
 * — a caller that means Expo says so.
 */
export const ReconcileMobileAppDeliveryRequestSchema = z.object({
  lanes: z.array(ReconcileLaneSchema).min(1)
})
export type ReconcileMobileAppDeliveryRequest = z.infer<typeof ReconcileMobileAppDeliveryRequestSchema>

export const ReconcileMobileAppDeliveryResponseSchema = z.object({
  id: z.string(),
  status: z.enum(['queued', 'already_queued']),
  reconcileQueuedAt: z.coerce.date(),
  /**
   * Which lanes this call actually put on the queue — never assumed to be the
   * lanes asked for. A lane inside its ten-minute window, and a Play lane whose
   * daily on-demand cap or quota marker is spent, are both dropped here while
   * the others go through, so `status: 'queued'` alone does not say what ran.
   */
  lanesQueued: z.array(ReconcileLaneSchema).optional()
})
export type ReconcileMobileAppDeliveryResponse = z.infer<typeof ReconcileMobileAppDeliveryResponseSchema>

/**
 * POST /v1/mobile-apps/release-recheck: the Release ready dialog's Recheck (SCH-3608).
 *
 * Per app it queues the `asc` reconcile (the board's Check now) and reads the
 * public Google Play store page right away, since only the page tells whether a
 * completed release was published from Publishing overview. The Play track itself
 * is read by the scheduled fleet sweep.
 */
export const ReleaseRecheckRequestSchema = z.object({
  // The dialog sends 8 per batch. 25 bounds one request to about 7 rounds of the server's 4-wide
  // pool, so it stays under gateway timeouts even when each Play page read is slow.
  mobileAppIds: z.array(z.string().min(1)).min(1).max(25)
})
export type ReleaseRecheckRequest = z.infer<typeof ReleaseRecheckRequestSchema>

/**
 * What the public Play store page said. `live` carries the versionName users are
 * served; `not_listed` is a 404 (no public listing); `unreadable` is a fetch or
 * parse failure and never means "not live"; `skipped` is an app with no Android
 * package.
 */
export const PlayStorePageReadSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('live'), versionString: z.string() }),
  z.object({ status: z.literal('not_listed') }),
  z.object({ status: z.literal('unreadable'), reason: z.string() }),
  z.object({ status: z.literal('skipped') })
])
export type PlayStorePageRead = z.infer<typeof PlayStorePageReadSchema>

export const ReleaseRecheckAppResultSchema = z.object({
  id: z.string(),
  /** Which reconcile lanes this call queued; a lane in its window or past its daily cap is dropped. */
  lanesQueued: z.array(ReconcileLaneSchema),
  /** Set when the reconcile could not be queued at all; the page read still ran. */
  reconcileError: z.string().nullable(),
  playPage: PlayStorePageReadSchema,
  /**
   * True when the page showed the version our release request is waiting to publish, and that was
   * recorded as published, so the app no longer offers Publish.
   */
  publishConfirmed: z.boolean()
})
export type ReleaseRecheckAppResult = z.infer<typeof ReleaseRecheckAppResultSchema>

export const ReleaseRecheckResponseSchema = z.object({
  results: z.array(ReleaseRecheckAppResultSchema)
})
export type ReleaseRecheckResponse = z.infer<typeof ReleaseRecheckResponseSchema>

/**
 * GET /v1/mobile-apps/releases
 */
export const ListAppReleasesRequestSchema = z.object({
  releaseType: AppReleaseTypeSchema.optional(),
  status: AppReleaseStatusSchema.optional(),
  versionId: z.string().optional(),
  mobileAppId: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).optional().default(20),
  offset: z.coerce.number().int().min(0).optional().default(0)
})
export type ListAppReleasesRequest = z.infer<typeof ListAppReleasesRequestSchema>

export const ListAppReleasesResponseSchema = z.object({
  data: z.array(AppReleaseResponseSchema),
  // `meta`, not a top-level `total`: the handler has always sent `{ message, data, meta }`,
  // and the flat `total` this replaces was never on the wire.
  meta: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
    /** Every `AppReleaseStatus` key, counted over the SAME `where` as `data` — zero-filled. */
    statusCounts: z.record(AppReleaseStatusSchema, z.number()).optional()
  }),
  message: z.string()
})
export type ListAppReleasesResponse = z.infer<typeof ListAppReleasesResponseSchema>

/**
 * POST /v1/mobile-apps/releases
 */
export const CreateAppReleaseRequestSchema = z.object({
  releaseType: AppReleaseTypeSchema,
  title: z.string().max(200).optional(),
  description: z.string().max(2000).optional(),
  versionNumber: z.string().optional(),
  gitCommitHash: z.string().optional(),
  gitBranch: z.string().optional(),
  targetMobileAppIds: z.array(z.string()).min(1),
  platforms: z.array(PlatformSchema).min(1)
})
export type CreateAppReleaseRequest = z.infer<typeof CreateAppReleaseRequestSchema>

export const CreateAppReleaseResponseSchema = z.object({
  data: AppReleaseResponseSchema,
  message: z.string()
})
export type CreateAppReleaseResponse = z.infer<typeof CreateAppReleaseResponseSchema>

/**
 * GET /v1/mobile-apps/releases/:releaseId
 */
export const GetAppReleaseRequestSchema = z.object({
  releaseId: z.string()
})
export type GetAppReleaseRequest = z.infer<typeof GetAppReleaseRequestSchema>

export const GetAppReleaseResponseSchema = z.object({
  data: AppReleaseResponseSchema,
  message: z.string()
})
export type GetAppReleaseResponse = z.infer<typeof GetAppReleaseResponseSchema>

/**
 * POST /v1/mobile-apps/releases/:releaseId/cancel
 */
export const CancelAppReleaseRequestSchema = z.object({
  releaseId: z.string()
})
export type CancelAppReleaseRequest = z.infer<typeof CancelAppReleaseRequestSchema>

export const CancelAppReleaseResponseSchema = z.object({
  data: AppReleaseResponseSchema,
  message: z.string()
})
export type CancelAppReleaseResponse = z.infer<typeof CancelAppReleaseResponseSchema>

/**
 * GET /v1/mobile-apps/releases/:releaseId/events
 */
export const ListAppReleaseEventsRequestSchema = z.object({
  releaseId: z.string(),
  limit: z.coerce.number().int().min(1).max(100).optional().default(50),
  offset: z.coerce.number().int().min(0).optional().default(0)
})
export type ListAppReleaseEventsRequest = z.infer<typeof ListAppReleaseEventsRequestSchema>

export const ListAppReleaseEventsResponseSchema = z.object({
  data: z.array(AppReleaseEventResponseSchema),
  total: z.number(),
  message: z.string()
})
export type ListAppReleaseEventsResponse = z.infer<typeof ListAppReleaseEventsResponseSchema>

/**
 * GET /v1/mobile-apps/versions/:versionId/deployment-status
 */
export const GetVersionDeploymentStatusRequestSchema = z.object({
  versionId: z.string()
})
export type GetVersionDeploymentStatusRequest = z.infer<typeof GetVersionDeploymentStatusRequestSchema>

export const GetVersionDeploymentStatusResponseSchema = z.object({
  data: VersionDeploymentStatusResponseSchema,
  message: z.string()
})
export type GetVersionDeploymentStatusResponse = z.infer<typeof GetVersionDeploymentStatusResponseSchema>

/**
 * GET /v1/mobile-apps/:id/artifact-history
 */
export const GetAppArtifactHistoryRequestSchema = z.object({
  mobileAppId: z.string(),
  limit: z.coerce.number().int().min(1).max(100).optional().default(50)
})
export type GetAppArtifactHistoryRequest = z.infer<typeof GetAppArtifactHistoryRequestSchema>

export const AppArtifactHistoryEventSchema = z.object({
  id: z.string(),
  kind: AppArtifactKindSchema,
  provider: z.string(),
  providerId: z.string().optional(),
  platform: PlatformSchema,
  appVersion: z.string().optional(),
  dispatchId: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
})
export type AppArtifactHistoryEvent = z.infer<typeof AppArtifactHistoryEventSchema>

export const AppArtifactHistorySummarySchema = z.object({
  total: z.number(),
  byKind: z.record(z.string(), z.number())
})
export type AppArtifactHistorySummary = z.infer<typeof AppArtifactHistorySummarySchema>

export const GetAppArtifactHistoryResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    events: z.array(AppArtifactHistoryEventSchema),
    summary: AppArtifactHistorySummarySchema
  })
})
export type GetAppArtifactHistoryResponse = z.infer<typeof GetAppArtifactHistoryResponseSchema>

/**
 * GET /v1/mobile-apps/releases/attention
 */
export const AttentionPublishingAccountSchema = z.object({
  id: z.string(),
  vendor: z.string(),
  accountId: z.string(),
  displayName: z.string().optional()
})
export type AttentionPublishingAccount = z.infer<typeof AttentionPublishingAccountSchema>

/**
 * WHY the row is on the list, which `status` alone cannot say. The screen
 * branches on this field, never on `status`.
 *
 * - `BLOCKED` / `REFUSED` — a release target a human owns, unchanged.
 * - `WAITING_ON_US` — Apple approved a version and is holding it for a release
 *   nobody has requested yet. Since SCH-1812 (R16, KTD11) this comes from the
 *   store-version row, not from a DELIVERED target narrowed against the
 *   operations-event timeline, so a target stuck in BLOCKED or FAILED can no
 *   longer hide an approved version.
 * - `STATE_UNKNOWN` — **no longer produced.** It existed only to report a
 *   DELIVERED target whose timeline said nothing, and that arm is gone with the
 *   timeline lookup. The member is kept so a client that still branches on it
 *   keeps parsing; nothing emits it, and a reader may treat it as unreachable.
 */
export const AttentionReasonSchema = z.enum(['BLOCKED', 'REFUSED', 'WAITING_ON_US', 'STATE_UNKNOWN'])
export type AttentionReason = z.infer<typeof AttentionReasonSchema>

/** The store-version row behind a `WAITING_ON_US` attention row (KTD11). */
export const AttentionStoreVersionSchema = z.object({
  /** `MobileAppStoreVersion.publicId`. */
  id: z.string(),
  /** The App Store Connect version id the release action must confirm. */
  storeVersionKey: z.string(),
  versionString: z.string().optional(),
  /** Apple's own word. `PENDING_DEVELOPER_RELEASE` is the only one that lands here. */
  vendorState: z.string(),
  observedAt: z.coerce.date()
})
export type AttentionStoreVersion = z.infer<typeof AttentionStoreVersionSchema>

/**
 * One row on the attention list. TWO shapes share it (KTD11):
 *
 *   - a release TARGET that is blocked or refused, which carries `status`,
 *     `attemptCount` and its `release`; and
 *   - a STORE VERSION Apple is holding for us, which carries `storeVersion` and
 *     none of those — because R16 reads the store-version record rather than
 *     release targets, so a BLOCKED or FAILED target can no longer hide an
 *     approved version that is sitting there waiting on a click.
 *
 * Hence the optional target fields. A renderer branches on `attentionReason`,
 * never on the presence of a field.
 */
export const AttentionTargetSchema = z.object({
  /** The row's identity: the release target's id, or the store-version row's id. */
  id: z.string(),
  platform: PlatformSchema,
  /** Absent on a store-version row, which has no release target behind it. */
  status: z.enum(['BLOCKED', 'REFUSED', 'DELIVERED']).optional(),
  attentionReason: AttentionReasonSchema,
  failureClass: z.string().optional(),
  failureReason: z.string().optional(),
  /** Absent on a store-version row. */
  attemptCount: z.number().optional(),
  mobileApp: z.object({ id: z.string(), name: z.string().optional() }),
  /** Absent on a store-version row. */
  release: z.object({ id: z.string(), title: z.string().optional(), releaseType: AppReleaseTypeSchema }).optional(),
  /** Present only on a `WAITING_ON_US` row. */
  storeVersion: AttentionStoreVersionSchema.optional(),
  publishingAccount: AttentionPublishingAccountSchema.optional(),
  updatedAt: z.coerce.date()
})
export type AttentionTarget = z.infer<typeof AttentionTargetSchema>

export const ListAttentionTargetsResponseSchema = z.object({
  message: z.string(),
  data: z.array(AttentionTargetSchema),
  total: z.number()
})
export type ListAttentionTargetsResponse = z.infer<typeof ListAttentionTargetsResponseSchema>

/**
 * GET /v1/mobile-apps/artifacts/:artifactId/adoption
 */
export const ArtifactAdoptionRequestSchema = z.object({
  artifactId: z.string(),
  days: z.coerce.number().int().min(1).max(30).optional().default(14)
})
export type ArtifactAdoptionRequest = z.infer<typeof ArtifactAdoptionRequestSchema>

export const ArtifactAdoptionMemberSchema = z.object({
  updateId: z.string(),
  platform: z.string(),
  runtimeVersion: z.string(),
  sessions: z.number()
})
export type ArtifactAdoptionMember = z.infer<typeof ArtifactAdoptionMemberSchema>

export const ArtifactAdoptionResponseSchema = z.object({
  artifactId: z.string(),
  updateGroupId: z.string(),
  targetStatus: z.string().nullable(),
  service: z.string(),
  windowDays: z.number(),
  totalSessions: z.number(),
  members: z.array(ArtifactAdoptionMemberSchema)
})
export type ArtifactAdoptionResponse = z.infer<typeof ArtifactAdoptionResponseSchema>

// ==========================================
// Mobile Apps CRUD
// ==========================================

/**
 * GET /v1/mobile-apps
 */
export const ListMobileAppsRequestSchema = z.object({
  isActive: z.preprocess(v => v === 'true' || v === true, z.boolean()).optional(),
  /**
   * THE ROLLOUT FILTER (R9): return only apps that are not on this version, plus
   * the apps whose standing against it is unknown.
   *
   * Unknown rows are INCLUDED rather than filtered out, and that is the point of
   * the filter: an app nobody could read is exactly the app an operator chasing
   * a rollout needs to see, and dropping it is how a fleet reads as finished
   * while a school sits six weeks behind.
   *
   * Setting it makes the read a fleet-wide one (U8): the standing is derived in
   * memory, so the page can only be sliced after every app has been judged.
   */
  notOnVersion: z.string().min(1).optional(),
  /**
   * Which store the filter and the count are about. A standing is per app AND
   * platform — an app can be current on iOS and six weeks behind on Android —
   * so a filter without a platform would be counting two different questions
   * together.
   */
  platform: StoreStatePlatformSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).optional().default(50),
  offset: z.coerce.number().int().min(0).optional().default(0)
})
export type ListMobileAppsRequest = z.infer<typeof ListMobileAppsRequestSchema>

// =============================================================================
// Store-version rollout summary (SCH-1812, R10)
// =============================================================================

/**
 * `GET /v1/mobile-apps/store-versions/summary?version=1.9.0[&platform=ios]`, and
 * its `/internal` mirror.
 *
 * `version` is required: R9 says no count is active until a target is chosen,
 * and a summary that defaulted to some version would produce a number nobody
 * asked for and everybody would quote.
 */
export const StoreVersionSummaryRequestSchema = z.object({
  version: z.string().min(1),
  /** Omitted ⇒ one block per platform, rather than the two silently added together. */
  platform: StoreStatePlatformSchema.optional()
})
export type StoreVersionSummaryRequest = z.infer<typeof StoreVersionSummaryRequestSchema>

/**
 * The three buckets for one platform (R10, KTD8).
 *
 * `unknown` is its OWN bucket and never folded into either of the others. The
 * denominator is every active, non-fixture, non-deleted app whose listing state
 * is not excluded, so an app with no identifiers stays in it — counted as
 * unknown, not quietly dropped to make the other two numbers add up.
 */
export const StoreVersionStandingCountsSchema = z.object({
  on: z.number().int().min(0),
  notOn: z.number().int().min(0),
  unknown: z.number().int().min(0)
})
export type StoreVersionStandingCounts = z.infer<typeof StoreVersionStandingCountsSchema>

export const StoreVersionSummarySchema = z.object({
  /** Echoed back, so a stored or shared count can never be read against the wrong target. */
  version: z.string(),
  /** The platform asked for, when one was. Absent ⇒ both blocks below are populated. */
  platform: StoreStatePlatformSchema.optional(),
  ios: StoreVersionStandingCountsSchema.optional(),
  android: StoreVersionStandingCountsSchema.optional()
})
export type StoreVersionSummary = z.infer<typeof StoreVersionSummarySchema>

export const StoreVersionSummaryResponseSchema = z.object({
  data: StoreVersionSummarySchema,
  message: z.string()
})
export type StoreVersionSummaryResponse = z.infer<typeof StoreVersionSummaryResponseSchema>

export const ListMobileAppsResponseSchema = z.object({
  data: z.array(MobileAppResponseSchema),
  total: z.number(),
  message: z.string()
})
export type ListMobileAppsResponse = z.infer<typeof ListMobileAppsResponseSchema>

/**
 * POST /v1/mobile-apps
 */
export const CreateMobileAppRequestSchema = z.object({
  name: placeholderRejected('Name').max(200),
  schoolId: z.string().min(1),
  // App Store / Play listing title, not the on-device label — both vendors
  // cap it at 30 characters.
  displayName: optionalPlaceholderRejected(
    'Display name',
    30,
    'Store listing name cannot exceed 30 characters (App Store / Play limit)'
  ),
  description: z.string().max(2000).optional(),
  kind: z.string().optional(),
  bundleId: optionalPlaceholderRejected('Bundle ID'),
  expoProjectId: optionalPlaceholderRejected('Expo project ID'),
  expoSlug: optionalPlaceholderRejected('Expo slug'),
  androidPackage: optionalPlaceholderRejected('Android package'),
  splashBackgroundColor: z.string().optional().or(z.literal('')),
  splashResizeMode: z.string().optional().or(z.literal('')),
  // Optional. Unset ⇒ the build falls back to `splashBackgroundColor`, which is
  // what every app did before this field existed.
  adaptiveIconBackgroundColor: z.string().optional().or(z.literal('')),
  schoolAppId: optionalPlaceholderRejected('School app ID'),
  configKey: optionalPlaceholderRejected('Config key'),
  buildProfiles: BuildProfilesSchema.optional(),
  regionMatrix: RegionMatrixSchema.optional(),
  tier: RegistryTierSchema.optional(),
  appleTeamId: optionalPlaceholderRejected('Apple team ID'),
  ascAppId: optionalPlaceholderRejected('App Store Connect app ID'),
  playAccountId: optionalPlaceholderRejected('Google Play account ID'),
  googleProjectId: optionalPlaceholderRejected('Google project ID'),
  oneSignalAppId: optionalPlaceholderRejected('OneSignal app ID'),
  oneSignalApiKey: z.string().optional(),
  appStoreUrl: z.string().url().optional().or(z.literal('')),
  playStoreUrl: z.string().url().optional().or(z.literal(''))
})
export type CreateMobileAppRequest = z.infer<typeof CreateMobileAppRequestSchema>

export const CreateMobileAppResponseSchema = z.object({
  data: MobileAppResponseSchema,
  message: z.string()
})
export type CreateMobileAppResponse = z.infer<typeof CreateMobileAppResponseSchema>

/**
 * GET /v1/mobile-apps/:id
 */
export const GetMobileAppRequestSchema = z.object({
  id: z.string()
})
export type GetMobileAppRequest = z.infer<typeof GetMobileAppRequestSchema>

export const GetMobileAppResponseSchema = z.object({
  data: MobileAppResponseSchema,
  message: z.string()
})
export type GetMobileAppResponse = z.infer<typeof GetMobileAppResponseSchema>

/**
 * GET /v1/mobile-apps/:id/events — the per-app activity history (plan
 * 2026-08-15-001 U17, requirements R14/R15/R20).
 *
 * Cursor-paginated, not offset-paginated, unlike the release-events read above:
 * this stream is append-only and read newest-first while it is still being
 * written to, so an offset silently repeats a row the moment an event lands
 * between two page fetches.
 */
export const ListMobileAppEventsRequestSchema = z.object({
  /** MobileApp public id. */
  id: z.string(),
  eventClass: MobileAppEventClassSchema.optional(),
  /** Inclusive lower bound on `occurredAt`, ISO-8601. */
  since: z.string().datetime().optional(),
  /** Inclusive upper bound on `occurredAt`, ISO-8601. */
  until: z.string().datetime().optional(),
  limit: z.coerce.number().int().min(1).max(MOBILE_APP_EVENT_PAGE_SIZE_MAX).optional(),
  /** Public id of the last event on the previous page. */
  cursor: z.string().optional()
})
export type ListMobileAppEventsRequest = z.infer<typeof ListMobileAppEventsRequestSchema>

export const ListMobileAppEventsResponseSchema = z.object({
  data: z.array(MobileAppEventSchema),
  /** Null when the stream is exhausted. */
  nextCursor: z.string().nullable(),
  /**
   * When per-app event recording started, fleet-wide, ISO-8601 — or null if
   * nothing has ever been recorded.
   *
   * The UI renders this as an explicit "recording began" marker at the foot of
   * the timeline. Apps have real builds and config edits that pre-date the
   * recorder, and without this the page reads as though nothing ever happened
   * to them. Absence before this date means unrecorded, not uneventful.
   */
  recordingBeganAt: z.string().datetime().nullable(),
  message: z.string()
})
export type ListMobileAppEventsResponse = z.infer<typeof ListMobileAppEventsResponseSchema>

/**
 * PATCH /v1/mobile-apps/:id
 */
// `maxLength`/`maxLengthMessage` are optional — same rationale as
// `optionalPlaceholderRejected` above.
const nullableNonPlaceholder = (label: string, maxLength?: number, maxLengthMessage?: string) => {
  const base = z.string().refine(v => v === '' || !PLACEHOLDER_PATTERN.test(v), {
    message: `${label} cannot be a placeholder (TODO/TBD/FIXME/XXX)`
  })
  const bounded =
    maxLength === undefined
      ? base
      : base.max(maxLength, { message: maxLengthMessage ?? `${label} cannot exceed ${maxLength} characters` })
  return bounded.optional().nullable().or(z.literal(''))
}

export const UpdateMobileAppRequestSchema = z.object({
  name: placeholderRejected('Name').max(200).optional(),
  schoolId: z.string().min(1).optional(),
  // App Store / Play listing title, not the on-device label — both vendors
  // cap it at 30 characters.
  displayName: nullableNonPlaceholder(
    'Display name',
    30,
    'Store listing name cannot exceed 30 characters (App Store / Play limit)'
  ),
  description: z.string().max(2000).optional().nullable(),
  kind: z.string().optional(),
  bundleId: nullableNonPlaceholder('Bundle ID'),
  expoProjectId: nullableNonPlaceholder('Expo project ID'),
  expoSlug: nullableNonPlaceholder('Expo slug'),
  androidPackage: nullableNonPlaceholder('Android package'),
  splashBackgroundColor: z.string().optional().nullable().or(z.literal('')),
  splashResizeMode: z.string().optional().nullable().or(z.literal('')),
  adaptiveIconBackgroundColor: z.string().optional().nullable().or(z.literal('')),
  schoolAppId: nullableNonPlaceholder('School app ID'),
  configKey: nullableNonPlaceholder('Config key'),
  buildProfiles: BuildProfilesSchema.optional().nullable(),
  regionMatrix: RegionMatrixSchema.optional().nullable(),
  tier: RegistryTierSchema.optional().nullable(),
  appleTeamId: nullableNonPlaceholder('Apple team ID'),
  ascAppId: nullableNonPlaceholder('App Store Connect app ID'),
  playAccountId: nullableNonPlaceholder('Google Play account ID'),
  googleProjectId: nullableNonPlaceholder('Google project ID'),
  oneSignalAppId: nullableNonPlaceholder('OneSignal app ID'),
  oneSignalApiKey: z.string().optional().nullable(),
  appStoreUrl: z.string().url().optional().nullable().or(z.literal('')),
  playStoreUrl: z.string().url().optional().nullable().or(z.literal('')),
  isActive: z.boolean().optional()
})
export type UpdateMobileAppRequest = z.infer<typeof UpdateMobileAppRequestSchema>

export const UpdateMobileAppResponseSchema = z.object({
  data: MobileAppResponseSchema,
  message: z.string()
})
export type UpdateMobileAppResponse = z.infer<typeof UpdateMobileAppResponseSchema>

// ==========================================
// Release Lines (the `/versions` routes)
// ==========================================
//
// A "version" on these routes is a RELEASE LINE (1.8, 1.9), one per mobile
// runtime, never a marketing version (R4). Entity: `schemas/entities/release-line.ts`.

/**
 * GET /v1/mobile-apps/versions — every release line, newest runtime first.
 */
export const ListReleaseLinesResponseSchema = z.object({
  data: z.array(ReleaseLineSchema),
  message: z.string()
})
export type ListReleaseLinesResponse = z.infer<typeof ListReleaseLinesResponseSchema>

const ReleaseLineGitRefSchema = z.string().trim().min(1).max(255)
const ReleaseLineWhatsNewSchema = z.string().max(4000)
const ReleaseLineNameSchema = z.string().trim().min(1).max(32)

/**
 * POST /v1/mobile-apps/versions — super-admin line creation. A runtime that
 * already has a line is a 409 and writes nothing.
 */
export const CreateReleaseLineRequestSchema = z.object({
  runtimeVersion: ReleaseLineRuntimeVersionSchema,
  name: ReleaseLineNameSchema.optional(),
  gitRef: ReleaseLineGitRefSchema.optional(),
  lifecycle: ReleaseLineLifecycleSchema.optional(),
  /** `true` makes the new line THE default and clears the previous one in the same write. */
  isDefault: z.boolean().optional(),
  whatsNew: ReleaseLineWhatsNewSchema.optional(),
  screenshotDeviceClasses: z.array(StoreScreenshotDeviceClassSchema).optional()
})
export type CreateReleaseLineRequest = z.infer<typeof CreateReleaseLineRequestSchema>

/**
 * PATCH /v1/mobile-apps/versions/:versionId — edit a line.
 *
 * `runtimeVersion` is the line's identity and cannot change. `isDefault` accepts
 * only `true`: exactly one line is the default (R3), so the way to move it is to
 * make another line the default, never to clear it.
 */
/**
 * A draft of a line's fleet-wide "What's new", built from the Linear Mobile releases of that line
 * (`Mobile <line>.x`): one bullet per feature or improvement issue that is not labelled `internal`,
 * newest release first, duplicates dropped. A starting point for a person to edit, never saved by
 * itself. `releases` names the Linear releases read; empty when Linear has none for the line.
 */
export const ReleaseLineWhatsNewDraftSchema = z.object({
  text: z.string(),
  releases: z.array(z.object({ name: z.string(), url: z.string().nullable() })),
  items: z.array(z.object({ identifier: z.string(), title: z.string(), url: z.string() }))
})
export type ReleaseLineWhatsNewDraft = z.infer<typeof ReleaseLineWhatsNewDraftSchema>

export const ReleaseLineWhatsNewDraftResponseSchema = z.object({
  data: ReleaseLineWhatsNewDraftSchema,
  message: z.string()
})
export type ReleaseLineWhatsNewDraftResponse = z.infer<typeof ReleaseLineWhatsNewDraftResponseSchema>

export const UpdateReleaseLineRequestSchema = z
  .object({
    name: ReleaseLineNameSchema.optional(),
    gitRef: ReleaseLineGitRefSchema.nullable().optional(),
    lifecycle: ReleaseLineLifecycleSchema.optional(),
    isDefault: z.literal(true).optional(),
    whatsNew: ReleaseLineWhatsNewSchema.nullable().optional(),
    screenshotDeviceClasses: z.array(StoreScreenshotDeviceClassSchema).nullable().optional()
  })
  .refine(body => Object.values(body).some(value => value !== undefined), {
    message: 'Provide at least one field to update'
  })
export type UpdateReleaseLineRequest = z.infer<typeof UpdateReleaseLineRequestSchema>

/** What one seed run did, per line. `unchanged` is what a second run reports. */
export const SeedReleaseLinesResultSchema = z.object({
  created: z.array(z.string()),
  unchanged: z.array(z.string()),
  /** Legacy pre-line rows (no runtime, e.g. "1.0.0") this run marked retired. */
  retiredLegacy: z.array(z.string()),
  /** Runtimes the fleet showed that the seed does not own (1.8 or newer, not a fixed line). */
  skipped: z.array(z.object({ runtimeVersion: z.string(), reason: z.string() })),
  /** The default line's runtime after the run. */
  defaultRuntimeVersion: z.string().nullable(),
  /**
   * App × line records this run planned, per non-retired line (U3). Zero on a
   * re-run: existing records are never touched. The current line's new records
   * carry a baseline snapshot of the app's listing.
   */
  plannedAppRecords: z.array(z.object({ runtimeVersion: z.string(), created: z.number().int().min(0) })),
  /**
   * The screenshot sets (U12). `keyedRows` are pre-line rows this run placed on
   * the line their version's `major.minor` names; `unkeyedRows` are the ones
   * left off every line (no line matches, or a newer set already holds that
   * line), which reads and pushes ignore. `copiedForward` is, per non-retired
   * line, the apps whose set was copied from their previous line. All zero on
   * a re-run.
   */
  screenshots: z.object({
    keyedRows: z.number().int().min(0),
    unkeyedRows: z.number().int().min(0),
    copiedForward: z.array(
      z.object({ runtimeVersion: z.string(), apps: z.number().int().min(0), rows: z.number().int().min(0) })
    )
  }),
  /**
   * The compliance sign-offs (U14) this run carried forward, per non-retired
   * line, from the previous line. Zero on a re-run.
   */
  complianceCarried: z.array(z.object({ runtimeVersion: z.string(), carried: z.number().int().min(0) }))
})
export type SeedReleaseLinesResult = z.infer<typeof SeedReleaseLinesResultSchema>

/** POST and PATCH /v1/mobile-apps/versions — the line as written. */
export const ReleaseLineResponseSchema = z.object({
  data: ReleaseLineSchema,
  message: z.string()
})
export type ReleaseLineResponse = z.infer<typeof ReleaseLineResponseSchema>

// ==========================================
// Compliance sign-offs on a line (U14, R2, R23, KTD11)
// ==========================================
//
// Super-admin only. Entity and the shared derivation:
// `schemas/entities/release-line.ts` (`deriveLineCompliance`).

/** GET /v1/mobile-apps/versions/:versionId/compliance — every field on both platforms with its standing. */
export const LineComplianceResponseSchema = z.object({
  data: z.object({
    lineId: z.string(),
    fields: z.array(LineComplianceFieldSchema)
  }),
  message: z.string()
})
export type LineComplianceResponse = z.infer<typeof LineComplianceResponseSchema>

/**
 * POST /v1/mobile-apps/versions/:versionId/compliance — sign off one field on
 * one platform. Signing a field that already has a row replaces it (a revoked
 * or carried row becomes a fresh sign-off on this line).
 */
export const SignOffComplianceRequestSchema = z
  .object({
    platform: PlatformSchema,
    field: ComplianceFieldSchema,
    lane: ComplianceLaneSchema,
    note: z.string().trim().max(1000).nullable().optional()
  })
  .refine(body => isComplianceFieldForPlatform(body.platform, body.field), {
    message: 'That compliance field does not belong to that platform',
    path: ['field']
  })
export type SignOffComplianceRequest = z.infer<typeof SignOffComplianceRequestSchema>

/** The sign-off as written, for the sign-off and revoke routes. */
export const ComplianceSignoffResponseSchema = z.object({
  data: ComplianceSignoffSchema,
  message: z.string()
})
export type ComplianceSignoffResponse = z.infer<typeof ComplianceSignoffResponseSchema>

/**
 * POST /internal/mobile-apps/release-lines/seed — idempotent.
 */
export const SeedReleaseLinesResponseSchema = z.object({
  data: SeedReleaseLinesResultSchema,
  message: z.string()
})
export type SeedReleaseLinesResponse = z.infer<typeof SeedReleaseLinesResponseSchema>

/**
 * What one backfill run copied from `AppArtifact.payload` into the build-identity
 * columns (`appVersion`, `buildNumber`, `runtimeVersion`). A second run reports
 * `updated: 0`: it fills only columns that are still null.
 */
export const BackfillArtifactIdentityResultSchema = z.object({
  /** BUILD and OTA_UPDATE artifacts with at least one identity column still null. */
  scanned: z.number().int().nonnegative(),
  /** Of those, how many had a payload value to copy. */
  updated: z.number().int().nonnegative()
})
export type BackfillArtifactIdentityResult = z.infer<typeof BackfillArtifactIdentityResultSchema>

/**
 * POST /internal/mobile-apps/release-lines/backfill-artifact-identity — idempotent.
 */
export const BackfillArtifactIdentityResponseSchema = z.object({
  data: BackfillArtifactIdentityResultSchema,
  message: z.string()
})
export type BackfillArtifactIdentityResponse = z.infer<typeof BackfillArtifactIdentityResponseSchema>

// ==========================================
// Release line summary and each app's lines (U4, R6, R9, R26)
// ==========================================
//
// Every count here comes out of the ONE app × line derivation in
// `schemas/entities/release-line.ts` (`deriveAppLineState`,
// `deriveCurrentAndUpcoming`), run on the server over the whole fleet in
// memory. The board, the per-app page and `pnpm sj release-lines` read these
// shapes and derive nothing themselves.

const AppLineCountSchema = z.number().int().nonnegative()

/**
 * Apps per marketing version on one line and platform: the canary view. Staff
 * ship 1.8.25 to a few schools before the rest of 1.8, so "live on 1.8" alone
 * hides the split this answers ("1.8.25: 3 apps, 1.8.23: 15 apps").
 */
export const ReleaseLineVersionCountSchema = z.object({
  /** Null when the store row carried no version string. */
  versionString: z.string().nullable(),
  appCount: AppLineCountSchema
})
export type ReleaseLineVersionCount = z.infer<typeof ReleaseLineVersionCountSchema>

/** The same split for versions still in flight, with the store state each app's version is in. */
export const ReleaseLineInFlightVersionCountSchema = ReleaseLineVersionCountSchema.extend({
  byStoreState: z.partialRecord(StoreVersionStateSchema, AppLineCountSchema)
})
export type ReleaseLineInFlightVersionCount = z.infer<typeof ReleaseLineInFlightVersionCountSchema>

/**
 * One line on one platform, over every in-scope app (R26).
 *
 * `states` + `untracked` = `appCount`, always. `untracked` is an app with no
 * record for the line and nothing observed on it (the derivation's
 * `unknown` / `no_record`): on a retired line that is most of the fleet, and
 * folding it into `unknown` would bury the apps whose store could not be read.
 * Every state key is present, zero included, so a reader never mistakes a
 * missing key for zero.
 *
 * U7 and U9 add optional draft and release-request counts to this object; the
 * derivation's buckets above do not change when they do.
 *
 * `appsWithDrafts` (U7): in-scope apps counted on this line for this platform
 * (their current line, else upcoming, else the default) that have any school
 * draft not yet released. One bulk read of the draft table, NOT the school
 * field status per app: a store-text draft reverted to the shipped value
 * still counts until released. Absent when the drafts were not read.
 *
 * `appsWithReleaseRequests` (U9): of those, the apps whose school asked for a
 * release (a pending draft row carrying a request date). Same bulk read, same
 * line, absent under the same condition.
 */
export const ReleaseLinePlatformSummarySchema = z.object({
  appCount: AppLineCountSchema,
  states: z.record(AppLineStateSchema, AppLineCountSchema),
  untracked: AppLineCountSchema,
  /** Why the `unknown` apps are unknown, so "3 unknown" can say "2 stale, 1 unreadable". */
  unknownReasons: z.partialRecord(AppLineStateReasonSchema, AppLineCountSchema),
  /** Apps live on this line, by the version the store serves. Newest version first. */
  liveVersions: z.array(ReleaseLineVersionCountSchema),
  /** Apps with a version in flight on this line (whatever the line's state), by that version. */
  inFlightVersions: z.array(ReleaseLineInFlightVersionCountSchema),
  appsWithDrafts: AppLineCountSchema.optional(),
  appsWithReleaseRequests: AppLineCountSchema.optional()
})
export type ReleaseLinePlatformSummary = z.infer<typeof ReleaseLinePlatformSummarySchema>

export const ReleaseLineSummaryLineSchema = z.object({
  line: ReleaseLineSchema,
  ios: ReleaseLinePlatformSummarySchema,
  android: ReleaseLinePlatformSummarySchema
})
export type ReleaseLineSummaryLine = z.infer<typeof ReleaseLineSummaryLineSchema>

/** One app's lines on both stores. A null platform is out of the store lane's scope for the app. */
export const AppReleaseLinesSchema = z.object({
  /** `MobileApp.publicId`. */
  id: z.string(),
  name: z.string().nullable(),
  displayName: z.string().nullable(),
  schoolName: z.string().nullable(),
  ios: AppPlatformLinesSchema.nullable(),
  android: AppPlatformLinesSchema.nullable(),
  /**
   * U7: the app has a school draft not yet released (any unapplied draft row).
   * Absent when the drafts were not read (the per-app lines view) or could not be.
   */
  hasPendingDrafts: z.boolean().optional(),
  /** U9: the school asked for a release of a pending draft. Absent exactly when `hasPendingDrafts` is. */
  hasPendingReleaseRequest: z.boolean().optional()
})
export type AppReleaseLines = z.infer<typeof AppReleaseLinesSchema>

/** An app held back on a line, with the reason staff recorded (R5). */
export const BlockedAppLineSchema = z.object({
  appId: z.string(),
  appName: z.string(),
  lineId: z.string(),
  lineName: z.string(),
  platform: PlatformSchema,
  reason: z.string()
})
export type BlockedAppLine = z.infer<typeof BlockedAppLineSchema>

/**
 * The fleet has moved past its default line on one store (SCH-3782): another
 * non-retired line has more apps live than the default. The default is what a
 * new app is planned onto and what a release falls back to, and nothing moves
 * it but a person, so a default nobody moved is read here rather than found
 * later.
 */
export const DefaultLineBehindFindingSchema = z.object({
  kind: z.literal('default_line_behind'),
  platform: z.enum(['ios', 'android']),
  defaultLine: z.object({ id: z.string(), name: z.string(), live: z.number().int() }),
  aheadLine: z.object({ id: z.string(), name: z.string(), live: z.number().int() })
})
export type DefaultLineBehindFinding = z.infer<typeof DefaultLineBehindFindingSchema>

type SummaryLineCounts = Pick<ReleaseLineSummaryLine, 'line' | 'ios' | 'android'>

/** The non-retired line newer than the default with the most live apps on one store, above the default's. */
function lineAheadOfDefault(
  lines: readonly SummaryLineCounts[],
  defaultEntry: SummaryLineCounts,
  platform: 'ios' | 'android'
): SummaryLineCounts | undefined {
  const newer = lines.filter(
    entry =>
      !entry.line.isDefault &&
      entry.line.lifecycle !== 'retired' &&
      compareLineRuntimes(entry.line.runtimeVersion, defaultEntry.line.runtimeVersion) > 0
  )
  let ahead: SummaryLineCounts | undefined
  for (const entry of newer) {
    const bar = ahead ? ahead[platform].states.live : defaultEntry[platform].states.live
    if (entry[platform].states.live > bar) ahead = entry
  }
  return ahead
}

/**
 * Per store, the non-retired line NEWER than the default with the most live
 * apps, when it has more live apps than the default. An older line never counts:
 * while a freshly promoted default is still rolling out, the line it replaced
 * holds more apps, and that is not a reason to move the default back. No
 * default line, or a tie, is no finding. Pure, over the summary's own line counts, so the board and
 * `pnpm sj release-lines` read the backend's one answer.
 */
export function defaultLineBehindFindings(lines: readonly SummaryLineCounts[]): DefaultLineBehindFinding[] {
  const defaultEntry = lines.find(entry => entry.line.isDefault)
  if (!defaultEntry) return []
  return (['ios', 'android'] as const).flatMap(platform => {
    const ahead = lineAheadOfDefault(lines, defaultEntry, platform)
    if (!ahead) return []
    return [
      {
        kind: 'default_line_behind' as const,
        platform,
        defaultLine: {
          id: defaultEntry.line.id,
          name: defaultEntry.line.name,
          live: defaultEntry[platform].states.live
        },
        aheadLine: { id: ahead.line.id, name: ahead.line.name, live: ahead[platform].states.live }
      }
    ]
  })
}

/**
 * GET /v1/mobile-apps/release-lines/summary
 * GET /internal/mobile-apps/release-lines/summary
 */
export const ReleaseLineSummarySchema = z.object({
  /** The `now` every state was derived against. */
  generatedAt: z.coerce.date(),
  /** Every line, newest runtime first. Empty until the seed has run. */
  lines: z.array(ReleaseLineSummaryLineSchema),
  blocked: z.array(BlockedAppLineSchema),
  /** Every in-scope app, by display name. */
  apps: z.array(AppReleaseLinesSchema),
  /** U7: true when the draft read failed, so no draft count or app flag is present. */
  draftsUnreadable: z.boolean().optional(),
  /** SCH-3782: line-level findings, e.g. a default line the fleet has moved past. Absent from older servers. */
  findings: z.array(DefaultLineBehindFindingSchema).optional()
})
export type ReleaseLineSummary = z.infer<typeof ReleaseLineSummarySchema>

export const ReleaseLineSummaryResponseSchema = z.object({
  data: ReleaseLineSummarySchema,
  message: z.string()
})
export type ReleaseLineSummaryResponse = z.infer<typeof ReleaseLineSummaryResponseSchema>

/**
 * GET /v1/mobile-apps/:id/release-lines — one app's lines, plus the fleet's
 * lines so the page can name them and offer them to the blocked control.
 */
export const AppReleaseLinesResponseSchema = z.object({
  data: z.object({
    app: AppReleaseLinesSchema,
    lines: z.array(ReleaseLineSchema)
  }),
  message: z.string()
})
export type AppReleaseLinesResponse = z.infer<typeof AppReleaseLinesResponseSchema>

/**
 * PATCH /v1/mobile-apps/:id/release-lines/:lineId — set or clear the blocked
 * reason on the app's record for that line and platform. Null or blank clears.
 */
export const SetAppLineBlockedReasonRequestSchema = z.object({
  platform: PlatformSchema,
  blockedReason: z.string().trim().max(500).nullable()
})
export type SetAppLineBlockedReasonRequest = z.infer<typeof SetAppLineBlockedReasonRequestSchema>

export const SetAppLineBlockedReasonResponseSchema = z.object({
  data: z.object({ blockedReason: z.string().nullable() }),
  message: z.string()
})
export type SetAppLineBlockedReasonResponse = z.infer<typeof SetAppLineBlockedReasonResponseSchema>

/**
 * POST /v1/mobile-apps/:id/release-lines/:lineId/drift-check — read the live
 * store listing for one platform and compare it with the app × line record's
 * shipped snapshot (U15, R24). The result is stored on the record and returned.
 */
export const CheckAppLineDriftRequestSchema = z.object({ platform: PlatformSchema })
export type CheckAppLineDriftRequest = z.infer<typeof CheckAppLineDriftRequestSchema>

export const CheckAppLineDriftResponseSchema = z.object({
  data: AppLineDriftSchema,
  message: z.string()
})
export type CheckAppLineDriftResponse = z.infer<typeof CheckAppLineDriftResponseSchema>

// ==========================================
// Release an app on a line (U13, R22, KTD9)
// ==========================================
//
// One staff action per app: push the store content (Apple app info, listing and
// screenshots; Play listing and images), or apply the build drafts and rebuild,
// or ship an over-the-air update, on the chosen line per platform, and freeze
// what was actually sent into the app × line record's shipped snapshot
// (`AppLineShippedSnapshotSchema`). Never a review submission.

/**
 * How a platform's target line was chosen. `requested`: named in the path or
 * the body. Otherwise the KTD9 default: the upcoming line for a store push,
 * the current one for a rebuild or OTA, else the other of the two, else the
 * fleet's default line.
 */
export const LINE_RELEASE_TARGET_SOURCES = ['requested', 'upcoming', 'current', 'default'] as const
export const LineReleaseTargetSourceSchema = z.enum(LINE_RELEASE_TARGET_SOURCES)
export type LineReleaseTargetSource = z.infer<typeof LineReleaseTargetSourceSchema>

/**
 * The path's `:lineId` for "the KTD9 default line on each platform", so the
 * setup rows and a caller that does not know the app's lines can ask for it.
 */
export const LINE_RELEASE_DEFAULT_TARGET = 'default'

/**
 * The `version` a build or OTA release on a line asks for (SCH-2563 U5, R8, R9):
 * a marketing version whose `mobile/v<version>` tag the line has, or the
 * literal `branch` for the line's own ref, which ships whatever version its
 * `version.json` names (a new one, when untagged). Absent: the line's latest
 * tagged version, else its branch when the line has no tag yet. Always three
 * parts: a four-part App Store version (`1.9.1.1`, SCH-2593) names a store
 * record, never a fleet version, so it is not a version a line can ship.
 */
export const LINE_RELEASE_BRANCH_VERSION = 'branch'
export const LineReleaseVersionSchema = z
  .string()
  .trim()
  .regex(/^(\d+\.\d+\.\d+|branch)$/, 'A version such as 1.9.1, or "branch" for the line\'s branch')

/**
 * POST /v1/mobile-apps/:id/release-lines/:lineId/release. `:lineId` is the
 * target on every platform (or `default`); `lines` overrides it per platform.
 */
export const ReleaseAppOnLineRequestSchema = z
  .object({
    kind: LineReleaseKindSchema,
    platforms: z.array(PlatformSchema).min(1).max(2),
    lines: z.partialRecord(PlatformSchema, z.string().trim().min(1)).optional(),
    /** Build and OTA only; see `LineReleaseVersionSchema`. */
    version: LineReleaseVersionSchema.optional(),
    /**
     * Store only, iOS only (SCH-2593, provisional): file this push under the
     * next four-part App Store version (`1.9.1.1`) when the app's record for
     * the fleet version is already live. An asset-only rebuild's opt-in;
     * absent or false keeps the three-part behaviour.
     */
    storeResubmission: z.boolean().optional()
  })
  .refine(body => new Set(body.platforms).size === body.platforms.length, {
    message: 'Name each platform once',
    path: ['platforms']
  })
export type ReleaseAppOnLineRequest = z.infer<typeof ReleaseAppOnLineRequestSchema>

/** GET /v1/mobile-apps/:id/release-lines/:lineId/release — the same choice, as query parameters. */
export const ReleaseAppOnLinePlanQuerySchema = z.object({
  kind: LineReleaseKindSchema,
  /** Comma-separated: `IOS`, `ANDROID` or both. */
  platforms: z
    .string()
    .transform(value => value.split(',').map(part => part.trim().toUpperCase()))
    .pipe(z.array(PlatformSchema).min(1).max(2)),
  iosLineId: z.string().trim().min(1).optional(),
  androidLineId: z.string().trim().min(1).optional(),
  version: LineReleaseVersionSchema.optional()
})
export type ReleaseAppOnLinePlanQuery = z.infer<typeof ReleaseAppOnLinePlanQuerySchema>

/** A school draft the release would carry, as the confirm dialog lists it. */
export const LineReleasePendingDraftSchema = z.object({
  fieldKey: SchoolAppFieldKeySchema,
  deliveryClass: SchoolAppFieldDeliveryClassSchema,
  /** A value draft (app name, icon, splash, adaptive icon) is applied to its live home first. */
  hasValue: z.boolean(),
  savedAt: z.string().datetime(),
  releaseRequestedAt: z.string().datetime().nullable()
})
export type LineReleasePendingDraft = z.infer<typeof LineReleasePendingDraftSchema>

/**
 * How a build or OTA release chose its ref (SCH-2563 U5, R9): the requested
 * version's tag, the line's latest tag (the default), the line's branch on
 * request, or the line's branch because the line has no tag yet.
 */
export const LINE_RELEASE_REF_CHOICES = ['requested_version', 'latest_tag', 'branch', 'no_tag'] as const
export const LineReleaseRefChoiceSchema = z.enum(LINE_RELEASE_REF_CHOICES)
export type LineReleaseRefChoice = z.infer<typeof LineReleaseRefChoiceSchema>

/** One ref as a release dialog shows it before dispatch: what it ships, and what the guard would say. */
export const LineReleaseRefReadSchema = z.object({
  ref: z.string(),
  /** The marketing version its `version.json` names; null when it could not be read. */
  version: z.string().nullable(),
  /** The commit it resolves to; null when it could not be read. */
  commit: z.string().nullable(),
  sourceClass: ReleaseSourceClassSchema,
  /** Every build or OTA release on a line is production, so this is the R6 warning as dispatch will record it. */
  sourceWarning: z.string().nullable(),
  /** The R5 finding the dispatch would carry; null when there is nothing to say. */
  identity: VersionIdentityFindingSchema.nullable(),
  /** Why the version, commit or identity could not be read; null when all were. */
  readError: z.string().nullable()
})
export type LineReleaseRefRead = z.infer<typeof LineReleaseRefReadSchema>

/** The ref a build or OTA release would dispatch from, and the line's alternatives. */
export const LineReleaseDispatchRefSchema = LineReleaseRefReadSchema.extend({
  chosenBy: LineReleaseRefChoiceSchema,
  /** The line's tagged versions (`mobile/v<line>.*`), newest first. */
  availableVersions: z.array(z.string()),
  /**
   * The line's branch, read the same way, so the picker can label it ("line
   * branch, v1.9.2") and say whether that version is new. `tagged`: the
   * version it names already has a tag. Null when the line has no ref.
   */
  lineBranch: LineReleaseRefReadSchema.extend({ tagged: z.boolean() }).nullable()
})
export type LineReleaseDispatchRef = z.infer<typeof LineReleaseDispatchRefSchema>

/** What one platform's release would do. `refusal` set means it would do nothing, and says why. */
export const LineReleasePlatformPlanSchema = z.object({
  platform: PlatformSchema,
  lineId: z.string().nullable(),
  lineName: z.string().nullable(),
  source: LineReleaseTargetSourceSchema.nullable(),
  /** The ref a build or OTA would be dispatched from: a version tag, or the line's branch (SCH-2563 U5). */
  gitRef: z.string().nullable(),
  /** Build and OTA: how that ref was chosen and what it ships. Null for a store push or a refused platform. */
  dispatchRef: LineReleaseDispatchRefSchema.nullable().optional(),
  steps: z.array(LineReleaseStepSchema),
  /** The line's what's new, `{schoolName}` filled in. Null when the line has none. */
  whatsNew: z.string().nullable(),
  pendingDrafts: z.array(LineReleasePendingDraftSchema),
  /**
   * The compliance fields the line lacks a sign-off for on this platform (U14).
   * A release is NOT refused for them, since it pushes content and submits
   * nothing, but the review submission after it will be.
   */
  complianceMissing: z.array(ComplianceFieldSchema),
  refusal: z.string().nullable()
})
export type LineReleasePlatformPlan = z.infer<typeof LineReleasePlatformPlanSchema>

export const ReleaseAppOnLinePlanSchema = z.object({
  appId: z.string(),
  kind: LineReleaseKindSchema,
  platforms: z.array(LineReleasePlatformPlanSchema)
})
export type ReleaseAppOnLinePlan = z.infer<typeof ReleaseAppOnLinePlanSchema>

export const ReleaseAppOnLinePlanResponseSchema = z.object({
  data: ReleaseAppOnLinePlanSchema,
  message: z.string()
})
export type ReleaseAppOnLinePlanResponse = z.infer<typeof ReleaseAppOnLinePlanResponseSchema>

/**
 * One step as the release result reports it. A committed `dispatch-build` or
 * `dispatch-ota` step carries the dispatch's guard report (SCH-2563 U5), so
 * the dialog shows its source warning and identity finding.
 */
export const LineReleaseResultStepSchema = LineReleaseStepResultSchema.extend({
  runtimeGuard: RuntimeGuardReportSchema.optional()
})
export type LineReleaseResultStep = z.infer<typeof LineReleaseResultStepSchema>

/** What one platform's release did, step by step. */
export const LineReleasePlatformResultSchema = z.object({
  platform: PlatformSchema,
  lineId: z.string(),
  lineName: z.string(),
  releaseId: z.string(),
  status: LineReleaseStatusSchema,
  failedStep: LineReleaseStepSchema.nullable(),
  steps: z.array(LineReleaseResultStepSchema),
  complianceMissing: z.array(ComplianceFieldSchema),
  /** The build or OTA dispatch id, for a rebuild or an OTA. */
  dispatchId: z.string().nullable()
})
export type LineReleasePlatformResult = z.infer<typeof LineReleasePlatformResultSchema>

export const ReleaseAppOnLineResultSchema = z.object({
  appId: z.string(),
  kind: LineReleaseKindSchema,
  platforms: z.array(LineReleasePlatformResultSchema)
})
export type ReleaseAppOnLineResult = z.infer<typeof ReleaseAppOnLineResultSchema>

export const ReleaseAppOnLineResponseSchema = z.object({
  data: ReleaseAppOnLineResultSchema,
  message: z.string()
})
export type ReleaseAppOnLineResponse = z.infer<typeof ReleaseAppOnLineResponseSchema>

/**
 * GET /v1/mobile-apps/release-lines/by-version?version=1.8.25
 * GET /internal/mobile-apps/release-lines/by-version?version=1.8.25
 *
 * Every app that has one marketing version, in a store or as a build we made.
 * Matched exactly on the version string.
 */
export const ReleaseLineVersionAppsRequestSchema = z.object({
  version: z.string().trim().min(1).max(64)
})
export type ReleaseLineVersionAppsRequest = z.infer<typeof ReleaseLineVersionAppsRequestSchema>

export const ReleaseLineVersionAppSchema = z.object({
  appId: z.string(),
  appName: z.string(),
  schoolName: z.string().nullable(),
  platform: PlatformSchema,
  /** The newest store state for this version. Null: we built it and no store has shown it. */
  storeState: StoreVersionStateSchema.nullable(),
  vendorState: z.string().nullable(),
  /** When a store first showed this version for the app. Null when none has. */
  firstObservedAt: z.coerce.date().nullable(),
  /** The line the store row resolved to (KTD2). Null when unresolved or never in a store. */
  runtimeVersion: z.string().nullable(),
  /** Store build numbers seen with this version. */
  storeBuildNumbers: z.array(z.string()),
  /** A BUILD artifact with this version exists: we built it. */
  built: z.boolean(),
  builtBuildNumbers: z.array(z.string())
})
export type ReleaseLineVersionApp = z.infer<typeof ReleaseLineVersionAppSchema>

export const ReleaseLineVersionAppsSchema = z.object({
  version: z.string(),
  apps: z.array(ReleaseLineVersionAppSchema)
})
export type ReleaseLineVersionApps = z.infer<typeof ReleaseLineVersionAppsSchema>

export const ReleaseLineVersionAppsResponseSchema = z.object({
  data: ReleaseLineVersionAppsSchema,
  message: z.string()
})
export type ReleaseLineVersionAppsResponse = z.infer<typeof ReleaseLineVersionAppsResponseSchema>

// ==========================================
// GitHub Integration
// ==========================================

/**
 * GET /v1/mobile-apps/git/branches
 */
export const ListGitBranchesResponseSchema = z.object({
  data: z.array(GitBranchResponseSchema),
  message: z.string()
})
export type ListGitBranchesResponse = z.infer<typeof ListGitBranchesResponseSchema>

/**
 * GET /v1/mobile-apps/git/release-refs
 *
 * The `release/mobile-*` branches and `mobile/v*` tags an OTA can be sent
 * from — branches first, then tags, each newest version first.
 */
export const ListGitReleaseRefsResponseSchema = z.object({
  data: z.array(GitReleaseRefSchema),
  message: z.string()
})
export type ListGitReleaseRefsResponse = z.infer<typeof ListGitReleaseRefsResponseSchema>

/**
 * GET /v1/mobile-apps/git/mobile-version?ref=
 *
 * The marketing version `apps/mobile/version.json` carries on that ref — what
 * an OTA or build dispatched from the ref will ship as.
 */
export const GetMobileVersionRequestSchema = z.object({
  ref: z.string().min(1)
})
export type GetMobileVersionRequest = z.infer<typeof GetMobileVersionRequestSchema>

export const MobileVersionOnRefSchema = z.object({
  ref: z.string(),
  /** Marketing version, e.g. `1.8.21`. */
  version: z.string(),
  /** Expo runtime version, e.g. `1.8.0`. */
  runtimeVersion: z.string(),
  /**
   * The commit the ref resolves to (SCH-2563 U4). Null when it could not be
   * read; absent from callers that read only `version.json`.
   */
  commit: z.string().nullable().optional(),
  /**
   * What the version names: its established commit, `establishedBy` and the
   * other-ref releases listed for context (KTD5). Set it against `commit` with
   * `versionIdentityFinding` for the finding a production dispatch would carry.
   * Null when it could not be read.
   */
  identity: VersionIdentitySchema.nullable().optional(),
  /** Why `commit` or `identity` could not be read. */
  identityError: z.string().optional()
})
export type MobileVersionOnRef = z.infer<typeof MobileVersionOnRefSchema>

export const GetMobileVersionResponseSchema = z.object({
  data: MobileVersionOnRefSchema,
  message: z.string()
})
export type GetMobileVersionResponse = z.infer<typeof GetMobileVersionResponseSchema>

// ==========================================
// Build Triggering
// ==========================================

/**
 * POST /v1/mobile-apps/:id/trigger-build
 *
 * `variant` selects the EAS build profile via `<variant>-dynamic`. `development`
 * yields `development-dynamic` — a `developmentClient: true`, internal-distribution
 * build installed by QR code and paired with a local Metro server. It is not a
 * store artifact, so it can never be auto-submitted (enforced below, and mirrored
 * by the backend's own schema).
 *
 * `simulator` yields `simulator-dynamic` — an UNSIGNED Release build for the iOS
 * Simulator (a `.tar.gz` wrapping a `.app`) or the Android emulator (an `.apk`).
 * No Apple team, certificate, provisioning profile or device list is involved, so
 * it is the one iOS artifact that builds non-interactively regardless of credential
 * state. `developmentClient: false` embeds the JS bundle, so it boots standalone
 * against the production backend with no Metro server. It is consumed by Appetize
 * (web embed) or Expo Orbit (local install), never by a phone and never by a store —
 * so no STORE auto-submit applies to it, but `autoSubmit: 'appetize'` does: the
 * platform pushes the finished binary to Appetize itself off the EAS webhook.
 */
export const BUILD_VARIANTS = ['production', 'preview', 'development', 'simulator'] as const
export const BuildVariantSchema = z.enum(BUILD_VARIANTS)
export type BuildVariant = z.infer<typeof BuildVariantSchema>

/**
 * What may happen to a build's artifact automatically once EAS finishes it.
 *
 * `ios-only` / `both` are STORE lanes — EAS hands the signed binary to App Store
 * Connect (and Play). `appetize` is not a store lane at all: the platform
 * uploads the UNSIGNED simulator artifact to Appetize so it becomes playable in
 * a browser. The two are mutually exclusive per variant, which is what
 * {@link allowedAutoSubmitModes} encodes.
 */
export const AUTO_SUBMIT_MODES = ['none', 'ios-only', 'both', 'appetize'] as const
export const AutoSubmitModeSchema = z.enum(AUTO_SUBMIT_MODES)
export type AutoSubmitMode = z.infer<typeof AutoSubmitModeSchema>

/**
 * Variants that produce no store artifact: a development client paired with Metro,
 * and an unsigned simulator/emulator binary. Neither can ever be auto-submitted
 * to a store — though `simulator` has somewhere else to go, see below.
 */
export const STORE_LESS_BUILD_VARIANTS = ['development', 'simulator'] as const satisfies readonly BuildVariant[]

/**
 * SINGLE SOURCE OF TRUTH for "which follow-on action may this variant ask for",
 * read by the API schemas, the workflow guard and the web dialog alike.
 *
 * A table rather than a predicate, because the rule stopped being a yes/no the
 * moment `appetize` arrived: `simulator` refuses the store lane AND is the only
 * variant that accepts Appetize, and a boolean cannot say both.
 *
 * - `production` / `preview` — signed store binaries: the store lane, or nothing.
 * - `development` — a Metro-paired development client. Nothing consumes it
 *   automatically; a human installs it by QR code.
 * - `simulator` — an unsigned simulator/emulator binary. No store will take it,
 *   but Appetize will: a `.tar.gz`/`.app` for iOS and an `.apk` for Android are
 *   exactly what the `simulator-dynamic` profile emits.
 */
const AUTO_SUBMIT_MODES_BY_VARIANT: Readonly<Record<BuildVariant, readonly AutoSubmitMode[]>> = {
  production: ['none', 'ios-only', 'both'],
  preview: ['none', 'ios-only', 'both'],
  development: ['none'],
  simulator: ['none', 'appetize']
}

/** The auto-submit modes a variant may legally be dispatched with. Never empty — `none` always qualifies. */
export function allowedAutoSubmitModes(variant: BuildVariant): readonly AutoSubmitMode[] {
  return AUTO_SUBMIT_MODES_BY_VARIANT[variant]
}

/** The rule itself, as both API schemas and the web dialog ask it. */
export function isAutoSubmitModeAllowedForVariant(variant: BuildVariant, mode: AutoSubmitMode): boolean {
  return allowedAutoSubmitModes(variant).includes(mode)
}

/**
 * Why a (variant, mode) pair was refused, in the caller's own terms.
 *
 * One frozen constant could not stay honest across two opposite refusals — "a
 * simulator build cannot reach a store" and "a production build has nothing for
 * Appetize to run" are different facts — so the message is derived and always
 * names what the variant WILL accept.
 */
export function autoSubmitUnavailableMessage(variant: BuildVariant, mode: AutoSubmitMode): string {
  const allowed = allowedAutoSubmitModes(variant)
    .map(m => `"${m}"`)
    .join(', ')

  if (mode === 'appetize') {
    return (
      `autoSubmit "appetize" is only available for the simulator variant: Appetize runs an UNSIGNED simulator ` +
      `(.app/.tar.gz) or emulator (.apk) binary, which is the one thing a "${variant}" build does not produce. ` +
      `Allowed for "${variant}": ${allowed}.`
    )
  }

  return (
    `auto-submit is not available for the development and simulator variants: neither produces a store artifact — ` +
    `development builds are internal-distribution development clients, and simulator builds are unsigned ` +
    `simulator/emulator binaries. Allowed for "${variant}": ${allowed}.`
  )
}

export const TriggerBuildRequestSchema = z
  .object({
    platform: z.enum(['ios', 'android', 'both']),
    variant: BuildVariantSchema,
    autoSubmit: AutoSubmitModeSchema.default('none'),
    schools: z.string().min(1).optional(),
    ref: z.string().min(1).default('main'),
    /**
     * Dispatch even though the runtime guard refuses the ref's runtime for this
     * app (R25, KTD7). Recorded on the release with who set it.
     */
    override: z.boolean().default(false),
    /**
     * Build a production binary even though a blocking readiness check fails (SCH-2318). A
     * super-admin decision: `overrideReason` is required with it, and both are recorded on the
     * `build.dispatched` event with the checks bypassed. Other variants never block on readiness.
     */
    overrideReadiness: z.boolean().optional(),
    overrideReason: z.string().trim().min(1).max(500).optional()
  })
  .superRefine((input, ctx) => {
    if (input.overrideReadiness && !input.overrideReason) {
      ctx.addIssue({
        code: 'custom',
        path: ['overrideReason'],
        message: 'overrideReadiness needs a reason: say why this build ships past a failing readiness check.'
      })
    }
    if (isAutoSubmitModeAllowedForVariant(input.variant, input.autoSubmit)) return
    ctx.addIssue({
      code: 'custom',
      path: ['autoSubmit'],
      message: autoSubmitUnavailableMessage(input.variant, input.autoSubmit)
    })
  })
export type TriggerBuildRequest = z.infer<typeof TriggerBuildRequestSchema>

export const TriggerBuildResponseSchema = z.object({
  data: z.object({
    /** The release this dispatch created. Every trigger is a release now (R1, R2). */
    releasePublicId: z.string(),
    /** One per app x platform. These are the ids the executor callback returns. */
    targetPublicIds: z.array(z.string()),
    dispatchId: z.string(),
    /** What the runtime guard found for the ref (U5). */
    runtimeGuard: RuntimeGuardReportSchema.optional(),
    /**
     * Readiness failures the build went ahead despite (SCH-2318): a non-production variant, which
     * readiness never holds back, or an overridden production build. Empty or absent when clean.
     */
    readinessWarnings: z.array(z.string()).optional()
  }),
  message: z.string()
})
export type TriggerBuildResponse = z.infer<typeof TriggerBuildResponseSchema>

/**
 * POST /v1/mobile-apps/trigger-ota-update
 *
 * Fleet-wide (not per-app): OTA spans a school set, so this is a top-level
 * route. `schools` is a comma-separated list of registry school keys, or `all`.
 */
export const TriggerOtaUpdateRequestSchema = z.object({
  schools: z.string().min(1),
  channel: z.enum(['production', 'preview']),
  platform: z.enum(['ios', 'android', 'both']),
  ref: z.string().min(1).default('main'),
  /**
   * Dispatch even though the runtime guard refuses the ref's runtime. A
   * single-school OTA only: a multi-school OTA skips the apps that do not accept
   * the ref instead (KTD7).
   */
  override: z.boolean().default(false)
})
export type TriggerOtaUpdateRequest = z.infer<typeof TriggerOtaUpdateRequestSchema>

export const TriggerOtaUpdateResponseSchema = z.object({
  data: z.object({
    dispatchId: z.string(),
    releasePublicId: z.string(),
    targetPublicIds: z.array(z.string()),
    /** The school keys dispatched to. Apps the runtime guard skipped are not here. */
    schoolsResolved: z.array(z.string()),
    /** What the runtime guard found for the ref, including the apps it skipped (U5). */
    runtimeGuard: RuntimeGuardReportSchema.optional()
  }),
  message: z.string()
})
export type TriggerOtaUpdateResponse = z.infer<typeof TriggerOtaUpdateResponseSchema>

// ==========================================
// Provisioning Dry-Run Plan
// ==========================================
//
// POST /v1/mobile-apps/provisioning-runs/dry-run — side-effect-free preview of
// what a provisioning run WOULD do. No ProvisioningRun row is created, no
// external API is called, no DB write happens. The plan reports the ordered
// steps, each step's side-effect class, the first external step, and any
// blockers (missing backend env secrets) plus reminders (human-pre-created
// resources that can't be auto-checked).

export const ProvisioningStepEffectSchema = z.enum(['db-only', 'external', 'side-effect'])
export type ProvisioningStepEffect = z.infer<typeof ProvisioningStepEffectSchema>

export const ProvisioningStepPlanSchema = z.object({
  name: z.string(),
  order: z.number().int(),
  effect: ProvisioningStepEffectSchema,
  status: z.enum(['ready', 'blocked', 'skipped']),
  missingEnvVars: z.array(z.string()),
  externalPreCreated: z.array(z.string())
})
export type ProvisioningStepPlan = z.infer<typeof ProvisioningStepPlanSchema>

export const ProvisioningPlanSchema = z.object({
  ready: z.boolean(),
  steps: z.array(ProvisioningStepPlanSchema),
  firstExternalStep: z.string().nullable(),
  blockers: z.array(z.string()),
  reminders: z.array(z.string())
})
export type ProvisioningPlan = z.infer<typeof ProvisioningPlanSchema>

export const ProvisioningPlanResponseSchema = z.object({
  plan: ProvisioningPlanSchema
})
export type ProvisioningPlanResponse = z.infer<typeof ProvisioningPlanResponseSchema>

// ==========================================
// Provisioning credentials (per-app, filed under the app's publishing accounts)
// ==========================================
//
// The MobileApp row itself stores NO provisioning secrets — the plaintext ASC
// and Google service-account columns were retired in the durability phase and
// dropped (SCH-2761); the KMS-encrypted `Integration` store is the only home.
// Credentials are filed under the app's publishing account (Apple team, Play
// account); legacy school-scoped rows are read as a fallback. These contracts
// are the admin surface onto that store, keyed by MobileApp public id.
//
// Reads NEVER return secret material: the GET reports presence only.

/**
 * PUT /v1/mobile-apps/:id/provisioning-credentials
 *
 * Every artifact is optional and vaulted independently (each is its own
 * single-writer row), but a body carrying none of them is rejected — an empty
 * upload that 200s would read as "credentials saved" while writing nothing.
 */
export const UpdateProvisioningCredentialsRequestSchema = z
  .object({
    ascApiKey: AscApiKeyInputSchema.optional(),
    apnsAuthKey: ApnsAuthKeyInputSchema.optional(),
    /** Service-account JSON in its serialized string form. */
    googleServiceAccountJson: z.string().min(1).optional()
  })
  .refine(body => !!(body.ascApiKey || body.apnsAuthKey || body.googleServiceAccountJson), {
    message: 'Provide at least one credential (ascApiKey, apnsAuthKey or googleServiceAccountJson)'
  })
export type UpdateProvisioningCredentialsRequest = z.infer<typeof UpdateProvisioningCredentialsRequestSchema>

/** Presence of each vaulted artifact for a school. Never carries secret material. */
export const ProvisioningCredentialsStatusSchema = z.object({
  ascApiKey: z.boolean(),
  apnsAuthKey: z.boolean(),
  googleServiceAccount: z.boolean()
})
export type ProvisioningCredentialsStatus = z.infer<typeof ProvisioningCredentialsStatusSchema>

/**
 * Where this app's credentials are read from. They belong to the app's PUBLISHING ACCOUNTS (its
 * Apple team and Play account), never to its school, and are written there.
 *
 * - `school`         — the app has a school: account lane first, the school's legacy rows as the
 *                      fallback for anything not yet on the account lane.
 * - `account`        — the app has no school (the School Journey fleet app): account lane only.
 * - `not-applicable` — no longer returned; kept so an older client still parses.
 */
export const ProvisioningCredentialsScopeSchema = z.enum(['school', 'account', 'not-applicable'])
export type ProvisioningCredentialsScope = z.infer<typeof ProvisioningCredentialsScopeSchema>

export const ProvisioningCredentialsResponseSchema = z.object({
  data: z.object({
    mobileAppId: z.string(),
    /** The app's school, or null for a school-less app. */
    schoolId: z.string().nullable(),
    scope: ProvisioningCredentialsScopeSchema,
    /** Always present from the current backend; nullable only for the retired `not-applicable`. */
    credentials: ProvisioningCredentialsStatusSchema.nullable()
  }),
  message: z.string()
})
export type ProvisioningCredentialsResponse = z.infer<typeof ProvisioningCredentialsResponseSchema>

// ==========================================
// Minted provisioning tokens (account-keyed credential vault, U3)
// ==========================================
//
// The credential vault hands external consumers a SHORT-LIVED MINTED TOKEN, never
// the credential (R7, KD3/KTD3). CI and operators name the account (or the app
// that resolves to it), the key, and the operations they need; the backend
// decrypts in-process, mints a scoped App Store Connect JWT, and records the
// issuance. No key material appears in the request, the response, or any log
// line — the only thing that leaves the process is the JWT.
//
// App Store Connect ONLY this phase. Google Play minting is deliberately absent:
// no Play credential is imported, no app resolves to a Play account, and a Play
// request is refused as out of scope rather than silently answered with an Apple
// token.

/** The vendor a token is minted for. Play is accepted only so it can be refused explicitly. */
export const MintedTokenProviderSchema = z.enum(['app-store-connect', 'google-play'])
export type MintedTokenProvider = z.infer<typeof MintedTokenProviderSchema>

/** Methods Apple's `scope` claim understands, mirroring the ASC client's method union. */
export const AscOperationMethodSchema = z.enum(['GET', 'POST', 'PATCH', 'DELETE'])
export type AscOperationMethod = z.infer<typeof AscOperationMethodSchema>

/**
 * One request line the minted token is allowed to make.
 *
 * `path` is constrained to an App Store Connect API path so a caller cannot
 * smuggle whitespace or a newline into the `scope` claim, which is a
 * space-separated `"<METHOD> <path>"` string on Apple's side — a space in the
 * path would silently widen or corrupt the claim.
 */
export const AscTokenOperationSchema = z.object({
  method: AscOperationMethodSchema,
  path: z
    .string()
    .min(1)
    .max(500)
    .regex(
      /^\/v\d+\/[A-Za-z0-9\-._~/%!$&'()*+,;=:@[\]?]*$/,
      'path must be an App Store Connect API path, e.g. /v1/apps'
    )
})
export type AscTokenOperation = z.infer<typeof AscTokenOperationSchema>

/**
 * POST /v1/mobile-apps/provisioning-credentials/asc-token
 * POST /internal/mobile-apps/provisioning-credentials/asc-token
 *
 * `callerId` is REQUIRED and is the only real attribution this surface has
 * (KTD7): the synthetic internal principal's `sub` is a constant, so every
 * API-key caller looks identical without it. It is a workflow run URL or an
 * operator handle.
 *
 * The account is named EITHER directly (`accountId` — an Apple team id) or
 * indirectly (`mobileAppId`, resolved through `MobileApp.appleTeamId`). Exactly
 * one, never both: accepting both would need a precedence rule, and the wrong
 * branch of that rule mints one team's token for another team's app.
 */
export const MintProvisioningTokenRequestSchema = z
  .object({
    provider: MintedTokenProviderSchema.default('app-store-connect'),
    /** Apple team id. Mutually exclusive with `mobileAppId`. */
    accountId: z.string().trim().min(1).max(200).optional(),
    /** Mobile app public id, resolved to its Apple team. Mutually exclusive with `accountId`. */
    mobileAppId: z.string().trim().min(1).max(200).optional(),
    /** WHICH key of that account — the vault stores one row per key (KTD1). */
    keyId: z.string().trim().min(1).max(200),
    /** Workflow run URL or operator handle. Recorded with the issuance. */
    callerId: z.string().trim().min(1).max(500),
    /** At least one; the token is scoped to exactly these request lines. */
    operations: z.array(AscTokenOperationSchema).min(1).max(50),
    /** Seconds. Bounded by Apple's twenty-minute ceiling; defaults to it. */
    ttlSeconds: z.number().int().min(1).max(1200).optional()
  })
  .refine(body => !!body.accountId !== !!body.mobileAppId, {
    message: 'Provide exactly one of accountId or mobileAppId'
  })
export type MintProvisioningTokenRequest = z.infer<typeof MintProvisioningTokenRequestSchema>

/**
 * The minted token and the non-secret facts about it.
 *
 * Carries NO key material: not the private key, not the issuer id. `keyId` is
 * echoed because the caller supplied it and needs to know which key answered;
 * it is a public identifier Apple puts in the JWT header regardless.
 */
export const MintProvisioningTokenResponseSchema = z.object({
  data: z.object({
    provider: z.literal('app-store-connect'),
    accountId: z.string(),
    keyId: z.string(),
    /** The bearer token to send to App Store Connect. */
    token: z.string(),
    /** ISO-8601. Always within Apple's twenty-minute ceiling. */
    expiresAt: z.string(),
    issuedAt: z.string(),
    /** The `scope` claim as minted — the exact request lines this token may make. */
    scope: z.array(z.string()),
    /** Echoed so a caller can confirm what was recorded against the issuance. */
    callerId: z.string()
  }),
  message: z.string()
})
export type MintProvisioningTokenResponse = z.infer<typeof MintProvisioningTokenResponseSchema>

/**
 * OAuth scopes this surface will mint a Google token for.
 *
 * ALLOWLIST, NOT A PASSTHROUGH, and the reason is that `google-play` is an
 * OVERLOADED PROVIDER. One provider name currently covers two different
 * credentials with two different jobs: the Play *publisher* service account,
 * which needs `androidpublisher`, and the service account handed to OneSignal
 * as `fcm_v1_service_account_json`, which needs `firebase.messaging` /
 * `cloud-platform`. Minting a Firebase-scoped token from whatever happens to be
 * filed under `google-play` would hand out a token for a purpose its key was
 * never meant to serve — and, worse, would look like it worked.
 *
 * `androidpublisher` alone is unambiguous today: it is what the one vaulted key
 * is for. Widen this only once an `fcm` provider exists to separate them.
 */
export const PLAY_TOKEN_SCOPES = ['https://www.googleapis.com/auth/androidpublisher'] as const
export const PlayTokenScopeSchema = z.enum(PLAY_TOKEN_SCOPES)
export type PlayTokenScope = z.infer<typeof PlayTokenScopeSchema>

/**
 * POST /v1/mobile-apps/provisioning-credentials/play-token
 * POST /internal/mobile-apps/provisioning-credentials/play-token
 *
 * The Google sibling of the ASC minting route, and deliberately NOT the same
 * route widened. Apple's token is a JWT the backend signs offline; Google's is
 * an OAuth2 exchange with Google's own token endpoint — an outbound call that
 * can fail on its own terms, and whose expiry Google chooses rather than we do.
 * Those are different failure surfaces, so they get different routes.
 *
 * `keyId` is OPTIONAL here, unlike the ASC route. The caller that needs this
 * most — the browser runner asking "let me talk to the Play API" — knows the
 * account but has no reason to know which key of it answered. Omitting it
 * resolves the single credential filed under the account and REFUSES if there
 * is more than one, rather than picking by row order.
 */
export const MintPlayTokenRequestSchema = z
  .object({
    /** Play developer account id. Mutually exclusive with `mobileAppId`. */
    accountId: z.string().trim().min(1).max(200).optional(),
    /** Mobile app public id, resolved through `MobileApp.playAccountId`. Mutually exclusive with `accountId`. */
    mobileAppId: z.string().trim().min(1).max(200).optional(),
    /** WHICH key of that account. Omit to use the account's only key — ambiguity is refused, never guessed. */
    keyId: z.string().trim().min(1).max(200).optional(),
    /** Workflow run URL or operator handle. Recorded with the issuance. */
    callerId: z.string().trim().min(1).max(500),
    /** Defaults to `androidpublisher`. Anything outside the allowlist is refused. */
    scopes: z.array(PlayTokenScopeSchema).min(1).max(4).optional()
  })
  .refine(body => !!body.accountId !== !!body.mobileAppId, {
    message: 'Provide exactly one of accountId or mobileAppId'
  })
export type MintPlayTokenRequest = z.infer<typeof MintPlayTokenRequestSchema>

/**
 * The minted token and the non-secret facts about it.
 *
 * Carries NO key material — not the private key, not the client email. `keyId`
 * is echoed because the caller may not have supplied one and needs to know
 * which key answered.
 *
 * `expiresAt` is GOOGLE'S expiry, derived from the `expires_in` it returned,
 * not a ceiling of our choosing. Treating it as ours is how a caller ends up
 * holding a token it believes is live.
 */
export const MintPlayTokenResponseSchema = z.object({
  data: z.object({
    provider: z.literal('google-play'),
    accountId: z.string(),
    keyId: z.string(),
    /** The bearer token to send to the Google Play Developer API. */
    token: z.string(),
    /** ISO-8601, as returned by Google. */
    expiresAt: z.string(),
    issuedAt: z.string(),
    /** The OAuth scopes the token was minted for. */
    scope: z.array(z.string()),
    /** Echoed so a caller can confirm what was recorded against the issuance. */
    callerId: z.string()
  }),
  message: z.string()
})
export type MintPlayTokenResponse = z.infer<typeof MintPlayTokenResponseSchema>

// ==========================================
// Account-scoped credential writes (account-keyed credential vault, U7)
// ==========================================
//
// The WRITE side of the vault. A credential is filed under the account that owns
// it (an Apple team, later a Play account) plus the key id naming which key of
// that account it is (KTD1), so a team holding two App Store Connect keys is
// representable. This is the destination the operator CLI writes to instead of
// the local `~/.school-journey/credentials` directory (R12).
//
// The request carries key material; the response NEVER does. A write reports
// only the non-secret row facts — which account and key it landed under, the
// row's public id, and whether it REPLACED something.
//
// Create-only by default (KTD16). An App Store Connect private key downloads
// exactly once and Apple cannot reissue it, so replacing a filed credential
// takes an explicit `allowOverwrite`, spelled out in the request rather than
// inferred from anything.

/** Which artifact a vaulted row holds. Mirrors the store's per-artifact provider keys. */
export const VAULT_CREDENTIAL_PROVIDERS = ['apple-asc', 'apns', 'google-play', 'fcm'] as const
export const VaultCredentialProviderSchema = z.enum(VAULT_CREDENTIAL_PROVIDERS)
export type VaultCredentialProvider = z.infer<typeof VaultCredentialProviderSchema>

/**
 * Serialized-credential ceiling, in bytes.
 *
 * The vault writes in `kms-direct` mode, whose plaintext limit is KMS's 4 KB
 * (KTD10). Bounding it HERE turns an oversize blob into a named 400 at the
 * boundary instead of an opaque encryptor failure after the request was already
 * accepted. Anything genuinely larger — an Android keystore — needs envelope
 * mode chosen deliberately at write time, which is a different change rather
 * than a bigger number.
 */
export const VAULT_CREDENTIAL_MAX_BYTES = 4000

/**
 * UTF-8 byte length of a string, computed WITHOUT a platform global.
 *
 * The obvious spellings are both unavailable here on purpose. `Buffer` is
 * Node-only, and `TextEncoder` needs either `@types/node` or the DOM lib — this
 * package declares neither, because it is the shared contract imported by the
 * backend, the Next.js dashboard AND the React Native app, and giving it a
 * platform's globals lets platform-specific code compile into a package that
 * three runtimes consume. Reaching for `Buffer` here typechecks in the monorepo
 * (where `@types/node` is hoisted into the root `node_modules`) and then fails
 * in the backend's Docker build, which copies `packages/shared-types` alone —
 * a failure that first appears at deploy time, after CI is green.
 *
 * Byte-for-byte equivalent to `Buffer.byteLength(value, 'utf8')`, including for
 * lone surrogates: an unpaired surrogate encodes as U+FFFD, which is 3 bytes in
 * both implementations.
 */
function utf8ByteLength(value: string): number {
  let bytes = 0

  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i)

    if (code < 0x80) {
      bytes += 1
    } else if (code < 0x800) {
      bytes += 2
    } else if (code >= 0xd800 && code <= 0xdbff && i + 1 < value.length) {
      const next = value.charCodeAt(i + 1)
      if (next >= 0xdc00 && next <= 0xdfff) {
        // A well-formed surrogate pair is ONE code point in 4 bytes; consume both.
        bytes += 4
        i++
      } else {
        // Lone high surrogate → U+FFFD.
        bytes += 3
      }
    } else {
      bytes += 3
    }
  }

  return bytes
}

/** Google service-account JSON in its serialized string form — the shape the store holds. */
export const GoogleServiceAccountInputSchema = z.object({
  serviceAccountJson: z.string().min(1)
})
export type GoogleServiceAccountInput = z.infer<typeof GoogleServiceAccountInputSchema>

/**
 * FCM v1 service-account JSON in its serialized string form.
 *
 * Same shape as {@link GoogleServiceAccountInputSchema} — both are a serialized
 * GCP service-account key — but a DISTINCT schema deliberately, mirroring the
 * distinct `fcm` vault provider: this key needs `firebase.messaging` /
 * `cloud-platform` scope and belongs to a Firebase PROJECT, never the Play
 * publisher account `google-play` names. See `PROVISIONING_PROVIDERS` in
 * `apps/backend/src/services/provisioning/credentials.ts`.
 */
export const FcmServiceAccountInputSchema = z.object({
  serviceAccountJson: z.string().min(1)
})
export type FcmServiceAccountInput = z.infer<typeof FcmServiceAccountInputSchema>

/**
 * The non-secret half of a write request, shared by every provider arm.
 *
 * `callerId` is REQUIRED for the same reason it is on the issuance surface
 * (KTD7): the injected internal principal's `sub` is a constant, so without it
 * every API-key caller looks identical and the record attributes nothing.
 */
const vaultAccountCredentialCommonFields = {
  /** The owning account — an Apple team id. Never defaulted; an empty one is refused (KTD2). */
  accountId: z.string().trim().min(1).max(200),
  /** WHICH key of that account+provider this row holds (KTD1). */
  keyId: z.string().trim().min(1).max(200),
  /** Workflow run URL or operator handle. Recorded with the write. */
  callerId: z.string().trim().min(1).max(500),
  /**
   * Replace an already-filed credential. Defaults to FALSE and must be spelled
   * out (KTD16) — the row it would destroy may hold a key Apple will not reissue.
   */
  allowOverwrite: z.boolean().default(false)
}

/**
 * POST /internal/mobile-apps/provisioning-credentials/account-credential
 *
 * Discriminated on `provider` so the stored blob's SHAPE is checked at the
 * boundary rather than at read time. A structurally wrong App Store Connect blob
 * that vaults cleanly surfaces only later, as a mint failure on a credential
 * whose original is by then long gone from the operator's laptop.
 */
export const VaultAccountCredentialRequestSchema = z
  .discriminatedUnion('provider', [
    z.object({
      provider: z.literal('apple-asc'),
      credentials: AscApiKeyInputSchema,
      ...vaultAccountCredentialCommonFields
    }),
    z.object({
      provider: z.literal('apns'),
      credentials: ApnsAuthKeyInputSchema,
      ...vaultAccountCredentialCommonFields
    }),
    z.object({
      provider: z.literal('google-play'),
      credentials: GoogleServiceAccountInputSchema,
      ...vaultAccountCredentialCommonFields
    }),
    z.object({
      provider: z.literal('fcm'),
      credentials: FcmServiceAccountInputSchema,
      ...vaultAccountCredentialCommonFields
    })
  ])
  .refine(body => utf8ByteLength(JSON.stringify(body.credentials)) <= VAULT_CREDENTIAL_MAX_BYTES, {
    message: `credentials must serialize to at most ${VAULT_CREDENTIAL_MAX_BYTES} bytes (KMS direct-encrypt limit)`,
    path: ['credentials']
  })
export type VaultAccountCredentialRequest = z.infer<typeof VaultAccountCredentialRequestSchema>

/**
 * What the write DID — never what it wrote.
 *
 * `overwrote` is reported from the write's own outcome rather than from a read
 * taken beforehand: the vault deliberately has no exists-probe (probe-then-write
 * is a TOCTOU race that can cost a key), so the only honest answer to "did this
 * replace something" comes from the engine that arbitrated it.
 */
export const VaultAccountCredentialResponseSchema = z.object({
  data: z.object({
    provider: VaultCredentialProviderSchema,
    accountId: z.string(),
    keyId: z.string(),
    /** The `Integration` row's public id. A row handle, not key material. */
    publicId: z.string(),
    /** True when an existing credential was REPLACED rather than newly filed. */
    overwrote: z.boolean(),
    /** ISO-8601. */
    storedAt: z.string(),
    /** Echoed so a caller can confirm what was recorded against the write. */
    callerId: z.string()
  }),
  message: z.string()
})
export type VaultAccountCredentialResponse = z.infer<typeof VaultAccountCredentialResponseSchema>

/**
 * GET /internal/mobile-apps/provisioning-credentials/account-credential
 *
 * Presence-only, NO-DECRYPT read (Track I, U47 step 1) — every key filed under
 * one account+provider, metadata only. This is NOT the probe-then-write guard
 * the write-side comment above warns against: it never precedes a write on the
 * SAME credential, it is a caller (the browser runner's own step verdicts)
 * asking afterwards whether an earlier deposit actually landed, and the write
 * path still never checks before writing.
 */
export const AccountCredentialPresenceQuerySchema = z.object({
  provider: VaultCredentialProviderSchema,
  accountId: z.string().trim().min(1).max(200)
})
export type AccountCredentialPresenceQuery = z.infer<typeof AccountCredentialPresenceQuerySchema>

export const AccountCredentialPresenceResponseSchema = z.object({
  data: z.object({
    provider: VaultCredentialProviderSchema,
    accountId: z.string(),
    present: z.boolean(),
    /**
     * Which keys of the account carry this provider. Metadata only — never key
     * material. The timestamps are the vault ROW's, so `updatedAt` answers "when
     * was this key last replaced" — the question an operator looking at a
     * credential actually has. They are the row's own, not a liveness check:
     * a key filed years ago and still working reports an old `updatedAt`.
     */
    keys: z.array(
      z.object({
        keyId: z.string(),
        isActive: z.boolean(),
        /** ISO-8601. When the row was first filed. */
        storedAt: z.string(),
        /** ISO-8601. When the row was last written — a replacement moves this. */
        updatedAt: z.string()
      })
    )
  }),
  message: z.string()
})
export type AccountCredentialPresenceResponse = z.infer<typeof AccountCredentialPresenceResponseSchema>

// ---------------------------------------------------------------------------
// Publishing accounts (publishing-accounts plan, U6)
//
// One handler set behind two gates (KD7): the dashboard reaches these through
// `/v1` under Clerk, automation through `/internal` under an API key. The
// request and response shapes below are what BOTH speak, so the two surfaces
// cannot drift into two contracts.
// ---------------------------------------------------------------------------

/**
 * The account entity vocabulary, re-exported so a client importing this API
 * module gets the request shapes AND the values inside them from one place. The
 * dashboard renders every one of these unions as a label, so importing them from
 * two paths is how two surfaces end up spelling the same state differently.
 */
export {
  ApiAccessProbeOutcomeSchema,
  LegacyReadinessReasonSchema,
  PLACEHOLDER_ACCOUNT_ID_PREFIX,
  PublishingAccountApiAccessBlockedStepSchema,
  PublishingAccountApiAccessReasonSchema,
  PublishingAccountApiAccessStateSchema,
  ReadinessReasonSchema,
  ReadinessReasonWireSchema,
  buildPlaceholderAccountId,
  isPlaceholderAccountId,
  legacyAgreementStateFrom,
  legacyReadinessReasonFrom,
  readinessReasonFromLegacy,
  withLegacyReadinessReasons
} from '../../schemas/entities/publishing-account'
export type {
  AccountCredentialLiveness,
  ApiAccessProbeOutcome,
  LegacyReadinessReason,
  PublishingAccount,
  PublishingAccountAgreementState,
  PublishingAccountApiAccessBlockedStep,
  PublishingAccountApiAccessReason,
  PublishingAccountApiAccessState,
  PublishingAccountKind,
  PublishingAccountOwnership,
  PublishingAccountProvisioningState,
  PublishingAccountReadiness,
  PublishingAccountVendor,
  ReadinessHeadline,
  ReadinessReason,
  ReadinessReasonWire,
  ReadinessVerdict
} from '../../schemas/entities/publishing-account'

/**
 * The credential-pointer vocabulary (credential-home plan U1), re-exported for
 * the same reason the account vocabulary above is: the dashboard renders every
 * one of these unions as a label, and importing them from two paths is how two
 * surfaces end up spelling the same state differently.
 */
export type {
  CredentialCheckStatus,
  CredentialCoverageState,
  CredentialLane,
  CredentialLivenessState,
  MobileAppCredentialPointer,
  ProvisioningCredentialLiveness
} from '../../schemas/entities/mobile-app-credential-pointer'
export {
  CREDENTIAL_LANES,
  CredentialCheckStatusSchema,
  CredentialCoverageStateSchema,
  CredentialLaneSchema,
  CredentialLivenessStateSchema,
  FCM_LANE_BY_ENVIRONMENT,
  MobileAppCredentialPointerSchema,
  ProvisioningCredentialLivenessSchema
} from '../../schemas/entities/mobile-app-credential-pointer'
export type { FcmCredentialLane } from '../../schemas/entities/mobile-app-credential-pointer'

/**
 * The Firebase project-binding vocabulary (SCH-2102 U1a), re-exported beside
 * the pointer lanes for the same reason: the backend resolver, the backfill and
 * the dashboard all spell these states, and one import path keeps them spelling
 * them the same way.
 */
export type {
  FirebaseAllocationDecision,
  FirebaseBindingCheckStatus,
  FirebaseBindingLifecycleState,
  FirebaseBindingSource,
  FirebaseEnvironment,
  MobileAppFirebaseProject
} from '../../schemas/entities/mobile-app-firebase-project'
export {
  FIREBASE_ALLOCATION_DECISIONS,
  FIREBASE_BINDING_CHECK_STATUSES,
  FIREBASE_BINDING_LIFECYCLE_STATES,
  FIREBASE_BINDING_SOURCES,
  FIREBASE_ENVIRONMENTS,
  FirebaseAllocationDecisionSchema,
  FirebaseBindingCheckStatusSchema,
  FirebaseBindingLifecycleStateSchema,
  FirebaseBindingSourceSchema,
  FirebaseEnvironmentSchema,
  MobileAppFirebaseProjectSchema
} from '../../schemas/entities/mobile-app-firebase-project'

/** Listing, optionally narrowed to one vendor. */
export const ListPublishingAccountsQuerySchema = z.object({
  vendor: PublishingAccountVendorSchema.optional()
})
export type ListPublishingAccountsQuery = z.infer<typeof ListPublishingAccountsQuerySchema>

export const ListPublishingAccountsResponseSchema = z.object({
  data: z.array(PublishingAccountReadinessSchema),
  message: z.string()
})
export type ListPublishingAccountsResponse = z.infer<typeof ListPublishingAccountsResponseSchema>

export const GetPublishingAccountResponseSchema = z.object({
  data: PublishingAccountReadinessSchema,
  message: z.string()
})
export type GetPublishingAccountResponse = z.infer<typeof GetPublishingAccountResponseSchema>

/**
 * Creating an account.
 *
 * `vendor` and `accountId` are the natural key and are the ONLY required fields.
 * Ownership and kind are optional and default to `unanswered` at the column,
 * never guessed (KD9) — a create that omits them is stating that nobody has
 * answered yet, which is a real state and not an omission to be filled in.
 */
export const CreatePublishingAccountRequestSchema = z.object({
  vendor: PublishingAccountVendorSchema,
  /**
   * The vendor's own identifier — OR a `pending-<schoolPublicId>` placeholder
   * for an account the vendor has not issued yet (R20, KTD5). The placeholder is
   * a real value on a real row, not a blank to be filled: recording the account
   * from the moment work on it starts is what gives every app under it somewhere
   * to point and something to explain an empty lane with.
   *
   * `placeholderRejected` still applies and is a DIFFERENT concept — it rejects
   * junk text (TODO/TBD/FIXME/empty), which `pending-<cuid>` is not.
   */
  accountId: placeholderRejected('accountId'),
  displayName: z.string().nullish(),
  ownership: PublishingAccountOwnershipSchema.optional(),
  kind: PublishingAccountKindSchema.optional(),
  /**
   * Optional and defaulted at the column to `awaiting_account` (R23). A create
   * that omits it is stating that nothing has been observed yet, which is a real
   * state — never `api_ready` because a credential happens to exist.
   */
  provisioningState: PublishingAccountProvisioningStateSchema.optional(),
  ownerContactName: z.string().nullish(),
  ownerContactEmail: z.email({ message: 'ownerContactEmail must be an email address' }).nullish()
})
export type CreatePublishingAccountRequest = z.infer<typeof CreatePublishingAccountRequestSchema>

/**
 * Updating an account.
 *
 * `vendor` and `accountId` are ABSENT by construction, not by omission: the
 * natural key is immutable after create, because apps reach the account by that
 * identifier copied onto the app row with no foreign key to cascade. Moving an
 * app between accounts is a repoint of the app, not an edit here.
 */
export const UpdatePublishingAccountRequestSchema = z
  .object({
    displayName: z.string().nullish(),
    ownership: PublishingAccountOwnershipSchema.optional(),
    kind: PublishingAccountKindSchema.optional(),
    /** Moves the provisioning axis as an observation is made (R21, R23). */
    provisioningState: PublishingAccountProvisioningStateSchema.optional(),
    ownerContactName: z.string().nullish(),
    ownerContactEmail: z.email({ message: 'ownerContactEmail must be an email address' }).nullish()
  })
  .refine(body => Object.keys(body).length > 0, { message: 'An update must name at least one field to change' })
export type UpdatePublishingAccountRequest = z.infer<typeof UpdatePublishingAccountRequestSchema>

export const PublishingAccountResponseSchema = z.object({
  data: PublishingAccountSchema,
  message: z.string()
})
export type PublishingAccountResponse = z.infer<typeof PublishingAccountResponseSchema>

/**
 * Refusing or un-refusing an account (R9, KD8).
 *
 * `refusedBy` is required and non-empty: this is the incident lever, and a
 * refusal nobody can attribute is one nobody can unwind. The internal
 * principal's `sub` is a constant, so the field is the only attribution this
 * surface has.
 *
 * `reason` IS REQUIRED, IN BOTH DIRECTIONS. The stored row carries only
 * `refusedBy` and `refusedAt`, and un-refusing clears both — so before this
 * field existed, the record of WHY minting was stopped lived nowhere, and the
 * record that it ever happened survived only until someone un-refused. The
 * operations timeline is what keeps both, and it can only keep what the request
 * supplies.
 *
 * Required on the un-refuse direction too, and that is the less obvious half:
 * restoring minting during an incident is the decision more likely to be
 * questioned afterwards, and "we believed the key was rotated" is the sentence
 * nobody reconstructs a week later. Making it optional there would make it
 * absent there, every time.
 */
export const SetPublishingAccountRefusalRequestSchema = z.object({
  isRefused: z.boolean(),
  refusedBy: placeholderRejected('refusedBy'),
  reason: placeholderRejected('reason').max(500)
})
export type SetPublishingAccountRefusalRequest = z.infer<typeof SetPublishingAccountRefusalRequestSchema>

/**
/**
 * WHAT THE RE-CHECK DID TO THE ROW, which is not the same as what it concluded.
 *
 * `superseded` is the value that had nowhere to go before this field existed. A
 * completed probe's write carries an ordering predicate, and a probe that
 * started earlier than one already recorded loses it (R30, AE11): the attempt
 * ran, it determined something, and the row kept a NEWER answer. That was
 * logged and nothing more, so the operator pressing re-check was shown the
 * other probe's state under a sentence claiming it as their own result.
 *
 * `not-attempted` is R3's structural hysteresis on the wire: a check that did
 * not complete writes no column at all.
 */
export const PublishingAccountApiAccessWriteSchema = z.enum(['written', 'superseded', 'not-attempted'])
export type PublishingAccountApiAccessWrite = z.infer<typeof PublishingAccountApiAccessWriteSchema>

/**
 * The refreshed verdict after an on-demand API-access re-check (R8).
 *
 * `outcome` IS THE LOAD-BEARING FIELD, not `data`. A probe that did not complete
 * writes no column (R3), so `data` comes back identical to what the caller
 * already had — same state, same `apiAccessObservedAt`, same verdict. This is
 * the only field that distinguishes "we asked and the answer is unchanged" from
 * "we could not ask", and a caller that renders `data` without reading it is
 * reporting a check that never happened as one that passed.
 *
 * `write` ANSWERS A DIFFERENT QUESTION FROM `outcome`, and the two are not
 * redundant: `outcome` is what THIS probe concluded, `write` is what happened to
 * the row. They part company on `superseded` — the probe determined something
 * and the row kept a newer answer — which is the case that used to be reported
 * as this check's own determination.
 *
 * `message` is the human sentence for the same facts and names the evidence. It
 * is for a person to read, never for a client to match on.
 */
export const RecheckPublishingAccountAgreementResponseSchema = z.object({
  data: PublishingAccountReadinessSchema,
  outcome: ApiAccessProbeOutcomeSchema,
  write: PublishingAccountApiAccessWriteSchema,
  message: z.string()
})
export type RecheckPublishingAccountAgreementResponse = z.infer<typeof RecheckPublishingAccountAgreementResponseSchema>

/**
 * `POST /v1/mobile-apps/publishing-accounts/check-for-school` (SCH-2930): re-check
 * the publishing accounts a school's apps publish under. The admin dialogs call it
 * when they open; an account checked in the last 15 minutes is skipped. A blocked
 * account files one Linear issue, never one per check.
 */
export const CheckSchoolPublishingAccountsRequestSchema = z.object({
  /**
   * The school's public id (the admin school payload) or its numeric id (the
   * mobile-app payload). The backend resolves either.
   */
  schoolId: z.string().trim().min(1).max(64)
})
export type CheckSchoolPublishingAccountsRequest = z.infer<typeof CheckSchoolPublishingAccountsRequestSchema>

export const ApiAccessCheckEntrySchema = z.object({
  publicId: z.string(),
  vendor: z.string(),
  accountId: z.string(),
  outcome: z.enum(['clear', 'blocked', 'check_failed', 'not_attempted', 'skipped_recent']),
  reason: z.string().nullable(),
  alert: z.enum(['not_blocked', 'already_filed', 'filed', 'failed']).nullable(),
  issueIdentifier: z.string().nullable()
})
export type ApiAccessCheckEntry = z.infer<typeof ApiAccessCheckEntrySchema>

export const CheckSchoolPublishingAccountsResponseSchema = z.object({
  data: z.object({
    checked: z.number().int(),
    blocked: z.number().int(),
    filed: z.number().int(),
    entries: z.array(ApiAccessCheckEntrySchema)
  })
})
export type CheckSchoolPublishingAccountsResponse = z.infer<typeof CheckSchoolPublishingAccountsResponseSchema>

// ==========================================
// Store-rejection remedy (U7)
// ==========================================

/**
 * Recording what caused a rejection and what fixed it (R13).
 *
 * The rejection is addressed by its `correlationId` — the one id shared by the
 * recorded rejection, the ticket filed for it and the remedy that closes the
 * loop (KTD1) — and never by a ticket key or an event public id, because both
 * of those make the write depend on a lookup that can fail while the rejection
 * itself is sitting right there in the list.
 *
 * `remedy` IS REQUIRED and is free text on purpose. The cause class is the
 * countable half (R15, R17); the remedy is the half no taxonomy can hold —
 * "sent the school's rights letter to Google" is a sentence, not an enum, and
 * it is the sentence the next operator needs before the next submission (R18).
 *
 * `resubmitted` is a FACT ABOUT THE ATTEMPT, not a request to reopen anything.
 * A rejected target stays terminal and a resubmission is a further attempt
 * (R12, Key Decisions), so this records that another attempt went out and
 * creates no release rows whatsoever (S6).
 */
export const RecordRejectionRemedyRequestSchema = z.object({
  causeClass: StoreRejectionCauseClassSchema,
  remedy: placeholderRejected('remedy').max(1000),
  resubmitted: z.boolean()
})
export type RecordRejectionRemedyRequest = z.infer<typeof RecordRejectionRemedyRequestSchema>

/**
 * `recorded` is `false` when a remedy was ALREADY on record for this rejection.
 *
 * Not an error, and not a second event: the spine is append-only and the write
 * dedupes on the correlation id, so the first remedy recorded is the one that
 * stands. The flag exists so the dialog can say "someone already recorded this"
 * rather than implying it just wrote what the operator typed.
 */
export const RecordRejectionRemedyResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    correlationId: z.string(),
    causeClass: StoreRejectionCauseClassSchema,
    resubmitted: z.boolean(),
    recorded: z.boolean()
  })
})
export type RecordRejectionRemedyResponse = z.infer<typeof RecordRejectionRemedyResponseSchema>

/**
 * One rejection still waiting for its remedy (R14, R24).
 *
 * Deliberately NOT an `AttentionTarget`. That shape requires a `release` and a
 * `mobileApp` and closes `status` to `BLOCKED` / `REFUSED`, while a rejection
 * with no release target at all is the EXPECTED case here — release targets
 * exist for 2 of 19 apps and have never held a blocked row, which is exactly
 * why the rejection record lives on the event spine instead (Key Decisions,
 * R11). Squeezing these rows into that table would mean fabricating a release
 * or dropping the rejections the plan says will occur.
 *
 * Nearly every field is nullable, and each null is a real state rather than a
 * gap worth hiding: a rejection the platform could not attribute to a school
 * (R22), a Linear filing that failed and left no ticket, an Apple rejection
 * whose reported cause is a fixed constant carrying no information.
 */
/**
 * The school behind a rejection, as the rejection readers name it.
 *
 * `name` is nullable because `School.name` is: a school row identified by only
 * half a name-and-id pair still has to render, and an empty string would read
 * as a school with no name rather than as one we could not fully identify. The
 * awaiting-remedy row deliberately does NOT use this shape — that reader drops
 * a half-identified school to `null` outright, so its `name` is a guarantee
 * rather than a possibility, and widening it here would loosen that contract.
 */
const RejectionSchoolRefSchema = z.object({ publicId: z.string(), name: z.string().nullable() }).nullable()

/** The Linear ticket raised for a rejection (R11), or `null` when filing never landed one. */
const RejectionTicketRefSchema = z.object({ identifier: z.string(), url: z.string() }).nullable()

export const RejectionAwaitingRemedySchema = z.object({
  /** The join key for everything about this rejection, and what the dialog opens on. */
  correlationId: z.string(),
  /** `null` when neither the rejection nor its filing recorded a storefront. */
  store: StoreRejectionStoreSchema.nullable(),
  /** ISO-8601. When the STORE rejected it, not when we stored the row. */
  occurredAt: z.string(),
  mobileApp: z.object({ publicId: z.string(), name: z.string().nullable() }).nullable(),
  school: z.object({ publicId: z.string(), name: z.string() }).nullable(),
  /** Whatever the store reported. Apple's is a fixed constant, not a description. */
  reportedCause: z.string().nullable(),
  /** The Linear ticket raised for it (R11), or `null` when filing never landed one. */
  ticket: RejectionTicketRefSchema
})
export type RejectionAwaitingRemedy = z.infer<typeof RejectionAwaitingRemedySchema>

export const ListRejectionsAwaitingRemedyResponseSchema = z.object({
  message: z.string(),
  data: z.array(RejectionAwaitingRemedySchema),
  /**
   * How many rows this response carries — the LENGTH of `data`, never a count
   * of everything outstanding.
   *
   * Read it together with {@link ListRejectionsAwaitingRemedyResponseSchema.shape.truncated}:
   * when that is `true`, the window holds outstanding rejections this page does
   * not show, and `total` is a floor rather than a total.
   */
  total: z.number(),
  /**
   * `true` when the read hit its page ceiling and older outstanding rejections
   * exist behind this page.
   *
   * The list is ordered newest-first, so what a truncated page omits is the
   * rejections that have been stuck LONGEST — the ones most worth seeing. A
   * reader that cannot tell a clipped page from a complete one silently tells
   * an operator the queue is shorter than it is.
   */
  truncated: z.boolean()
})
export type ListRejectionsAwaitingRemedyResponse = z.infer<typeof ListRejectionsAwaitingRemedyResponseSchema>

// ==========================================
// Store-rejection statistics (U8)
// ==========================================

/**
 * The default and the ceiling for the read-back window, in days.
 *
 * The spine retains 730 days, and this read deliberately refuses to span it.
 * Every query behind these counts is an anti-join bounded by `occurredAt`, and
 * the bound is the only thing keeping a fleet-wide scan proportional to a
 * submission cadence measured in days rather than to two years of app history.
 *
 * A `days` above the ceiling is CLAMPED rather than rejected: this value ends
 * up in a bookmarked URL and in links people paste at each other, and a 400 on
 * a stale bookmark teaches nobody anything the clamped answer does not.
 */
export const REJECTION_STATS_DEFAULT_WINDOW_DAYS = 180
export const REJECTION_STATS_MAX_WINDOW_DAYS = 365

/**
 * Cause counts, plus the outstanding bucket AS A SIBLING KEY (R24).
 *
 * `awaiting_remedy` is not a cause and never joins the union — a rejection has
 * a cause the moment the store reports it and keeps that cause after it is
 * fixed, so admitting the state to the cause list would make every cause count
 * depend on whether the fix had landed yet. Keyed off the union rather than
 * spelled out, so subdividing a class (R17) updates this shape in the same edit
 * and cannot leave a class that is counted but not representable.
 *
 * The keys are exhaustive and the values sum to the window's rejection total:
 * every recorded rejection either has a remedy carrying a class, or is still
 * awaiting one.
 */
export const RejectionCauseCountKeySchema = z.enum([
  ...STORE_REJECTION_CAUSE_CLASSES,
  STORE_REJECTION_OUTSTANDING_BUCKET
])
export type RejectionCauseCountKey = z.infer<typeof RejectionCauseCountKeySchema>

export const RejectionCauseCountsSchema = z.record(RejectionCauseCountKeySchema, z.number().int())
export type RejectionCauseCounts = z.infer<typeof RejectionCauseCountsSchema>

/**
 * The rejection rate for one store (R16, AE8).
 *
 * `approved` and `rate` are NULLABLE because Play reports no approval at all —
 * Google's developer alerts say when something is wrong and stay silent when it
 * is not, so no denominator exists on that lane and inventing one would put a
 * number beside Apple's that means something entirely different. A `null` rate
 * is the honest reading of "this store does not tell us", and it is distinct
 * from `0`, which means "nothing was rejected out of submissions we saw".
 *
 * `rate` is the share of DECIDED submissions that were rejected —
 * `rejected / (rejected + approved)` — so AE8's four rejections against six
 * approvals reads as 0.4. `null` when the window decided nothing at all.
 */
export const RejectionStoreRateSchema = z.object({
  rejected: z.number().int(),
  approved: z.number().int().nullable(),
  rate: z.number().nullable()
})
export type RejectionStoreRate = z.infer<typeof RejectionStoreRateSchema>

/**
 * A rejection whose `#app-operations` announcement never landed (D6, R9).
 *
 * The failure this reports is SILENT on every other surface: the bot is not a
 * member of the private channel, Slack accepts the call, the ticket is filed
 * and assigned exactly as it should be, and nobody is told. The ticket key is
 * carried because it is what a human quotes when they go looking for the work
 * that was announced to no one.
 */
export const RejectionFailedAnnouncementSchema = z.object({
  correlationId: z.string(),
  occurredAt: z.string(),
  store: StoreRejectionStoreSchema.nullable(),
  ticket: RejectionTicketRefSchema,
  school: RejectionSchoolRefSchema,
  /** Slack's own failure string — `not_in_channel` and `channel_not_found` are D6's two shapes. */
  slackError: z.string().nullable()
})
export type RejectionFailedAnnouncement = z.infer<typeof RejectionFailedAnnouncementSchema>

/**
 * The Play lane's received-versus-classified pair.
 *
 * THE POINT OF THE PAIR is that a parser which has never seen a real Google
 * alert cannot report its own wrongness. `received` alerts with `classified: 0`
 * is the visible form of the extraction believing the wrong format, and it is
 * indistinguishable from "no Play rejections happened" unless both numbers are
 * shown side by side.
 *
 * - `received`   inbound mail classified as a Play alert
 * - `untrusted`  mail that looked like Play but failed the sender-trust check
 *                and was demoted to an ad-hoc finding — it never reached the
 *                Play handler, so it can never be classified
 * - `failedUnclassified`
 *                inbound mail whose typed handling FAILED, of any type — see
 *                below for why it cannot be narrowed to Play
 * - `classified` recorded rejections carrying `store: 'google'`
 *
 * ## Why the failure count is not Play-specific
 *
 * The classify job records `classifiedType` only on the SUCCESS branch; its
 * failure branch writes `status: 'FAILED'` and the error and nothing else. So a
 * Play alert whose handler failed leaves a row that carries no type at all, and
 * is indistinguishable on the stored columns from any other typed handler's
 * failure. Counting `classifiedType = 'play' AND status = 'FAILED'` — the
 * obvious predicate — therefore matches NOTHING and reports a permanent zero
 * beside a real backlog. The count is named for what it actually measures
 * instead. Narrowing it means recording the type on the failure branch first.
 */
export const PlayLaneCountsSchema = z.object({
  received: z.number().int(),
  untrusted: z.number().int(),
  /** ⚠️ ALL failed inbound mail, not only Play's — see the type docblock. */
  failedUnclassified: z.number().int(),
  classified: z.number().int()
})
export type PlayLaneCounts = z.infer<typeof PlayLaneCountsSchema>

/** One school with at least one rejection in the window — the selector's options. */
export const RejectionStatsSchoolSchema = z.object({
  publicId: z.string(),
  name: z.string().nullable(),
  rejected: z.number().int()
})
export type RejectionStatsSchool = z.infer<typeof RejectionStatsSchoolSchema>

/**
 * What previously caused rejections, and what is still open (R15, R16, R17,
 * R18, R24).
 *
 * Everything here is DERIVED from the operations-event spine at read time and
 * stored nowhere, which is what lets the answer stay correct across a later
 * subdivision of the cause union: a class recorded months ago resolves through
 * `storeRejectionCauseClassFor`, so counts taken across the change still add up
 * (AE9) instead of splitting into a column nobody backfilled.
 *
 * `school` is `null` for the fleet view. A per-school view resolves through the
 * app rollup the events carry, which means rejections the platform could not
 * attribute to any school (R22) appear in the fleet counts and in no school's —
 * correctly, since they belong to no school until someone attributes them.
 */
export const RejectionStatsSchema = z.object({
  window: z.object({
    days: z.number().int(),
    /** ISO-8601. Both bounds are echoed so a reader can tell a clamped window from the one they asked for. */
    since: z.string(),
    until: z.string()
  }),
  school: RejectionSchoolRefSchema,
  /** Distinct rejections in the window, however they were later classified. */
  rejected: z.number().int(),
  counts: RejectionCauseCountsSchema,
  byStore: z.object({
    apple: z.number().int(),
    google: z.number().int(),
    /** Neither the rejection nor its filing named a storefront. */
    unknown: z.number().int()
  }),
  rate: z.object({ apple: RejectionStoreRateSchema, google: RejectionStoreRateSchema }),
  failedAnnouncements: z.array(RejectionFailedAnnouncementSchema),
  /**
   * The Play lane pair — FLEET ONLY. `null` in a per-school response.
   *
   * Three of the four counts are over `InboundMessage`, and nothing ever writes
   * that table's `schoolId`: the persist path does not set it and the classify
   * job never fills it in. A per-school filter on that column therefore matches
   * no row ever written, and would report a confident `0` for every school —
   * which reads as "no Play alerts arrived" rather than as "we do not record
   * which school a Play alert belongs to".
   *
   * The whole block is nulled rather than the three mail counts alone, because
   * the pair is the unit: `classified` on its own cannot distinguish a quiet
   * quarter from a parser that has stopped understanding Google's format, which
   * is the only thing these numbers exist to show.
   */
  playLane: PlayLaneCountsSchema.nullable(),
  /**
   * `true` when at least one aggregate behind these numbers hit its scan
   * ceiling, so every count here is a FLOOR rather than a total.
   *
   * The window reaches a year and the reads are bounded per event type. Without
   * this flag a clipped scan renders as a smaller, entirely plausible set of
   * counts — and a rejection rate computed from two independently clipped
   * halves is not merely low, it is arbitrary.
   */
  truncated: z.boolean(),
  /** Schools with a rejection in the window. In a per-school response, that school alone. */
  schools: z.array(RejectionStatsSchoolSchema)
})
export type RejectionStats = z.infer<typeof RejectionStatsSchema>

export const GetRejectionStatsResponseSchema = z.object({
  message: z.string(),
  data: RejectionStatsSchema
})
export type GetRejectionStatsResponse = z.infer<typeof GetRejectionStatsResponseSchema>

// ---------------------------------------------------------------------------
// App Store Connect webhooks (SCH-1294): status, register, ping, fleet sweep.
//
// One handler set behind two gates, like publishing accounts (KD7): the
// dashboard reaches status/register/ping through `/v1/mobile-apps/:id/asc-webhook…`
// under Clerk, automation through `/internal/mobile-apps/:publicId/asc-webhook…`
// under an API key. The sweep is internal-only. `cannot_check` is DATA on every
// one of these — a 200 carrying a reason — never a 5xx, so one team without a
// key cannot hide every other app's answer.
// ---------------------------------------------------------------------------

export { AscWebhookCoverageCountsSchema } from '../../schemas/entities/publishing-account'
export type { AscWebhookCoverageCounts } from '../../schemas/entities/publishing-account'

/** Apple's own view of the registration, when the action asked Apple. `secret` is never returned by Apple. */
export const AscWebhookRegistrationSchema = z.object({
  webhookId: z.string(),
  url: z.string(),
  enabled: z.boolean(),
  eventTypes: z.array(z.string())
})
export type AscWebhookRegistration = z.infer<typeof AscWebhookRegistrationSchema>

/** The most recent verified delivery of any type for the app — what the UI shows as "last delivery". */
export const AscWebhookLastDeliverySchema = z.object({
  /** `WebhookDelivery.publicId`. */
  id: z.string(),
  eventTypeRaw: z.string(),
  status: z.string(),
  /** ISO-8601. */
  receivedAt: z.string()
})
export type AscWebhookLastDelivery = z.infer<typeof AscWebhookLastDeliverySchema>

export const AscWebhookStatusSchema = z.object({
  /** `MobileApp.publicId`. */
  appId: z.string(),
  readiness: AscWebhookReadinessSchema,
  /** Present only when the status action reached Apple and found the app's registration. */
  registration: AscWebhookRegistrationSchema.nullable(),
  lastDelivery: AscWebhookLastDeliverySchema.nullable(),
  /** ISO-8601 of the latest `webhookPingCreated` delivery resolved to this app, if any. */
  lastPingAt: z.string().nullable()
})
export type AscWebhookStatus = z.infer<typeof AscWebhookStatusSchema>

export const GetAscWebhookStatusResponseSchema = z.object({
  data: AscWebhookStatusSchema,
  message: z.string()
})
export type GetAscWebhookStatusResponse = z.infer<typeof GetAscWebhookStatusResponseSchema>

/**
 * The outcome of one register call. `cannot_check` carries the same reason
 * vocabulary readiness uses, so the button and the badge cannot disagree.
 */
export const RegisterAscWebhookOutcomeSchema = z.discriminatedUnion('outcome', [
  z.object({
    outcome: z.literal('registered'),
    webhookId: z.string(),
    url: z.string(),
    /** True when an existing registration for this app's URL path was updated rather than created. */
    reused: z.boolean()
  }),
  z.object({ outcome: z.literal('cannot_check'), reason: AscWebhookCannotCheckReasonSchema })
])
export type RegisterAscWebhookOutcome = z.infer<typeof RegisterAscWebhookOutcomeSchema>

export const RegisterAscWebhookResponseSchema = z.object({
  data: RegisterAscWebhookOutcomeSchema,
  message: z.string()
})
export type RegisterAscWebhookResponse = z.infer<typeof RegisterAscWebhookResponseSchema>

/**
 * The outcome of one test ping. `sent_not_received` is truthful, not a failure:
 * Apple accepted the ping and no matching delivery landed inside the bounded
 * wait. A later readiness read shows `registered_pinged` once it does.
 */
export const PingAscWebhookOutcomeSchema = z.discriminatedUnion('outcome', [
  z.object({
    outcome: z.literal('received'),
    webhookId: z.string(),
    /** `WebhookDelivery.publicId` of the matched `webhookPingCreated` row. */
    deliveryId: z.string(),
    /** ISO-8601. */
    receivedAt: z.string()
  }),
  z.object({ outcome: z.literal('sent_not_received'), webhookId: z.string(), waitedMs: z.number().int() }),
  z.object({ outcome: z.literal('not_registered') }),
  z.object({ outcome: z.literal('cannot_check'), reason: AscWebhookCannotCheckReasonSchema })
])
export type PingAscWebhookOutcome = z.infer<typeof PingAscWebhookOutcomeSchema>

export const PingAscWebhookResponseSchema = z.object({
  data: PingAscWebhookOutcomeSchema,
  message: z.string()
})
export type PingAscWebhookResponse = z.infer<typeof PingAscWebhookResponseSchema>

/** Fleet sweep body. Mirrors the ASC audit: omit `schools` for every active app. */
export const AscWebhookSweepBodySchema = z.object({
  schools: z.array(z.string().min(1)).max(100).optional()
})
export type AscWebhookSweepBody = z.infer<typeof AscWebhookSweepBodySchema>

export const AscWebhookSweepRowSchema = z.object({
  /** `MobileApp.publicId`. */
  appId: z.string(),
  /** School slug, else app name, else public id — what an operator reads. */
  label: z.string(),
  action: z.enum(['created', 'updated', 'skipped', 'failed']),
  readiness: AscWebhookReadinessSchema,
  /** Why the app was skipped or failed; null on created/updated. */
  reason: z.string().nullable()
})
export type AscWebhookSweepRow = z.infer<typeof AscWebhookSweepRowSchema>

export const AscWebhookSweepResponseSchema = z.object({
  data: z.object({
    appsConsidered: z.number().int().nonnegative(),
    rows: z.array(AscWebhookSweepRowSchema)
  }),
  message: z.string()
})
export type AscWebhookSweepResponse = z.infer<typeof AscWebhookSweepResponseSchema>

/**
 * App Store version probe and release action (SCH-1319).
 *
 * `POST /v1/mobile-apps/:id/app-store-version/refresh` asks Apple what it holds
 * right now; `POST /v1/mobile-apps/:id/app-store-version/actions` acts on that
 * answer. Both are mirrored under `/internal` behind the provisioning-vault key.
 */

/**
 * Apple's per-version review state and release type now live in
 * `schemas/entities/mobile-deployment`, beside the store-version mapping that
 * has to key on them (an entity module may not import an API module). They are
 * re-exported from this path, which is where every existing caller imports them
 * from — the move is a relocation, not a contract change.
 */

/**
 * What Apple told us about the version, whenever Apple returned one (R1).
 *
 * `createdDate` is when Apple created the VERSION RECORD — it is not an
 * approval time, and no approval time is available from this endpoint. Nothing
 * downstream may rename it `approvedAt` or render it as one.
 */
const appStoreVersionFacts = {
  appStoreVersionId: z.string().min(1),
  versionString: z.string().min(1),
  appVersionState: AscAppVersionStateSchema,
  /** Apple omits this on some versions; null means "Apple did not say". */
  releaseType: AscReleaseTypeSchema.nullable(),
  /** ISO-8601. Apple's version-record creation time, NOT an approval time. */
  createdDate: z.string().nullable()
}

/**
 * Why a returned version cannot be released, or why there is no version at all.
 * `NO_VERSION` is the ONLY reason that carries null version metadata — every
 * other refusal describes a version Apple actually returned.
 */
export const APP_STORE_VERSION_NOT_RELEASABLE_REASONS = ['NO_VERSION', 'VERSION_STATE'] as const
export const AppStoreVersionNotReleasableReasonSchema = z.enum(APP_STORE_VERSION_NOT_RELEASABLE_REASONS)
export type AppStoreVersionNotReleasableReason = z.infer<typeof AppStoreVersionNotReleasableReasonSchema>

/**
 * Why the probe could not reach a determination. Distinct codes so U2 can tell
 * an unfiled key from a dead one, and so the dialog can say what to do about it
 * — never merged into an absence (R3).
 */
export const APP_STORE_VERSION_UNREADABLE_CODES = [
  /** The app carries no Apple team, ASC app id, or bundle id to anchor on (R13, KTD8). */
  'NO_APPLE_IDENTIFIERS',
  /** No App Store Connect key is filed for the app's Apple team. */
  'NO_KEY',
  /** A key is filed and Apple refused it — it needs rotating, not filing. */
  'KEYS_DEAD',
  /** Apple answered, but the resolved key cannot see this app (404 / not visible). */
  'APP_NOT_VISIBLE',
  /** Transport failure, 5xx, or a rate limit — Apple did not answer at all. */
  'PROVIDER_ERROR'
] as const
export const AppStoreVersionUnreadableCodeSchema = z.enum(APP_STORE_VERSION_UNREADABLE_CODES)
export type AppStoreVersionUnreadableCode = z.infer<typeof AppStoreVersionUnreadableCodeSchema>

/** Apple is holding an approved version for a developer release (KTD3). */
export const ReleasableAppStoreVersionVerdictSchema = z.object({
  kind: z.literal('releasable'),
  ...appStoreVersionFacts
})
export type ReleasableAppStoreVersionVerdict = z.infer<typeof ReleasableAppStoreVersionVerdictSchema>

/**
 * A determination that there is nothing to release. Split on `reason` so the
 * versionless case is the ONLY one that can carry null metadata: a version
 * Apple returned always arrives with the full R1 facts.
 */
export const NotReleasableAppStoreVersionVerdictSchema = z.discriminatedUnion('reason', [
  z.object({
    kind: z.literal('not-releasable'),
    reason: z.literal('NO_VERSION'),
    appStoreVersionId: z.null(),
    versionString: z.null(),
    appVersionState: z.null(),
    releaseType: z.null(),
    createdDate: z.null()
  }),
  z.object({
    kind: z.literal('not-releasable'),
    reason: z.literal('VERSION_STATE'),
    ...appStoreVersionFacts
  })
])
export type NotReleasableAppStoreVersionVerdict = z.infer<typeof NotReleasableAppStoreVersionVerdictSchema>

/**
 * The probe failed. Strict, and carrying NO version fields at all — an
 * `unreadable` that could hold `appStoreVersionId: null` is exactly the shape
 * that lets a failed probe be read as "no version" (R3).
 */
export const UnreadableAppStoreVersionVerdictSchema = z.strictObject({
  kind: z.literal('unreadable'),
  code: AppStoreVersionUnreadableCodeSchema,
  /** Human-readable, already through `scrubProviderMessage` — never raw provider text. */
  reason: z.string().min(1)
})
export type UnreadableAppStoreVersionVerdict = z.infer<typeof UnreadableAppStoreVersionVerdictSchema>

/**
 * The three-arm verdict (KTD3, R2, R3). A failed probe, an app with no version,
 * and a version that is not releasable are three distinct answers — a nullable
 * version shape is what would let the first read as the second.
 */
export const AppStoreVersionVerdictSchema = z.discriminatedUnion('kind', [
  ReleasableAppStoreVersionVerdictSchema,
  NotReleasableAppStoreVersionVerdictSchema,
  UnreadableAppStoreVersionVerdictSchema
])
export type AppStoreVersionVerdict = z.infer<typeof AppStoreVersionVerdictSchema>

/**
 * `POST /v1/mobile-apps/:id/app-store-version/refresh` (KTD2). Every outcome is
 * persisted on the app's timeline before this is returned.
 */
export const RefreshAppStoreVersionResponseSchema = z.object({
  data: z.object({
    /** `MobileApp.publicId`. */
    appId: z.string(),
    verdict: AppStoreVersionVerdictSchema,
    /** ISO-8601 — when we asked Apple, so the dialog can say how fresh this is. */
    checkedAt: z.string()
  }),
  message: z.string()
})
export type RefreshAppStoreVersionResponse = z.infer<typeof RefreshAppStoreVersionResponseSchema>

/**
 * Whether a store-registration read reached a determination (`GET /v1/mobile-apps/:id/store-identifiers`).
 *
 * `checked` — the store answered, and the lists below are its answer (an empty list is "not there").
 * `not_configured` — the registry row lacks what the check needs (team, account, identifier); no
 * store was asked. `unreadable` — a key or the store failed; the lists are empty and mean nothing.
 */
export const StoreIdentifiersCheckStatusSchema = z.enum(['checked', 'not_configured', 'unreadable'])
export type StoreIdentifiersCheckStatus = z.infer<typeof StoreIdentifiersCheckStatusSchema>

/** One bundle id this app needs at Apple, and whether the team has it registered. */
export const AppleBundleIdRegistrationSchema = z.object({
  identifier: z.string(),
  /**
   * `app` is the main bundle id; `notification_extension` is the OneSignal NSE one;
   * `share_extension` is the Expo share extension's (`<bundleId>.expo-sharing-extension`).
   */
  role: z.enum(['app', 'notification_extension', 'share_extension']),
  registered: z.boolean(),
  /** Apple's bundle-id resource id, when registered. */
  resourceId: z.string().nullable(),
  name: z.string().nullable(),
  platform: z.string().nullable()
})
export type AppleBundleIdRegistration = z.infer<typeof AppleBundleIdRegistrationSchema>

export const AppleStoreIdentifiersSchema = z.object({
  status: StoreIdentifiersCheckStatusSchema,
  reason: z.string().nullable(),
  teamId: z.string().nullable(),
  bundleIds: z.array(AppleBundleIdRegistrationSchema),
  /** The App Store Connect app record; null when checked and absent, or when not checked. */
  app: z
    .object({
      ascAppId: z.string(),
      name: z.string(),
      bundleId: z.string(),
      sku: z.string().nullable()
    })
    .nullable()
})
export type AppleStoreIdentifiers = z.infer<typeof AppleStoreIdentifiersSchema>

export const GoogleStoreIdentifiersSchema = z.object({
  status: StoreIdentifiersCheckStatusSchema,
  reason: z.string().nullable(),
  accountId: z.string().nullable(),
  packageName: z.string().nullable(),
  /** The Play app for this package; null when checked and Play answered "Package not found". */
  app: z
    .object({
      packageName: z.string(),
      /** From Play Developer Reporting, best effort — null when that API is unavailable. */
      displayName: z.string().nullable()
    })
    .nullable()
})
export type GoogleStoreIdentifiers = z.infer<typeof GoogleStoreIdentifiersSchema>

export const GetStoreIdentifiersResponseSchema = z.object({
  data: z.object({
    appId: z.string(),
    apple: AppleStoreIdentifiersSchema,
    google: GoogleStoreIdentifiersSchema,
    /** ISO-8601 — when the stores were asked. */
    checkedAt: z.string()
  }),
  message: z.string()
})
export type GetStoreIdentifiersResponse = z.infer<typeof GetStoreIdentifiersResponseSchema>

/**
 * Android setup checklist ROWS the dashboard can run — not one-to-one with browser-runner step
 * ids. `play.listing-api` and `play.graphics-api` are performed by the backend over the Play
 * Developer API; `play.shell` is one runner step; `play.app-content` is SEVERAL, run in one
 * window (see `ANDROID_SETUP_STEPS` in the backend for the mapping).
 *
 * `play.app-content` replaced the old `play.privacy-policy` row. Every section under Play's "App
 * content" — privacy policy, sign-in details, ads, content rating, target audience, data safety,
 * government apps, financial features, health — is browser-only: `androidpublisher` reaches the
 * edit surface (listings, details, images, tracks) and has no endpoint for any of these
 * declarations. Since they all live behind one console page and one login, working them one row
 * at a time meant one browser window per section for no gain.
 */
/**
 * What one "generate store assets" run produced.
 *
 * `failed` is part of the SUCCESS shape on purpose: the run commits each asset independently, so a
 * partial result is the normal outcome of a transient upload failure and the operator has to be
 * told which slots are still empty.
 */
export const GeneratedStoreAssetSchema = z.object({
  slotName: z.string(),
  width: z.number(),
  height: z.number(),
  bytes: z.number()
})
export type GeneratedStoreAsset = z.infer<typeof GeneratedStoreAssetSchema>

export const GenerateStoreAssetsResponseSchema = z.object({
  generated: z.array(GeneratedStoreAssetSchema),
  failed: z.array(z.object({ slotName: z.string(), reason: z.string() })),
  /** What the render used, so the operator can see why the art looks the way it does. */
  brand: z.object({
    displayName: z.string(),
    primary: z.string(),
    usedLogo: z.boolean()
  }),
  /**
   * The roll that produced this run's sample names, feed posts and portraits.
   *
   * Reported so a listing that looks wrong can be traced to the data behind it: the same seed
   * always produces the same screenshots.
   */
  seed: z.number()
})
export type GenerateStoreAssetsResponse = z.infer<typeof GenerateStoreAssetsResponseSchema>

export const ANDROID_SETUP_STEP_IDS = [
  'play.shell',
  'play.listing-api',
  'play.graphics-api',
  'play.app-content',
  'play.store-settings',
  'play.countries',
  'play.permission-declarations',
  'play.first-release',
  'play.managed-publishing',
  'play.publish-changes'
] as const
export const AndroidSetupStepIdSchema = z.enum(ANDROID_SETUP_STEP_IDS)
export type AndroidSetupStepId = z.infer<typeof AndroidSetupStepIdSchema>

/**
 * A step's most recent run on the local backend host.
 *
 * `starting` means the dispatch exists but the runner has not reported a single step yet — the
 * process is launching, and no browser window has opened. `running` means it has reported at
 * least once, so something is genuinely being driven. They are separate because collapsing them
 * hid every launch-time crash behind a healthy-looking "running" for the whole staleness window.
 * `needs_attention` means the runner finished but the step's read-back is not green; `failed`
 * means the run itself stopped — and for a crash at launch, `detail` now carries the runner's own
 * last line rather than nothing.
 */
export const AndroidSetupLastRunSchema = SetupLastRunSchema
export type AndroidSetupLastRun = z.infer<typeof AndroidSetupLastRunSchema>

/**
 * One Play Console section a checklist row drives.
 *
 * `automated` is the operator-facing half of the distinction the runner already enforces:
 * declarations are legal attestations, so `appContentStep`'s apply is deliberately inert for
 * content rating, ads and target audience ("the CLI never guesses on declarations") and the run
 * only reports what is still outstanding. Saying which is which on the card is the difference
 * between a row that finished and a row that finished YOUR half.
 */
export const AndroidSetupCoverageSchema = z.object({
  label: z.string(),
  automated: z.boolean()
})
export type AndroidSetupCoverage = z.infer<typeof AndroidSetupCoverageSchema>

export const AndroidSetupStepSchema = z.object({
  id: AndroidSetupStepIdSchema,
  title: z.string(),
  via: z.enum(['browser', 'api']),
  description: z.string(),
  /** The console sections this row covers. Empty for a row that is one thing. */
  covers: z.array(AndroidSetupCoverageSchema),
  lastRun: AndroidSetupLastRunSchema.nullable()
})
export type AndroidSetupStep = z.infer<typeof AndroidSetupStepSchema>

/** `GET /v1/mobile-apps/:id/android-setup` */
export const GetAndroidSetupResponseSchema = z.object({
  data: z.object({
    appId: z.string(),
    steps: z.array(AndroidSetupStepSchema),
    localBrowser: z.object({ available: z.boolean(), reason: z.string().nullable() })
  }),
  message: z.string()
})
export type GetAndroidSetupResponse = z.infer<typeof GetAndroidSetupResponseSchema>

/**
 * `POST /v1/mobile-apps/:id/android-setup/:stepId/run` — local dev only. Starts the step in a
 * headful Playwright window on the backend host; poll `GET …/android-setup` for its outcome.
 */
export const RunAndroidSetupStepResponseSchema = z.object({
  data: z.object({
    appId: z.string(),
    stepId: AndroidSetupStepIdSchema,
    /** The dispatch this run reports to — how to follow it everywhere else. */
    launchId: z.string(),
    /** Null for a hosted run; see `AndroidSetupLastRunSchema.logPath`. */
    logPath: z.string().nullable()
  }),
  message: z.string()
})
export type RunAndroidSetupStepResponse = z.infer<typeof RunAndroidSetupStepResponseSchema>

/**
 * A Play version name as Play Console prints it (`1.9.1`, `1.9.1 (412)`). The characters are closed
 * because the value rides a CLI argument and is matched against console text.
 */
export const PlayVersionNameSchema = z
  .string()
  .min(1)
  .max(32)
  .regex(/^[0-9A-Za-z.\-+ ()]+$/)

/**
 * `POST /v1/mobile-apps/:id/android-setup/:stepId/run` body. Optional as a whole: every row but
 * `play.publish-changes` runs with no body at all.
 */
export const RunAndroidSetupStepRequestSchema = z
  .object({
    /**
     * `play.publish-changes` only, and required there: the version name Publishing overview's
     * ready-to-publish list must name before the runner clicks Publish. It refuses otherwise, so a
     * click can never publish a change nobody asked for.
     */
    expectedVersion: PlayVersionNameSchema.optional()
  })
  .default({})
export type RunAndroidSetupStepRequest = z.infer<typeof RunAndroidSetupStepRequestSchema>

/**
 * The Apple setup checklist.
 *
 * The Apple counterpart of the Android rows above, and deliberately NOT a copy of them: Apple
 * exposes far more over its API than Play does, so most of this work is already automated in
 * `create-apple-identifiers` and needs no browser at all. What the rows must carry instead is
 * *why* a row cannot be run, because Apple's walls are permanent rather than unimplemented.
 */
export const APPLE_SETUP_STEP_IDS = [
  'apple.identifiers',
  'apple.asc-app',
  'apple.app-group',
  'apple.apns-key',
  // Everything from here to `apple.submit-review` is the STORE side of the app: the answers App
  // Store Connect refuses to let a version be submitted without. They are listed in the order the
  // console asks for them, and all but the privacy questionnaire are `api` rows — Apple exposes
  // this surface far better than Play does, so a browser is the exception here rather than the rule.
  'apple.app-info',
  'apple.age-rating',
  'apple.privacy',
  'apple.pricing',
  'apple.listing',
  'apple.screenshots',
  'apple.review-info',
  'apple.release-option',
  // Only an upgrade that asks for it runs this (`StartAppUpgradeRequest.resetRatings`): a first
  // setup has no ratings to reset, and Run full setup never reaches it.
  'apple.reset-ratings',
  'apple.submit-review',
  'apple.ios-cert'
] as const
export const AppleSetupStepIdSchema = z.enum(APPLE_SETUP_STEP_IDS)
export type AppleSetupStepId = z.infer<typeof AppleSetupStepIdSchema>

/**
 * How a row gets done — four lanes, not Android's two.
 *
 * `api` — the backend does it itself over the App Store Connect API. Runnable.
 * `browser` — no API exists, so the runner drives the portal. Runnable where a runner is reachable.
 * `manual` — **Apple refuses it to every credential we can hold.** `POST /v1/apps` answers
 *   `403 FORBIDDEN_ERROR … does not allow 'CREATE'` even with an ADMIN-role key, so the app record
 *   is an irreducible human pre-create. Never runnable; the row links out instead.
 * `delegated` — owned by another system on purpose. EAS mints and rotates the iOS distribution
 *   certificate during the first build, so there is nothing here to run and a Run button would
 *   imply we had taken that back.
 *
 * The distinction is the point of the table: `manual` and `delegated` are settled decisions, and an
 * operator who cannot tell them from "not built yet" goes looking for work that does not exist.
 */
export const AppleSetupViaSchema = z.enum(['api', 'browser', 'manual', 'delegated'])
export type AppleSetupVia = z.infer<typeof AppleSetupViaSchema>

/** Same four states as the Android rows — see `AndroidSetupLastRunSchema`. */
export const AppleSetupLastRunSchema = SetupLastRunSchema
export type AppleSetupLastRun = z.infer<typeof AppleSetupLastRunSchema>

export const AppleSetupStepSchema = z.object({
  id: AppleSetupStepIdSchema,
  title: z.string(),
  via: AppleSetupViaSchema,
  description: z.string(),
  /** Why this row cannot be run, for `manual` and `delegated`. Null when the row is runnable. */
  blockedReason: z.string().nullable(),
  /** Where the operator goes to do it by hand, for a `manual` row. */
  consoleUrl: z.string().nullable(),
  /**
   * The row needs an App Store Connect app record before it can do anything.
   *
   * Every store-metadata row does, because each one writes against an `appInfos` or
   * `appStoreVersions` resource that only exists once Apple holds the record. Carried on the row
   * rather than derived in the dashboard so the two surfaces cannot disagree about which rows wait.
   */
  requiresAscApp: z.boolean(),
  /**
   * The row makes an IRREVERSIBLE, externally-visible change and the dashboard must confirm before
   * running it. True for exactly one row today: submitting to App Review puts a real school's app
   * in front of Apple's reviewers, and withdrawing it starts the queue again from the back.
   */
  confirmBeforeRun: z.boolean(),
  covers: z.array(AndroidSetupCoverageSchema),
  lastRun: AppleSetupLastRunSchema.nullable()
})
export type AppleSetupStep = z.infer<typeof AppleSetupStepSchema>

/** `GET /v1/mobile-apps/:id/apple-setup` */
export const GetAppleSetupResponseSchema = z.object({
  data: z.object({
    appId: z.string(),
    steps: z.array(AppleSetupStepSchema),
    localBrowser: z.object({ available: z.boolean(), reason: z.string().nullable() })
  }),
  message: z.string()
})
export type GetAppleSetupResponse = z.infer<typeof GetAppleSetupResponseSchema>

/**
 * `POST /v1/mobile-apps/:id/apple-setup/:stepId/run`
 *
 * `launchId` is null for an `api` row, which the backend carries out in-process and has already
 * finished by the time this responds — there is no dispatch to follow.
 */
export const RunAppleSetupStepResponseSchema = z.object({
  data: z.object({
    appId: z.string(),
    stepId: AppleSetupStepIdSchema,
    launchId: z.string().nullable(),
    logPath: z.string().nullable()
  }),
  message: z.string()
})
export type RunAppleSetupStepResponse = z.infer<typeof RunAppleSetupStepResponseSchema>

/**
 * `POST /v1/mobile-apps/:id/apple-setup/:stepId/run` body, optional.
 *
 * `storeResubmission` (SCH-2593, provisional) opts a version row (listing, screenshots, review
 * information, release option, submit for review) into the four-part App Store version of an
 * asset-only rebuild: when the app's record for the fleet version is live, the row writes to
 * `1.9.1.1` (created if needed) while the build stays 1.9.1. Absent or false: today's behaviour.
 */
export const RunAppleSetupStepRequestSchema = z
  .object({ storeResubmission: z.boolean().optional() })
  .optional()
  .transform(body => body ?? {})
export type RunAppleSetupStepRequest = z.infer<typeof RunAppleSetupStepRequestSchema>

/**
 * The actions this route accepts today. The phased-release members
 * (`start_phased`, `pause_phased`, …) are deferred and land here without a new
 * route (KTD1).
 */
export const APP_STORE_VERSION_ACTIONS = ['release'] as const

export const ReleaseAppStoreVersionRequestSchema = z.object({
  action: z.literal('release'),
  /**
   * A PRECONDITION, never a source of truth (KTD4): the action re-probes and
   * refuses with `VERSION_CHANGED` when the version it resolves is not this
   * one, so consent is bound to a version rather than to a moment.
   */
  expectedAppStoreVersionId: z.string().min(1),
  /**
   * Required on the `/internal` mirror (R11) — the workflow run or operator the
   * caller acts for. The system principal's `sub` is a constant and attributes
   * nothing, so without this an unattended release is anonymous.
   */
  callerId: z.string().min(1).optional()
})
export type ReleaseAppStoreVersionRequest = z.infer<typeof ReleaseAppStoreVersionRequestSchema>

/** One route, one union on `action` — every member shares the legality guard (KTD1). */
export const AppStoreVersionActionRequestSchema = z.discriminatedUnion(
  'action',
  [ReleaseAppStoreVersionRequestSchema],
  { error: () => `action must be one of: ${APP_STORE_VERSION_ACTIONS.join(', ')}` }
)
export type AppStoreVersionActionRequest = z.infer<typeof AppStoreVersionActionRequestSchema>

/**
 * What the action did. Refusals carry the verdict that produced them so the
 * dialog can render Apple's own state without a second probe, and every one of
 * them is recorded on the timeline before it is returned (R10).
 *
 * `released` means Apple ACCEPTED the release request — not that the app is
 * live. The webhook lane's `submission.released` is what says that (KTD6).
 */
export const AppStoreVersionActionOutcomeSchema = z.discriminatedUnion('outcome', [
  z.object({
    outcome: z.literal('released'),
    /** The version Apple was actually asked to release — the probed id, not the caller's. */
    appStoreVersionId: z.string().min(1),
    /** Apple's `appStoreVersionReleaseRequests` resource id. */
    releaseRequestId: z.string().min(1)
  }),
  z.object({
    outcome: z.literal('rejected_by_apple'),
    appStoreVersionId: z.string().min(1),
    /** Scrubbed — no key id, URL, or email ever reaches this field. */
    reason: z.string().min(1)
  }),
  z.object({
    outcome: z.literal('VERSION_CHANGED'),
    /** What the caller confirmed; the verdict carries what Apple now holds. */
    expectedAppStoreVersionId: z.string().min(1),
    verdict: AppStoreVersionVerdictSchema
  }),
  z.object({ outcome: z.literal('NOT_RELEASABLE'), verdict: AppStoreVersionVerdictSchema }),
  z.object({ outcome: z.literal('NO_VERSION'), verdict: AppStoreVersionVerdictSchema }),
  z.object({ outcome: z.literal('UNREADABLE'), verdict: AppStoreVersionVerdictSchema })
])
export type AppStoreVersionActionOutcome = z.infer<typeof AppStoreVersionActionOutcomeSchema>

export const AppStoreVersionActionResponseSchema = z.object({
  data: AppStoreVersionActionOutcomeSchema,
  message: z.string()
})
export type AppStoreVersionActionResponse = z.infer<typeof AppStoreVersionActionResponseSchema>

// ============================================================================
// Play production release (SCH-2920)
//
// Android's counterpart of "Release This Version". Play has no approved-and-waiting state, so the
// verb is "push the production release Play is holding back": a `draft`, or a staged `inProgress`
// rollout below 100%, moved to `completed`. Same shape as the App Store action: a fresh read of
// the production track decides, and the caller's versionCode and status are a precondition.
// ============================================================================

export const PLAY_RELEASE_UNREADABLE_CODES = [
  /** The app has no `playAccountId` or no `androidPackage`. */
  'NO_IDENTIFIERS',
  /** The Play account has no usable service-account key filed. */
  'NO_KEY',
  /** Today's shared edit quota is spent; reading the track would spend more. */
  'QUOTA_SPENT',
  /** Play answered, but the key cannot see this package. */
  'APP_NOT_VISIBLE',
  /** Transport failure, 5xx, or a rate limit. */
  'PROVIDER_ERROR'
] as const
export const PlayReleaseUnreadableCodeSchema = z.enum(PLAY_RELEASE_UNREADABLE_CODES)
export type PlayReleaseUnreadableCode = z.infer<typeof PlayReleaseUnreadableCodeSchema>

const playReleaseFacts = {
  versionCode: z.number().int(),
  /** The release name on the track (`"12 (1.9.0)"`), when it has one. */
  releaseName: z.string().nullable(),
  /** Play's status verbatim. */
  status: z.string(),
  rolloutFraction: z.number().nullable()
}

const NotReleasablePlayReleaseVerdictSchema = z.discriminatedUnion('reason', [
  z.object({ kind: z.literal('not-releasable'), reason: z.literal('NO_RELEASE') }),
  /** The newest production release is already out (`completed`), or `halted`. */
  z.object({ kind: z.literal('not-releasable'), reason: z.literal('RELEASE_STATE'), ...playReleaseFacts })
])

export const PlayReleaseVerdictSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('releasable'), ...playReleaseFacts, status: z.enum(['draft', 'inProgress']) }),
  NotReleasablePlayReleaseVerdictSchema,
  z.strictObject({
    kind: z.literal('unreadable'),
    code: PlayReleaseUnreadableCodeSchema,
    /** Already scrubbed — never raw provider text. */
    reason: z.string().min(1)
  })
])
export type PlayReleaseVerdict = z.infer<typeof PlayReleaseVerdictSchema>

/** `POST /v1/mobile-apps/:id/play-release/refresh`. Reads the production track; writes nothing to Play. */
export const RefreshPlayReleaseResponseSchema = z.object({
  data: z.object({
    appId: z.string(),
    verdict: PlayReleaseVerdictSchema,
    checkedAt: z.string()
  }),
  message: z.string()
})
export type RefreshPlayReleaseResponse = z.infer<typeof RefreshPlayReleaseResponseSchema>

export const ReleasePlayVersionRequestSchema = z.object({
  action: z.literal('release'),
  /** PRECONDITION: the action re-reads the track and refuses with `VERSION_CHANGED` on any drift. */
  expectedVersionCode: z.number().int(),
  expectedStatus: z.enum(['draft', 'inProgress']),
  callerId: z.string().min(1).optional()
})
export type ReleasePlayVersionRequest = z.infer<typeof ReleasePlayVersionRequestSchema>

export const PlayReleaseActionRequestSchema = z.discriminatedUnion('action', [ReleasePlayVersionRequestSchema], {
  error: () => 'action must be one of: release'
})
export type PlayReleaseActionRequest = z.infer<typeof PlayReleaseActionRequestSchema>

/**
 * `released` means Play ACCEPTED the commit: the release is `completed` on the production track and
 * sent for review where review is still due. It does not mean the app is live.
 */
export const PlayReleaseActionOutcomeSchema = z.discriminatedUnion('outcome', [
  z.object({ outcome: z.literal('released'), versionCode: z.number().int() }),
  z.object({ outcome: z.literal('rejected_by_play'), versionCode: z.number().int(), reason: z.string().min(1) }),
  z.object({
    outcome: z.literal('VERSION_CHANGED'),
    expectedVersionCode: z.number().int(),
    expectedStatus: z.enum(['draft', 'inProgress']),
    verdict: PlayReleaseVerdictSchema
  }),
  z.object({ outcome: z.literal('NOT_RELEASABLE'), verdict: PlayReleaseVerdictSchema }),
  z.object({ outcome: z.literal('NO_RELEASE'), verdict: PlayReleaseVerdictSchema }),
  z.object({ outcome: z.literal('UNREADABLE'), verdict: PlayReleaseVerdictSchema })
])
export type PlayReleaseActionOutcome = z.infer<typeof PlayReleaseActionOutcomeSchema>

export const PlayReleaseActionResponseSchema = z.object({
  data: PlayReleaseActionOutcomeSchema,
  message: z.string()
})
export type PlayReleaseActionResponse = z.infer<typeof PlayReleaseActionResponseSchema>

// ============================================================================
// OneSignal app ensure (SCH-1511)
//
// One idempotent verb over the app, its per-app REST key, and its two push
// channels. It is the SAME work the `create-onesignal-app` provisioning step
// does — exposed so a school provisioned before the orchestrator existed, or
// one whose key never landed, can be brought to the same end state without a
// full run. Re-running it on a healthy app changes nothing and reports `ok`.
// ============================================================================

/**
 * What we did to one push channel.
 *
 * `already` means OneSignal itself reports the channel configured, which is the
 * only source of truth for it — the registry holds no channel columns. A channel
 * a human configured in the dashboard reads `already`, so a school set up by
 * hand is not re-uploaded over and does not read as broken.
 */
export const OneSignalChannelOutcomeSchema = z.enum(['already', 'configured', 'missing'])
export type OneSignalChannelOutcome = z.infer<typeof OneSignalChannelOutcomeSchema>

export const EnsureOneSignalAppOutcomeSchema = z.discriminatedUnion('verdict', [
  z.object({
    /** `ok` — nothing needed doing. `changed` — the app, the key or a channel moved. */
    verdict: z.enum(['ok', 'changed']),
    oneSignalAppId: z.string(),
    /** True when an existing OneSignal app was adopted rather than created. */
    adopted: z.boolean(),
    /**
     * True when a fresh per-app REST key was minted and persisted. OneSignal
     * vends token material exactly once and never re-vends it, so a key that is
     * absent or no longer authenticates can only be replaced, never read back.
     */
    keyMinted: z.boolean(),
    ios: OneSignalChannelOutcomeSchema,
    android: OneSignalChannelOutcomeSchema
  }),
  z.object({
    verdict: z.literal('blocked'),
    /** Null when the block happened before an app could be resolved at all. */
    oneSignalAppId: z.string().nullable(),
    reason: z.object({ type: z.string(), message: z.string() })
  })
])
export type EnsureOneSignalAppOutcome = z.infer<typeof EnsureOneSignalAppOutcomeSchema>

/**
 * What a "register the Firebase apps" run produced.
 *
 * `blocked` is a verdict, not an error: the Firebase PROJECT cannot be created by the fleet service
 * account, so "a human must make the project" is the most likely outcome for a new school and has
 * to arrive as something the dashboard can render rather than as a failure.
 *
 * The config file CONTENTS are deliberately absent — they are build inputs, and this response is
 * read by a browser.
 */
export const EnsureFirebaseAppsOutcomeSchema = z.object({
  verdict: z.enum(['ok', 'blocked', 'error']),
  detail: z.string(),
  projectId: z.string().nullable(),
  androidAppId: z.string().nullable(),
  iosAppId: z.string().nullable(),
  hasGoogleServicesJson: z.boolean(),
  hasGoogleServiceInfoPlist: z.boolean(),
  /**
   * Whether both files were written into the app's EAS environments as the
   * `GOOGLE_SERVICES_JSON` / `GOOGLE_SERVICES_INFO_PLIST` file variables the build reads.
   * Optional so an older backend's response still parses; `detail` says why when false.
   */
  filedToEas: z.boolean().optional()
})
export type EnsureFirebaseAppsOutcome = z.infer<typeof EnsureFirebaseAppsOutcomeSchema>

export const EnsureFirebaseAppsResponseSchema = z.object({
  data: EnsureFirebaseAppsOutcomeSchema,
  message: z.string()
})
export type EnsureFirebaseAppsResponse = z.infer<typeof EnsureFirebaseAppsResponseSchema>

export const EnsureOneSignalAppResponseSchema = z.object({
  data: EnsureOneSignalAppOutcomeSchema,
  message: z.string()
})
export type EnsureOneSignalAppResponse = z.infer<typeof EnsureOneSignalAppResponseSchema>

// ============================================================================
// Expo project — create the app's EAS project from the dashboard
//
// The same work the `create-eas-project` provisioning step does, for one app
// that already has a registry row but no `expoProjectId`. A row that already
// holds a project id is left alone: relinking a live app to another project
// would orphan its builds, so that stays a by-hand edit.
// ============================================================================

/** Optional: the slug to create under. Falls back to the row's stored `expoSlug`. */
export const CreateExpoProjectRequestSchema = z.object({
  slug: z
    .string()
    .trim()
    .regex(/^[a-z0-9][a-z0-9-]*$/, 'Expo slug must be lowercase letters, digits and hyphens')
    .optional()
})
export type CreateExpoProjectRequest = z.infer<typeof CreateExpoProjectRequestSchema>

export const CreateExpoProjectOutcomeSchema = z.object({
  /**
   * `created` — a new EAS project was made and linked. `adopted` — a project
   * with this slug already existed under the account and was linked.
   * `already_linked` — the row already held a project id; nothing was done.
   */
  verdict: z.enum(['created', 'adopted', 'already_linked']),
  expoProjectId: z.string(),
  expoSlug: z.string().nullable(),
  /** `@account/slug`. Null for `already_linked`, which does not read Expo. */
  fullName: z.string().nullable(),
  /**
   * The row's `schoolAppId` and `buildProfiles` after the link. A link fills either one when it
   * is blank (`0018.<slug>`, `<variant>-<slug>`), as the provisioning write-back does. Absent for
   * `already_linked`.
   */
  schoolAppId: z.string().nullable().optional(),
  buildProfiles: BuildProfilesSchema.nullable().optional(),
  /**
   * Follow-up work that failed after the project was linked: the convention fill, or registering
   * the project's build and submit webhooks. The link itself stands.
   */
  warnings: z.array(z.string()).optional()
})
export type CreateExpoProjectOutcome = z.infer<typeof CreateExpoProjectOutcomeSchema>

export const CreateExpoProjectResponseSchema = z.object({
  data: CreateExpoProjectOutcomeSchema,
  message: z.string()
})
export type CreateExpoProjectResponse = z.infer<typeof CreateExpoProjectResponseSchema>

// ---------------------------------------------------------------------------
// Expo activity — the app's own builds and store submissions, read from EAS
// ---------------------------------------------------------------------------

/**
 * One EAS build, as the Expo section of the app dialog shows it.
 *
 * A PROJECTION of `interfaces/expo`'s `ExpoBuild`, not a mirror: the raw row carries queue metrics,
 * actor ids and an internal-distribution archive URL that an operator reading "what did we last
 * build" has no use for. Every field is nullable because EAS itself leaves them unset on a build
 * that never got far enough to have them (a queued build has no version, a cancelled one has no
 * artifact).
 *
 * `status` and `platform` stay raw strings rather than enums: they are Expo's vocabulary, and a
 * new member arriving upstream must render as itself rather than fail the response parse.
 */
export const ExpoBuildSummarySchema = z.object({
  id: z.string(),
  /** Deep link into expo.dev for this build. Null when the project slug is unknown. */
  url: z.string().nullable(),
  status: z.string(),
  platform: z.string(),
  profile: z.string().nullable(),
  channel: z.string().nullable(),
  distribution: z.string().nullable(),
  appVersion: z.string().nullable(),
  appBuildVersion: z.string().nullable(),
  runtimeVersion: z.string().nullable(),
  gitCommitHash: z.string().nullable(),
  gitCommitMessage: z.string().nullable(),
  createdAt: z.string(),
  completedAt: z.string().nullable(),
  /** The installable artifact, when the build produced one. */
  artifactUrl: z.string().nullable(),
  initiatedBy: z.string().nullable(),
  /** Expo's own failure text, verbatim — the reason a red row is red. */
  error: z.string().nullable()
})
export type ExpoBuildSummary = z.infer<typeof ExpoBuildSummarySchema>

/** One EAS submission to App Store Connect or Google Play. Same projection rules as the build. */
export const ExpoSubmissionSummarySchema = z.object({
  id: z.string(),
  url: z.string().nullable(),
  status: z.string(),
  platform: z.string(),
  createdAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  /** The build this submission uploaded, when EAS reports one. */
  appVersion: z.string().nullable(),
  appBuildVersion: z.string().nullable(),
  /** Play only: the track it went to, and how it was released there. */
  track: z.string().nullable(),
  releaseStatus: z.string().nullable(),
  rollout: z.number().nullable(),
  error: z.string().nullable()
})
export type ExpoSubmissionSummary = z.infer<typeof ExpoSubmissionSummarySchema>

/**
 * One lane of the Expo read.
 *
 * `error` is a STRING BESIDE THE ROWS, not a thrown failure: builds and submissions are two
 * independent EAS queries, and one of them failing is not a reason to blank the other. An empty
 * `rows` with a null `error` means Expo answered and this app has nothing of that kind yet — a
 * fact about the app, which is what the panel says.
 */
export const ExpoActivityLaneSchema = <T extends z.ZodTypeAny>(row: T) =>
  z.object({ rows: z.array(row), error: z.string().nullable() })

export const ExpoBuildLaneSchema = ExpoActivityLaneSchema(ExpoBuildSummarySchema)
export const ExpoSubmissionLaneSchema = ExpoActivityLaneSchema(ExpoSubmissionSummarySchema)

// ---------------------------------------------------------------------------
// Direct store submission — hand an already-built EAS artifact to a store
// ---------------------------------------------------------------------------

/**
 * Play's own track names, plus the escape hatch.
 *
 * `internal` leads because it is the one track a mistake is cheap on: it reaches only the testers
 * on the internal list and needs no review. A custom closed-testing track is named by the operator,
 * which is what `z.string()` is doing at the end of the union rather than an enum.
 */
export const PLAY_TRACKS = ['internal', 'alpha', 'beta', 'production'] as const
export type PlayTrack = (typeof PLAY_TRACKS)[number]

/**
 * Play's release-status vocabulary, verbatim.
 *
 * `draft` uploads the binary and leaves it unreleased in the console for a human to roll out —
 * the safe default for `production`. `inProgress` is the only status that takes a rollout share.
 */
export const PLAY_RELEASE_STATUSES = ['draft', 'completed', 'inProgress', 'halted'] as const
export type PlayReleaseStatus = (typeof PLAY_RELEASE_STATUSES)[number]

export const MOBILE_APP_STORE_SUBMISSION_STATUSES = [
  'QUEUED',
  'DOWNLOADING',
  'UPLOADING',
  'COMMITTING',
  'SUCCEEDED',
  'FAILED'
] as const
export const MobileAppStoreSubmissionStatusSchema = z.enum(MOBILE_APP_STORE_SUBMISSION_STATUSES)
export type MobileAppStoreSubmissionStatus = z.infer<typeof MobileAppStoreSubmissionStatusSchema>

/**
 * Why a submission failed, split into the stage it failed at and what that stage said.
 *
 * `stage` is the half an operator acts on: "the artifact could not be downloaded" is our problem
 * or Expo's, "Play refused the bundle" is the store's, and a single flattened message makes those
 * two look like the same class of failure.
 */
export const MobileAppStoreSubmissionErrorSchema = z.object({
  stage: z.enum(['download', 'credential', 'upload']),
  message: z.string()
})
export type MobileAppStoreSubmissionError = z.infer<typeof MobileAppStoreSubmissionErrorSchema>

/** One attempt to hand an already-built EAS artifact to a store. */
export const MobileAppStoreSubmissionSchema = z.object({
  id: z.string(),
  platform: z.enum(['IOS', 'ANDROID']),
  easBuildId: z.string(),
  artifactKind: z.enum(['AAB', 'APK']),
  track: z.string(),
  releaseStatus: z.string(),
  rolloutFraction: z.number().nullable(),
  /** What Play assigned the upload. Null until the upload succeeds. */
  versionCode: z.string().nullable(),
  versionName: z.string().nullable(),
  status: MobileAppStoreSubmissionStatusSchema,
  error: MobileAppStoreSubmissionErrorSchema.nullable(),
  createdAt: z.string(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable()
})
export type MobileAppStoreSubmission = z.infer<typeof MobileAppStoreSubmissionSchema>

/**
 * `POST /v1/mobile-apps/:id/expo/builds/:buildId/submit`
 *
 * ANDROID ONLY, and `platform` is absent on purpose rather than defaulted: our backend cannot
 * upload to App Store Connect (Apple requires Transporter, not a REST endpoint), so accepting a
 * platform we would refuse would be an API promising something it cannot do.
 */
export const SubmitBuildToPlayRequestSchema = z
  .object({
    track: z.union([z.enum(PLAY_TRACKS), z.string().min(1).max(64)]),
    releaseStatus: z.enum(PLAY_RELEASE_STATUSES),
    /**
     * Play's own fraction (0–1), exclusive to `inProgress`. Refused elsewhere rather than ignored:
     * silently dropping it would tell an operator they staged a 10% rollout when they shipped to
     * everyone.
     */
    rolloutFraction: z.number().gt(0).lte(1).optional(),
    releaseNotes: z.string().max(500).optional()
  })
  .refine(body => body.releaseStatus === 'inProgress' || body.rolloutFraction === undefined, {
    message: 'rolloutFraction applies only to an inProgress release',
    path: ['rolloutFraction']
  })
  .refine(body => body.releaseStatus !== 'inProgress' || body.rolloutFraction !== undefined, {
    message: 'an inProgress release needs a rolloutFraction',
    path: ['rolloutFraction']
  })
export type SubmitBuildToPlayRequest = z.infer<typeof SubmitBuildToPlayRequestSchema>

/**
 * `POST /v1/mobile-apps/:id/expo/builds/:buildId/submit-ios`
 *
 * EMPTY BY DESIGN. The Play request carries a track, a release status and a rollout fraction
 * because we drive that upload ourselves. EAS Submit takes none of them: it hands the build to App
 * Store Connect, where the release decisions belong to the version, not to the upload. The app's
 * `ascAppId` supplies the only value EAS requires, so there is nothing left for a caller to choose.
 */
export const SubmitBuildToAppStoreRequestSchema = z.object({})
export type SubmitBuildToAppStoreRequest = z.infer<typeof SubmitBuildToAppStoreRequestSchema>

export const SubmitBuildToAppStoreResponseSchema = z.object({
  data: z.object({
    /**
     * EAS's own submission id — the record the Expo panel already lists and polls.
     *
     * Null on the `github-actions` lane: there, the backend dispatches a workflow and the
     * submission is created minutes later by eas-cli on the runner, so no id exists yet. The
     * Expo panel picks it up on its next read either way.
     */
    submissionId: z.string().nullable(),
    status: z.string(),
    /**
     * How the submission was started. `eas-api` means this backend called Expo directly with its
     * own EXPO_TOKEN; `github-actions` means it dispatched the submit workflow, which holds the
     * token instead. The lane is reported because it changes what an operator watches next.
     */
    lane: z.enum(['eas-api', 'github-actions'])
  }),
  message: z.string()
})
export type SubmitBuildToAppStoreResponse = z.infer<typeof SubmitBuildToAppStoreResponseSchema>

export const SubmitBuildToPlayResponseSchema = z.object({
  data: MobileAppStoreSubmissionSchema,
  message: z.string()
})
export type SubmitBuildToPlayResponse = z.infer<typeof SubmitBuildToPlayResponseSchema>

/**
 * `GET /v1/mobile-apps/:id/expo/activity`
 *
 * `submissions` is what EAS SUBMIT did; `playSubmissions` is what WE did — the direct uploads
 * started from this dashboard, which EAS knows nothing about. Two lists rather than one merged
 * feed because they answer different questions and fail differently: an EAS submission going wrong
 * is Expo's to explain, one of ours is ours.
 */
export const GetExpoActivityResponseSchema = z.object({
  data: z.object({
    appId: z.string(),
    /** The app's `expoProjectId`. Null when the row has none, which is itself the answer. */
    projectId: z.string().nullable(),
    /** The project page on expo.dev, when the slug is known. */
    projectUrl: z.string().nullable(),
    /**
     * When the EAS read behind `builds` and `submissions` was taken (ISO). The backend reuses one
     * read per app for a short window instead of calling EAS on every poll, so this can be up to a
     * minute old; a build the EAS webhook moved since then already carries its newer status. Null
     * when EAS was not read at all. Optional so a browser ahead of the API still parses.
     */
    easReadAt: z.string().nullable().optional(),
    builds: ExpoBuildLaneSchema,
    submissions: ExpoSubmissionLaneSchema,
    /** Submissions this dashboard started, newest first. */
    playSubmissions: z.array(MobileAppStoreSubmissionSchema),
    /**
     * Whether those submission records are actually persisted.
     *
     * False while `MobileAppStoreSubmission` is authored but not yet promoted: the backend keeps
     * them in process memory so the feature can be exercised, and they do not survive a restart.
     * Reported rather than hidden — a row silently vanishing is worse than a line saying it will.
     * Remove this field with the fallback it describes.
     */
    playSubmissionsDurable: z.boolean(),
    /**
     * Whether this app can be submitted to Play from here at all: it needs an Android package and
     * a linked Play publishing account. Reported so the button can say WHY it is disabled rather
     * than failing on click.
     */
    playSubmitReady: z.object({ ready: z.boolean(), reason: z.string().nullable() }),
    /**
     * Whether a finished iOS build can be handed to App Store Connect from here: the app needs an
     * Expo project and an `ascAppId`, and the backend needs an EXPO_TOKEN. Reported for the same
     * reason as the Play one — so the button can say WHY it is disabled rather than failing on click.
     */
    iosSubmitReady: z.object({ ready: z.boolean(), reason: z.string().nullable() })
  }),
  message: z.string()
})
export type GetExpoActivityResponse = z.infer<typeof GetExpoActivityResponseSchema>

// ---------------------------------------------------------------------------
// EAS environment variables (Expo tab)
// ---------------------------------------------------------------------------

/**
 * The three EAS environments a build profile can select with its `environment`
 * field. Lowercase here and uppercased at the Expo GraphQL boundary, because
 * that is the casing every other surface in this repo uses for them
 * (`eas.json`, `interfaces/eas-cli`'s `EasEnvironment`, the provisioning step).
 */
export const EasEnvironmentSchema = z.enum(['development', 'preview', 'production'])
export type EasEnvironment = z.infer<typeof EasEnvironmentSchema>

/** Expo's own visibility ladder. `secret` values are never readable back, by design. */
export const EasEnvVarVisibilitySchema = z.enum(['public', 'sensitive', 'secret'])
export type EasEnvVarVisibility = z.infer<typeof EasEnvVarVisibilitySchema>

/**
 * What the registry expects a variable to be, versus what EAS actually holds.
 *
 * `unreadable` is not a hedge, it is Expo's contract: a `sensitive` or `secret`
 * variable comes back with a null value, so "does it match the registry?" has no
 * answer from outside. Saying so is the honest report — reporting it as `match`
 * would claim a comparison nobody performed, and as `drift` would flag every
 * correctly-set secret.
 */
export const EasEnvVarStatusSchema = z.enum(['match', 'drift', 'missing', 'unreadable'])
export type EasEnvVarStatus = z.infer<typeof EasEnvVarStatusSchema>

export const EasEnvVarStateSchema = z.object({
  name: z.string(),
  status: EasEnvVarStatusSchema,
  visibility: EasEnvVarVisibilitySchema,
  /** What the registry says this should be. Null for a platform-sourced secret. */
  expected: z.string().nullable(),
  /** What EAS holds, when it is readable at all. */
  actual: z.string().nullable(),
  /**
   * True when the variable is account-wide (Expo `scope: SHARED`) rather than set on
   * this project. Shared variables are NOT ours to overwrite — a push targets the
   * project scope, which then shadows the shared one for this app only.
   */
  shared: z.boolean()
})
export type EasEnvVarState = z.infer<typeof EasEnvVarStateSchema>

export const EasEnvEnvironmentStateSchema = z.object({
  environment: EasEnvironmentSchema,
  vars: z.array(EasEnvVarStateSchema),
  missingCount: z.number().int(),
  driftCount: z.number().int()
})
export type EasEnvEnvironmentState = z.infer<typeof EasEnvEnvironmentStateSchema>

export const GetEasEnvResponseSchema = z.object({
  data: z.object({
    appId: z.string(),
    /** The app's `expoProjectId`. Null ⇒ nothing to read, which is itself the finding. */
    projectId: z.string().nullable(),
    environments: z.array(EasEnvEnvironmentStateSchema),
    /**
     * Registry fields that have no value to push, by variable name. A row missing its
     * `bundleId` cannot seed `BASE_BUNDLE_ID`, and the fix is on the registry row, not here.
     */
    unresolved: z.array(z.string())
  }),
  message: z.string()
})
export type GetEasEnvResponse = z.infer<typeof GetEasEnvResponseSchema>

/**
 * Push registry values into EAS.
 *
 * `includeDrift` is opt-in for a reason: creating a missing variable cannot destroy
 * a value anybody chose, while overwriting a drifted one can. The dashboard asks for
 * the second explicitly.
 */
export const PopulateEasEnvRequestSchema = z.object({
  environments: z.array(EasEnvironmentSchema).min(1).default(['development', 'preview', 'production']),
  includeDrift: z.boolean().default(false)
})
export type PopulateEasEnvRequest = z.infer<typeof PopulateEasEnvRequestSchema>

export const PopulateEasEnvResponseSchema = z.object({
  data: z.object({
    created: z.array(z.object({ environment: EasEnvironmentSchema, name: z.string() })),
    updated: z.array(z.object({ environment: EasEnvironmentSchema, name: z.string() })),
    skipped: z.array(z.object({ environment: EasEnvironmentSchema, name: z.string(), reason: z.string() })),
    failed: z.array(z.object({ environment: EasEnvironmentSchema, name: z.string(), message: z.string() }))
  }),
  message: z.string()
})
export type PopulateEasEnvResponse = z.infer<typeof PopulateEasEnvResponseSchema>

// ---------------------------------------------------------------------------
// iOS submit credentials (the App Store Connect key EAS Submit uses)
// ---------------------------------------------------------------------------

/**
 * WHY THIS EXISTS BESIDE THE ENVIRONMENT PANEL. `eas submit --non-interactive` cannot prompt, so
 * a project whose iOS credentials carry no `appStoreConnectApiKeyForSubmissions` fails the submit
 * outright — and the readiness check for `submit-ios` reads registry columns only, so the
 * dashboard can call an app ready while EAS holds no key for it. Same failure shape as a missing
 * EAS variable: invisible from here until a store run is already spent.
 *
 * The bind is a POINTER, not a key upload. Every key listed already exists in the Expo account;
 * binding one attaches it to this app's iOS credentials. No private material passes through this
 * API in either direction.
 */
export const EasSubmitKeySchema = z.object({
  /** EAS's own UUID for the key — the value the bind takes. */
  easKeyId: z.string(),
  /** Apple's 10-character key id, as shown in App Store Connect. */
  keyIdentifier: z.string(),
  issuerIdentifier: z.string(),
  /** Operator-facing name in EAS. Nullable there, so nullable here. */
  name: z.string().nullable(),
  createdAt: z.string().nullable()
})
export type EasSubmitKey = z.infer<typeof EasSubmitKeySchema>

/**
 * Four states, and the distinction between the last two is the whole point of the panel.
 *
 * - `bound`: a key is attached; submits can run.
 * - `bindable`: no key attached, but the account holds at least one for this app's Apple team —
 *   one button press away.
 * - `no-candidate-key`: no key attached and none on file for the team. The fix is in App Store
 *   Connect and `eas credentials`, not here.
 * - `no-credentials`: EAS has no iOS credentials for this bundle id at all, which means no build
 *   has ever provisioned them.
 */
export const EasSubmitKeyStatusSchema = z.enum(['bound', 'bindable', 'no-candidate-key', 'no-credentials'])
export type EasSubmitKeyStatus = z.infer<typeof EasSubmitKeyStatusSchema>

export const GetEasSubmitKeyResponseSchema = z.object({
  data: z.object({
    appId: z.string(),
    /** The app's `expoProjectId`. Null ⇒ nothing to read, which is itself the finding. */
    projectId: z.string().nullable(),
    /** The registry row's `bundleId` — the one bundle a store submit uses. */
    bundleId: z.string().nullable(),
    /** The registry row's `appleTeamId`; candidate keys are filtered to it. */
    appleTeamId: z.string().nullable(),
    status: EasSubmitKeyStatusSchema,
    /** EAS's iOS-credentials id for `bundleId`. The subject of a bind; null when none exist. */
    iosCredentialsId: z.string().nullable(),
    /** The key currently bound for submissions, when one is. */
    boundKey: EasSubmitKeySchema.nullable(),
    /** Every account key on this app's Apple team, newest first. Any one may be bound. */
    candidateKeys: z.array(EasSubmitKeySchema)
  }),
  message: z.string()
})
export type GetEasSubmitKeyResponse = z.infer<typeof GetEasSubmitKeyResponseSchema>

/**
 * Bind one existing account key to this app's iOS credentials.
 *
 * `easKeyId` is optional: omitted, the backend binds the newest candidate on the app's Apple team,
 * which is the one-button case the panel offers. Passing it explicitly is how an operator picks a
 * different key from the list.
 */
export const BindEasSubmitKeyRequestSchema = z.object({
  easKeyId: z.string().min(1).optional()
})
export type BindEasSubmitKeyRequest = z.infer<typeof BindEasSubmitKeyRequestSchema>

export const BindEasSubmitKeyResponseSchema = z.object({
  data: z.object({
    iosCredentialsId: z.string(),
    boundKey: EasSubmitKeySchema
  }),
  message: z.string()
})
export type BindEasSubmitKeyResponse = z.infer<typeof BindEasSubmitKeyResponseSchema>

// ---------------------------------------------------------------------------
// App upgrade
// ---------------------------------------------------------------------------

/**
 * Upgrade one school's app to the latest mobile version — the "Upgrade" tab under Setup.
 *
 * GET  /v1/mobile-apps/:id/upgrade?version=   (super-admin) where the app stands, what it would move to
 * POST /v1/mobile-apps/:id/upgrade            (super-admin) start the upgrade drive
 *
 * An upgrade is a setup-graph drive (SCH-2236) with three differences from "Run full setup":
 *
 * - the target is the newest live release line, and the ref is that line's latest version tag
 *   (`mobile/v<x.y.z>`), else the line's branch — chosen by the release plan (SCH-2563), the same
 *   way "Release on line" chooses it;
 * - the build and store-upload nodes are reset at the start, so the app's old builds do not count
 *   as done and both platforms are built and uploaded again at the target version — except where a
 *   platform already got part of the way (`AppUpgradeProgress`): a newest finished build of the
 *   target version, from the target's commit, is kept and only what follows it runs, and a store
 *   already holding the version in review or live has nothing run for it;
 * - on iOS the start makes the App Store version for the target (created, or the never-submitted
 *   open one renumbered) and resets App Review, so the new build is attached to that version and
 *   sent for review; on Android the production-track upload is sent to Google's review once
 *   managed publishing is on, so the approved update waits to be published;
 * - store submission is on by default — the point of an upgrade is a new binary in the stores.
 *
 * A step done before its current revision (the catalog marks what changed, such as App Review
 * notes that now need the call recording) runs again too, and so does a revised step never done
 * here (an app set up before the setup graph existed still ships under the changed step). Nothing
 * else does: the drive is scoped to those steps, so the live app's setup stands, and any other step
 * never done here (screenshots, the store listing, …) is run from its own tab before an upgrade or patch.
 */

/**
 * The nodes an upgrade resets, so the drive builds and uploads again even when an old build is done,
 * and sends the new iOS build to App Review on the version the start made.
 */
export const APP_UPGRADE_RESET_NODES = [
  'build.ios',
  'build.android',
  'apple.testflight',
  'apple.submit-review',
  'play.upload'
] as const satisfies readonly SetupGraphNodeId[]

/**
 * The steps an upgrade that goes to the stores runs before the Play upload: managed publishing
 * (SCH-2922), so the update Google approves waits to be published rather than going live by itself.
 * Reset only with store submission on, since without it nothing reaches Play. Inside the drive the
 * upload waits on them; their own first-setup dependency, the first release, is settled by then.
 */
export const APP_UPGRADE_PLAY_GATE_NODES = ['play.managed-publishing'] as const satisfies readonly SetupGraphNodeId[]

/**
 * Where an upgrade that goes to the stores ends Android, after its Play upload.
 * `play.permission-declarations` answers the declarations Play raises from the new bundle's manifest
 * (foreground service, full-screen intent, photo and video): Play asks them per bundle, so one
 * answered for an earlier release does not cover this one, and Play refuses the send until they are
 * answered (CM Knights 1.9.1, 2026-10-09: "You must let us know whether your app uses any Foreground
 * Service permissions"). `play.send-for-review` then sends the production release to Google's review,
 * as App Review ends iOS; elsewhere the catalog runs it only in a rejection remedy (`scopedOnly`).
 * Inside the drive both wait on the upload and the gates, and the send waits on the declarations.
 * Reset only with store submission on.
 */
export const APP_UPGRADE_PLAY_REVIEW_NODES = [
  'play.permission-declarations',
  'play.send-for-review'
] as const satisfies readonly SetupGraphNodeId[]

/**
 * The Apple steps that write to the App Store version. Inside an upgrade they write to the version
 * the start made (`SetupRunAll.storeVersion`); an upgrade without store submission makes none, so it
 * does not run them.
 */
export const APP_STORE_VERSION_NODES = [
  'apple.listing',
  'apple.screenshots',
  'apple.review-info',
  'apple.release-option',
  'apple.reset-ratings',
  'apple.submit-review'
] as const satisfies readonly SetupGraphNodeId[]

/** Where one platform of the app stands today: the last build the ledger recorded. */
export const AppUpgradeStandingSchema = z.object({
  platform: PlatformSchema,
  version: z.string().nullable(),
  buildNumber: z.string().nullable(),
  runtimeVersion: z.string().nullable(),
  /** The release line the app is on for this platform, when known. */
  lineName: z.string().nullable()
})
export type AppUpgradeStanding = z.infer<typeof AppUpgradeStandingSchema>

/**
 * How far one platform already got with the target version, which decides what the upgrade keeps
 * (a plain upgrade from the line's ref with store upload on; a patch, an explicit ref or an upgrade
 * without store upload always rebuilds):
 * - `rebuild` — nothing to keep: built, uploaded and sent to review again;
 * - `built` — the newest finished production build carries the target version from the target's
 *   commit (on iOS, one that went to App Store Connect): kept, and the store steps after it run;
 * - `uploaded` — Android only: Play also holds that build's versionCode, so only the send for review
 *   (behind managed publishing) runs;
 * - `in-review` — the store already holds the target version in review or live: nothing runs.
 */
export const AppUpgradeProgressStageSchema = z.enum(['rebuild', 'built', 'uploaded', 'in-review'])
export type AppUpgradeProgressStage = z.infer<typeof AppUpgradeProgressStageSchema>

export const AppUpgradeProgressSchema = z.object({
  platform: PlatformSchema,
  stage: AppUpgradeProgressStageSchema,
  /** The kept build's version and build number (versionCode on Android); null when nothing is kept. */
  version: z.string().nullable(),
  buildNumber: z.string().nullable(),
  /** One line the Upgrade tab shows: "Build kept: 1.9.1 build 9 already built." */
  detail: z.string()
})
export type AppUpgradeProgress = z.infer<typeof AppUpgradeProgressSchema>

/** The line and version the upgrade moves to. */
export const AppUpgradeTargetSchema = z.object({
  lineId: z.string(),
  lineName: z.string(),
  runtimeVersion: z.string(),
  /** How the release plan chose the ref, what version it ships, and the line's other tagged versions. */
  dispatchRef: LineReleaseDispatchRefSchema.nullable(),
  /** Set when the plan would not build from this line, and why. The upgrade refuses while it is set. */
  refusal: z.string().nullable()
})
export type AppUpgradeTarget = z.infer<typeof AppUpgradeTargetSchema>

/**
 * What the upgrade does with one setup node:
 * - `run` — not done yet; it runs (a step a newer version needs, such as the VoIP certificate);
 * - `rebuild` — reset by the upgrade and run again at the target version;
 * - `upgrade` — done before the node's current revision (the step changed since), or a revised node
 *   never done here; it runs;
 * - `done` — already done; kept.
 */
/**
 * What an upgrade does with a step: `rebuild` (reset: built, uploaded or submitted again),
 * `upgrade` (done before a revision of the step, or a revised step never done here, so it runs),
 * `done` (kept), `leave` (not done here, and not part of an upgrade: the app shipped without it, so it
 * is run from its own tab).
 */
export const AppUpgradeStepActionSchema = z.enum(['leave', 'rebuild', 'upgrade', 'done'])
export type AppUpgradeStepAction = z.infer<typeof AppUpgradeStepActionSchema>

/** A setup-graph node as the upgrade sees it: the node itself (the Upgrade tab draws it), and its action. */
export const AppUpgradeStepSchema = SetupGraphNodeSchema.extend({
  action: AppUpgradeStepActionSchema
})
export type AppUpgradeStep = z.infer<typeof AppUpgradeStepSchema>

export const GetAppUpgradePlanQuerySchema = z.object({
  /** A tagged version on the target line (`1.9.2`), or `branch` for the line's branch. Defaults to the latest tag. */
  version: LineReleaseVersionSchema.optional()
})
export type GetAppUpgradePlanQuery = z.infer<typeof GetAppUpgradePlanQuerySchema>

/**
 * What `apple.review-info` would file on the new App Store version, read without writing anything:
 * the same composer the step runs (`composeAppleReviewDetail`), fed the saved App Store listing as
 * the step's never-set fill would leave it. `notes` null means the step would refuse, and `missing`
 * says why; the text is never made up for the preview.
 */
export const AppleReviewNotesPreviewSchema = z.object({
  notes: z.string().nullable(),
  /** Why no notes would be filed (nothing saved and nothing to fill it with, a listing that does not parse). */
  missing: z.string().nullable(),
  /**
   * `saved` — the listing's notes as saved; `upgraded` — saved, with a retired calling paragraph
   * swapped for the current one (the step saves that first); `suggested` — never saved, so the
   * step saves the fleet template for this app first and files that.
   */
  source: z.enum(['saved', 'upgraded', 'suggested']).nullable(),
  /** Something the step's fill reports but does not stop on, such as a swap that would pass the field's cap. */
  warning: z.string().nullable(),
  /** The reviewer contact block: one person answers App Review for the whole fleet. */
  contact: z.object({ firstName: z.string(), lastName: z.string(), phone: z.string(), email: z.string() }),
  /**
   * The demo account fields. No password is sent (`passwordSet` false): the login screen's Demo
   * login button signs the reviewer in, so the account is declared not required.
   */
  demoAccount: z.object({ required: z.boolean(), username: z.string(), passwordSet: z.boolean() })
})
export type AppleReviewNotesPreview = z.infer<typeof AppleReviewNotesPreviewSchema>

/**
 * Where a version's "What's new" text comes from, in the order every store path tries them:
 * `line` — the release line's fleet-wide text with the school's name filled in; `listing` — the
 * school's saved store listing text; `default` — neither has any, so the plain fallback is sent.
 */
export const WhatsNewSourceSchema = z.enum(['line', 'listing', 'default'])
export type WhatsNewSource = z.infer<typeof WhatsNewSourceSchema>

/**
 * The "What's new" text the upgrade would send to both stores for the target version, and where it
 * comes from. App Store Connect and Play take the same text. `lineId`/`lineName` name the line whose
 * text was looked up (null when the plan has no target line), so the tab can link to its editor.
 */
export const AppUpgradeWhatsNewPreviewSchema = z.object({
  text: z.string(),
  source: WhatsNewSourceSchema,
  lineId: z.string().nullable(),
  lineName: z.string().nullable()
})
export type AppUpgradeWhatsNewPreview = z.infer<typeof AppUpgradeWhatsNewPreviewSchema>

/**
 * One thing the upgrade needs before it starts, checked so that an upgrade that starts runs to
 * review without stopping partway: the Apple and Play publishing accounts' API access, the
 * production build gate for each platform, and the store records the submission goes to.
 * `fail` refuses the start (409); `warn` is shown and starts anyway.
 */
export const AppUpgradePreflightCheckSchema = z.object({
  id: z.string(),
  label: z.string(),
  status: SetupPreflightStatusSchema,
  /** What was observed and, when it does not pass, what to do about it. */
  detail: z.string()
})
export type AppUpgradePreflightCheck = z.infer<typeof AppUpgradePreflightCheckSchema>

export const AppUpgradePreflightSchema = z.object({
  /** No check fails: the upgrade can start. */
  ok: z.boolean(),
  checks: z.array(AppUpgradePreflightCheckSchema)
})
export type AppUpgradePreflight = z.infer<typeof AppUpgradePreflightSchema>

export const AppUpgradePlanSchema = z.object({
  appId: z.string(),
  current: z.array(AppUpgradeStandingSchema),
  /** Null when no live release line has a git ref to build from. */
  target: AppUpgradeTargetSchema.nullable(),
  /** Every platform's store already holds the target version in review or live: nothing is left to do. */
  upToDate: z.boolean(),
  /**
   * How far each platform already got with the target version, and so what the default start keeps.
   * Empty while a drive is open (it answers for itself) or when the target names no version.
   */
  progress: z.array(AppUpgradeProgressSchema),
  steps: z.array(AppUpgradeStepSchema),
  /** The setup drive open on this app, if any. An upgrade cannot start while one is open. */
  runAll: SetupRunAllSchema.nullable(),
  /** The ref the upgrade would build from when nothing is overridden. */
  defaultRef: z.string().nullable(),
  /** What App Review information the upgrade would file, when it runs `apple.review-info`; else null. */
  reviewNotesPreview: AppleReviewNotesPreviewSchema.nullable(),
  /** The "What's new" text both stores would get for the target version. Null with no target. */
  whatsNewPreview: AppUpgradeWhatsNewPreviewSchema.nullable(),
  /**
   * What would stop the upgrade partway, checked for the default start (store upload on). Null while
   * a drive is open or a step is in flight: nothing can start then, and the plan polls every few
   * seconds, too often to read the vendors each time.
   */
  preflight: AppUpgradePreflightSchema.nullable()
})
export type AppUpgradePlan = z.infer<typeof AppUpgradePlanSchema>

export const GetAppUpgradePlanResponseSchema = z.object({
  data: AppUpgradePlanSchema,
  message: z.string()
})
export type GetAppUpgradePlanResponse = z.infer<typeof GetAppUpgradePlanResponseSchema>

export const StartAppUpgradeRequestSchema = z
  .object({
    /** A tagged version on the target line, or `branch`. Defaults to the line's latest tag. */
    version: LineReleaseVersionSchema.optional(),
    /**
     * Build from this git ref instead of the one the line chooses (for example `dev`, to ship what
     * is on it before a tag exists). Refused (422) when it cannot be read or carries a runtime
     * other than the target line's.
     */
    ref: z.string().trim().min(1).max(200).optional(),
    /** Upload the builds to TestFlight and Play and submit for review. On by default for an upgrade. */
    includeSubmission: z.boolean().default(true),
    /**
     * A patch (SCH-2593): the build keeps the fleet version, and App Store Connect files it under
     * the next four-part version (`1.9.1` live → `1.9.1.1`), for a change such as a new app name.
     * Needs store submission, and the fleet version's App Store record to be live already.
     */
    patch: z.boolean().default(false),
    /**
     * Reset the App Store summary rating when the new version is released (`apple.reset-ratings`,
     * run on the App Store version the start makes, before it goes to App Review). Off by default,
     * and needs store submission: without it no App Store version is made.
     */
    resetRatings: z.boolean().default(false)
  })
  .refine(body => !(body.version && body.ref), {
    message: 'Send a version or a git ref, not both',
    path: ['ref']
  })
  .refine(body => !body.patch || body.includeSubmission, {
    message: 'A patch only exists in the stores: turn store submission on',
    path: ['patch']
  })
  .refine(body => !body.resetRatings || body.includeSubmission, {
    message: 'Resetting the App Store rating needs an App Store version: turn store submission on',
    path: ['resetRatings']
  })
export type StartAppUpgradeRequest = z.infer<typeof StartAppUpgradeRequestSchema>

export const StartAppUpgradeResponseSchema = z.object({
  data: z.object({
    appId: z.string(),
    /** The ref the builds are made from. */
    ref: z.string(),
    /** The version `apps/mobile/version.json` names at that ref, when it could be read. */
    version: z.string().nullable(),
    runAll: SetupRunAllSchema,
    launched: z.array(SetupGraphNodeIdSchema)
  }),
  message: z.string()
})
export type StartAppUpgradeResponse = z.infer<typeof StartAppUpgradeResponseSchema>

// ==========================================
// Configuration history (SCH-3729)
// ==========================================
// Kept in this file rather than its own module: its snapshot schema reuses the store listing,
// branding, pre-login and onboarding response schemas defined above, and a separate module
// importing them back from here would be a circular import.

/**
 * An app's configuration history (SCH-3729).
 *
 * Every save, store submission, release and OTA writes a revision; a scheduled
 * sweep writes one when it finds a change nothing recorded. Each revision points
 * at a full snapshot of the configuration, stored once per distinct content.
 *
 * A snapshot holds the SAME payloads the admin dialog's tabs already render, so
 * a tab shows a past revision by reading its section instead of its live
 * endpoint. A section is `null` when that revision did not record it: imported
 * revisions (from a release line's shipped record, before history was kept)
 * hold only store text and images.
 *
 * Never in a snapshot: the OneSignal REST key, the Datadog client token, any key
 * material, and live vendor reads (EAS env, submit key, store identifiers),
 * which describe Apple, Google and Expo rather than our configuration.
 */

export const CONFIG_SNAPSHOT_SCHEMA_VERSION = 1 as const

/** The MobileApp row as the General, Expo, Apple, Google, OneSignal, Firebase and Availability tabs read it. */
export const ConfigSnapshotAppSchema = MobileAppResponseSchema.partial()
export type ConfigSnapshotApp = z.infer<typeof ConfigSnapshotAppSchema>

/** One store's screenshots for one release line, as `GET /:id/screenshots?store&version` returns them. */
export const ConfigSnapshotScreenshotsSchema = z.object({
  store: z.string(),
  /** The release line (`1.9`); null for the default the endpoint picks with no `version`. */
  version: z.string().nullable(),
  list: StoreScreenshotListResponseSchema
})
export type ConfigSnapshotScreenshots = z.infer<typeof ConfigSnapshotScreenshotsSchema>

export const MobileAppConfigSnapshotSchema = z.object({
  schemaVersion: z.literal(CONFIG_SNAPSHOT_SCHEMA_VERSION),
  capturedAt: z.string(),
  app: ConfigSnapshotAppSchema.nullable(),
  storeListings: GetMobileAppStoreListingsResponseSchema.nullable(),
  screenshots: z.array(ConfigSnapshotScreenshotsSchema).nullable(),
  assetManifest: MobileAppAssetManifestSchema.nullable(),
  brandingConfig: GetBrandingConfigResponseSchema.nullable(),
  preLoginSettings: GetPreLoginSettingsResponseSchema.nullable(),
  onboardingConfig: GetOnboardingConfigResponseSchema.nullable(),
  /**
   * Imported revisions only: the release line's shipped record as it was kept
   * (`AppLineShippedSnapshotSchema`), for the store text and images it holds.
   */
  shippedRecord: z.unknown().nullable()
})
export type MobileAppConfigSnapshot = z.infer<typeof MobileAppConfigSnapshotSchema>

/** The snapshot sections a tab can read; the web maps each tab to one or more. */
export const CONFIG_SNAPSHOT_SECTIONS = [
  'app',
  'storeListings',
  'screenshots',
  'assetManifest',
  'brandingConfig',
  'preLoginSettings',
  'onboardingConfig'
] as const
export type ConfigSnapshotSection = (typeof CONFIG_SNAPSHOT_SECTIONS)[number]

/**
 * What caused a revision.
 * - `edit`: a save (operations event of a configuration type).
 * - `submission`: sent to App Review or a Play review track.
 * - `release`: released on a line, or released to the store.
 * - `ota`: an over-the-air update published.
 * - `sweep`: the scheduled sweep found a change no event recorded.
 * - `baseline`: the first revision of an app, taken when history started.
 * - `imported`: a release line's shipped record from before history was kept.
 */
export const ConfigRevisionTriggerSchema = z.enum([
  'edit',
  'submission',
  'release',
  'ota',
  'sweep',
  'baseline',
  'imported'
])
export type ConfigRevisionTrigger = z.infer<typeof ConfigRevisionTriggerSchema>

/** One dropdown entry. */
export const ConfigRevisionSummarySchema = z.object({
  id: z.string(),
  capturedAt: z.string(),
  trigger: ConfigRevisionTriggerSchema,
  eventType: z.string().nullable(),
  actorLabel: z.string().nullable(),
  summary: z.string().nullable(),
  platform: PlatformSchema.nullable(),
  versionString: z.string().nullable(),
  buildNumber: z.string().nullable(),
  /** Same configuration as the revision before it (a submission that changed nothing, for example). */
  unchanged: z.boolean(),
  partial: z.boolean()
})
export type ConfigRevisionSummary = z.infer<typeof ConfigRevisionSummarySchema>

// GET /v1/mobile-apps/:id/config-revisions?limit=&before=
export const ListConfigRevisionsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(500).default(200),
  /** Revision id: return revisions captured before it (paging). */
  before: z.string().optional()
})
export type ListConfigRevisionsQuery = z.infer<typeof ListConfigRevisionsQuerySchema>

export const ListConfigRevisionsResponseSchema = z.object({
  revisions: z.array(ConfigRevisionSummarySchema),
  hasMore: z.boolean()
})
export type ListConfigRevisionsResponse = z.infer<typeof ListConfigRevisionsResponseSchema>

// GET /v1/mobile-apps/:id/config-revisions/:revisionId
export const GetConfigRevisionResponseSchema = z.object({
  revision: ConfigRevisionSummarySchema,
  snapshot: MobileAppConfigSnapshotSchema
})
export type GetConfigRevisionResponse = z.infer<typeof GetConfigRevisionResponseSchema>
